import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/useAuthStore';
import { 
    apiClient, getTrips, updateTrip, getDrivers, createDriver, 
    updateDriver, getVehicles, createVehicle, updateVehicle, 
    getVehicleTypes, getBases, openDriverSession, closeDriverSession, 
    getTariffs, getClients, getAreas, getCostCenters, getRequesters, 
    sendDriverDailySchedule, getQualityInsights, getOccupiedVehicleCodes 
} from '../api';
import { 
    Table, Button, Card, Badge, ButtonGroup, Nav, OverlayTrigger, 
    Tooltip, Form, Modal, Row, Col, Spinner, Container, Alert 
} from 'react-bootstrap';
import { 
    LogOut, Check, Edit3, Plus, ArrowRight, Navigation, MessageCircle, AlertTriangle, Send, Star, Filter, Award,
    Phone, Clock, User, Car, ChevronDown, ChevronUp, Save, Mail, RefreshCw, CheckCircle2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import SearchableMultiSelect from '../components/SearchableMultiSelect';
import TripAuditModal from '../components/TripAuditModal';
import BookingSentinelView from '../components/BookingSentinelView';
import BookingMeetingPointsManager from '../components/BookingMeetingPointsManager';
import ProviderSettlementsView from '../components/ProviderSettlementsView';
import ProviderRulesManager from '../components/ProviderRulesManager';
import ErrorBoundary from '../components/ErrorBoundary';
import { LanguageProvider, useLanguage, LanguageSelector } from '../context/LanguageContext';

const useGoogleMapsLoaded = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => {
        const checkGoogle = () => { if (window.google && window.google.maps) setIsLoaded(true); };
        if (window.google && window.google.maps) setIsLoaded(true);
        else {
            const interval = setInterval(checkGoogle, 200);
            const timeout = setTimeout(() => clearInterval(interval), 10000);
            return () => { clearInterval(interval); clearTimeout(timeout); };
        }
    }, []);
    return isLoaded;
};

// Banderas locales por zona horaria y moneda
const getFlagEmoji = (timezone, localCurrencyCode, lat, lng, originBase = null) => {
    let tz = timezone;
    if (!tz) {
        if (localCurrencyCode === 'PEN') tz = 'America/Lima';
        else if (localCurrencyCode === 'ARS') tz = 'America/Argentina/Mendoza';
        else if (localCurrencyCode === 'EUR') tz = 'Europe/Madrid';
        else if (localCurrencyCode === 'UYU') tz = 'America/Montevideo';
        else if (lat < 0 && lat > -18 && lng < -68 && lng > -82) tz = 'America/Lima';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) tz = 'America/Montevideo';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) tz = 'Europe/Madrid';
        else tz = 'America/Santiago';
    }
    const tzLower = (tz || '').toLowerCase();
    let countryCode = originBase?.country_code ? originBase.country_code.toLowerCase().trim() : '';

    if (!countryCode) {
        if (tzLower.includes('montevideo') || tzLower.includes('uruguay')) countryCode = 'uy';
        else if (tzLower.includes('madrid') || tzLower.includes('barcelona') || tzLower.includes('spain')) countryCode = 'es';
        else if (tzLower.includes('santiago')) countryCode = 'cl';
        else if (tzLower.includes('argentina') || tzLower.includes('mendoza') || tzLower.includes('buenos_aires')) countryCode = 'ar';
        else if (tzLower.includes('lima')) countryCode = 'pe';
        else if (tzLower.includes('santo_domingo') || tzLower.includes('punta_cana')) countryCode = 'do';
        else if (tzLower.includes('bogota') || tzLower.includes('colombia')) countryCode = 'co';
        else if (tzLower.includes('new_york')) countryCode = 'us';
        else if (tzLower.includes('london')) countryCode = 'gb';
        else if (tzLower.includes('sao_paulo') || tzLower.includes('rio')) countryCode = 'br';
        else if (localCurrencyCode === 'EUR') countryCode = 'es';
        else if (localCurrencyCode === 'UYU') countryCode = 'uy';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) countryCode = 'uy';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) countryCode = 'es';
        else countryCode = 'cl';
    }

    return (
        <img 
            src={`https://flagcdn.com/w20/${countryCode}.png`} 
            srcSet={`https://flagcdn.com/w40/${countryCode}.png 2x`}
            width="20" 
            alt={countryCode.toUpperCase()}
            className="align-middle me-1 shadow-sm"
            style={{ borderRadius: '2px', border: '1px solid rgba(0,0,0,0.15)' }}
        />
    );
};

const getStatusColor = (status, isDelayed = false) => {
    if (isDelayed) return '#dc3545'; // Rojo fuerte
    switch (status?.toLowerCase()) {
        case 'assigned': return '#fd7e14'; // Naranja
        case 'confirmed': return '#198754'; // Verde
        case 'en_route': return '#ffc107'; // Amarillo
        case 'arrived': return '#0dcaf0'; // Celeste
        case 'on_board': return '#0d6efd'; // Azul
        case 'completed': return '#28a745'; // Verde finalizado
        case 'pending': return '#6f42c1'; // Morado
        case 'cancelled_paid': return '#e83e8c'; // Rosa/magenta para Cancelado con Cargo
        case 'anulado': return '#6c757d'; // Gris para anulados
        default: return '#adb5bd';
    }
};

const STATUS_LABELS = {
    'pending': 'Pendiente', 'PENDING': 'Pendiente',
    'assigned': 'Asignado', 'ASSIGNED': 'Asignado',
    'confirmed': 'Confirmado', 'CONFIRMED': 'Confirmado',
    'en_route': 'En Camino', 'EN_ROUTE': 'En Camino',
    'arrived': 'En Posición', 'ARRIVED': 'En Posición',
    'on_board': 'En Ruta', 'ON_BOARD': 'En Ruta',
    'completed': 'Finalizado', 'COMPLETED': 'Finalizado',
    'cancelled': 'Cancelado', 'CANCELLED': 'Cancelado',
    'cancelled_paid': 'Cancelado con Cargo', 'CANCELLED_PAID': 'Cancelado con Cargo',
    'no_show': 'No Show', 'NO_SHOW': 'No Show',
    'anulado': 'ANULADO', 'ANULADO': 'ANULADO'
};

const getStatusText = (status, t) => {
    if (!status) return '';
    const key = `status_${status.toLowerCase()}`;
    const translated = typeof t === 'function' ? t(key) : '';
    if (translated && translated !== key) return translated;
    return STATUS_LABELS[status] || status;
};

const getTodayStr = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const formatDateSafe = (dateVal) => {
    if (!dateVal) return '-';
    try {
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('es-CL');
    } catch {
        return '-';
    }
};

const formatTimeSafe = (dateVal) => {
    if (!dateVal) return '';
    try {
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
        return '';
    }
};

const formatTripEarnings = (trip, fallbackCurrency = null) => {
    const currency = trip.provider_currency_code || trip.provider?.currency_code || fallbackCurrency || trip.origin_base?.currency_code || trip.local_currency_code || 'CLP';
    const rawAmount = (currency !== 'CLP' && trip.provider_cost_local !== null && trip.provider_cost_local !== undefined)
        ? trip.provider_cost_local 
        : (trip.driver_earnings || 0);
    const amount = Number(rawAmount).toLocaleString();
    if (currency === 'USD') return `US$ ${amount}`;
    if (currency === 'PEN') return `S/ ${amount}`;
    return `$${amount} ${currency}`;
};

const getThisMonthStr = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `${yyyy}-${mm}`;
};

const isAirport = (address) => {
    if (!address || typeof address !== 'string') return false;
    const keywords = ['aeropuerto', 'airport', 'scl', 'anf', 'cjc', 'iqq', 'lsc', 'ccp', 'mdz', 'eze', 'aep', 'puj', 'sdq', 'lim'];
    return keywords.some(k => address.toLowerCase().includes(k));
};

const VehicleTypeIcon = ({ type }) => {
    const name = type?.name?.toLowerCase() || '';
    let icon = '🚗';
    let label = 'S';
    let fullName = 'Standard';

    if (name.includes('sprinter')) { icon = '🚐'; label = 'SPR'; fullName = 'Sprinter'; }
    else if (name.includes('large')) { icon = '🚐'; label = 'LPC'; fullName = 'Large People Carrier'; }
    else if (name.includes('people')) { icon = '🚐'; label = 'PC'; fullName = 'People Carrier'; }
    else if (name.includes('bus')) { icon = '🚌'; label = 'BUS'; fullName = 'Minibus / Bus'; }
    else if (name.includes('luxury')) { icon = '💎'; label = 'LUX'; fullName = 'Luxury'; }
    else if (name.includes('executive')) { icon = '⭐'; label = 'EXE'; fullName = 'Executive'; }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{fullName}</Tooltip>}>
            <div className="d-flex flex-column align-items-center" style={{lineHeight: 1, cursor: 'help'}}>
                <span style={{fontSize: '1.2rem'}}>{icon}</span>
                <small className="fw-bold text-muted" style={{fontSize: '0.65rem'}}>{label}</small>
            </div>
        </OverlayTrigger>
    );
};

const ProviderPortal = () => {
    const { user, logout } = useAuthStore();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const translate = t;
    const audioContextRef = useRef(null);
    const mapsLoaded = useGoogleMapsLoaded();
    
    // UI State
    const [activeTab, setActiveTab] = useState('dispatch'); // 'dispatch', 'fleet', 'tariffs', 'claims'
    const [fleetTab, setFleetTab] = useState('drivers'); // 'drivers', 'vehicles'
    const [tariffsSubTab, setTariffsSubTab] = useState('routes'); // 'routes', 'rules'
    const [isLoading, setIsLoading] = useState(false);
    
    // State de Reclamos e Apelaciones
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimTrip, setClaimTrip] = useState(null);
    const [appealNotes, setAppealNotes] = useState('');
    const [appealFiles, setAppealFiles] = useState([]);
    const [submittingAppeal, setSubmittingAppeal] = useState(false);

    // State de Calidad & Encuestas Booking
    const [qualityData, setQualityData] = useState(null);
    const [qualityLoading, setQualityLoading] = useState(false);
    const [qualityStartDate, setQualityStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [qualityEndDate, setQualityEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [qualityDriverFilter, setQualityDriverFilter] = useState('');
    const [qualityCriticalOnly, setQualityCriticalOnly] = useState(false);
    const [qualitySearch, setQualitySearch] = useState('');

    // Data State
    const [trips, setTrips] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [occupiedCodes, setOccupiedCodes] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [tariffs, setTariffs] = useState([]);
    const [bases, setBases] = useState([]);
    const [providerDetails, setProviderDetails] = useState(null);
    
    // Catálogos adicionales para filtros
    const [clients, setClients] = useState([]);
    const [areas, setAreas] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [requesters, setRequesters] = useState([]);

    // Filtros de TripsLogView
    const [filterMode, setFilterMode] = useState('all_active'); // 'all_active', 'today', 'this_month', 'custom'
    const [startDateFilter, setStartDateFilter] = useState(getTodayStr());
    const [endDateFilter, setEndDateFilter] = useState(getTodayStr());
    const [searchTerm, setSearchTerm] = useState('');
    const [searchFile, setSearchFile] = useState('');
    const [selectedClient, setSelectedClient] = useState([]);
    const [filterStatus, setFilterStatus] = useState([]); 
    const [selectedDriverOrVehicle, setSelectedDriverOrVehicle] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [selectedCostCenters, setSelectedCostCenters] = useState([]);
    const [selectedRequesters, setSelectedRequesters] = useState([]);
    const [searchAquivoyId, setSearchAquivoyId] = useState('');
    const [searchOsId, setSearchOsId] = useState('');
    const [searchTripId, setSearchTripId] = useState('');
    const [dispatchSubTab, setDispatchSubTab] = useState('active'); // 'active' (vigentes) o 'finished' (historial)
    const [filterUnassignedOnly, setFilterUnassignedOnly] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Filtros de Tarifario
    const [tariffOriginSearch, setTariffOriginSearch] = useState('');
    const [tariffDestSearch, setTariffDestSearch] = useState('');
    const [tariffCapacity, setTariffCapacity] = useState('');
    const [tariffVehicleType, setTariffVehicleType] = useState('');
    const [tariffPage, setTariffPage] = useState(1);
    const tariffPageSize = 50;

    // Paginación Despacho
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Modals & Action States
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [assignForm, setAssignForm] = useState({ driver_id: '', vehicle_id: '', notes: '', driver_notes: '' });
    const [assignConflictWarning, setAssignConflictWarning] = useState(null);
    
    const [showDriverModal, setShowDriverModal] = useState(false);

    const [claimsSubTab, setClaimsSubTab] = useState('pending'); // 'pending', 'closed', 'all'

    // Filtrado de incidencias y reclamos de Booking
    const isClaimClosed = useCallback((t) => {
        if (!t) return false;
        return (
            t.booking_incident_status === 'PENALIZED' ||
            t.booking_incident_status === 'RESOLVED' ||
            (t.booking_incident_penalty_pct && t.booking_incident_penalty_pct > 0)
        );
    }, []);

    const isClaimPendingAction = useCallback((t) => {
        if (!t || isClaimClosed(t)) return false;
        return t.provider_appeal_status !== 'submitted' && t.provider_appeal_status !== 'reviewed';
    }, [isClaimClosed]);

    const incidentsTrips = useMemo(() => {
        return (trips || []).filter(t => t && (t.booking_incident_status || t.booking_incident_type || t.provider_appeal_status === 'submitted'));
    }, [trips]);

    const pendingIncidentsCount = useMemo(() => {
        return incidentsTrips.filter(t => isClaimPendingAction(t)).length;
    }, [incidentsTrips, isClaimPendingAction]);

    const submittedIncidentsCount = useMemo(() => {
        return incidentsTrips.filter(t => (t.provider_appeal_status === 'submitted' || t.provider_appeal_status === 'reviewed') && !isClaimClosed(t)).length;
    }, [incidentsTrips, isClaimClosed]);

    const closedIncidentsCount = useMemo(() => {
        return incidentsTrips.filter(t => isClaimClosed(t)).length;
    }, [incidentsTrips, isClaimClosed]);

    const filteredIncidentsTrips = useMemo(() => {
        if (claimsSubTab === 'pending') {
            return incidentsTrips.filter(t => isClaimPendingAction(t));
        } else if (claimsSubTab === 'submitted') {
            return incidentsTrips.filter(t => (t.provider_appeal_status === 'submitted' || t.provider_appeal_status === 'reviewed') && !isClaimClosed(t));
        } else if (claimsSubTab === 'closed') {
            return incidentsTrips.filter(t => isClaimClosed(t));
        }
        return incidentsTrips;
    }, [incidentsTrips, claimsSubTab, isClaimPendingAction, isClaimClosed]);

    const [incidentMessages, setIncidentMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [isSyncingClaims, setIsSyncingClaims] = useState(false);

    const fetchIncidentMessages = async (tripId) => {
        setLoadingMessages(true);
        try {
            const res = await apiClient.get(`/transport/trips/${tripId}/incident-messages`);
            setIncidentMessages(res.data || []);
        } catch (err) {
            console.error("Error al obtener mensajes del incidente:", err);
            setIncidentMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    const [isSyncingSingleTrip, setIsSyncingSingleTrip] = useState(false);

    const handleSyncSingleTripClaim = async (tripId) => {
        if (!tripId) return;
        setIsSyncingSingleTrip(true);
        try {
            const res = await apiClient.post(`/transport/trips/${tripId}/sync-email`);
            if (res.data?.synced_count > 0) {
                toast.success(`🎉 Se vincularon ${res.data.synced_count} correo(s) de Booking para esta reserva.`);
            } else if (res.data?.found_count > 0) {
                toast.info("Los correos de esta reserva ya estaban sincronizados.");
            } else {
                toast.info("No se encontraron correos para esta O.S. en Gmail.");
            }
            await fetchIncidentMessages(tripId);
        } catch (err) {
            console.error("Error al sincronizar correo del viaje:", err);
            toast.error("Error al buscar correos en Gmail.");
        } finally {
            setIsSyncingSingleTrip(false);
        }
    };

    const handleSyncClaims = async () => {
        setIsSyncingClaims(true);
        try {
            const res = await apiClient.post('/transport/incidents/sync-emails', null, { params: { since_days: 5, limit: 15 } });
            if (res.data?.processed_count > 0) {
                toast.success(`🎉 Se sincronizaron ${res.data.processed_count} reclamos nuevos desde Gmail.`);
            } else {
                toast.info("No hay nuevos reclamos en la bandeja de entrada.");
            }
            if (loadPortalData) await loadPortalData();
        } catch (err) {
            console.error("Error al sincronizar reclamos:", err);
            toast.error("Error al conectar con Gmail para sincronizar reclamos.");
        } finally {
            setIsSyncingClaims(false);
        }
    };

    const handleOpenClaimModal = async (tripObj) => {
        setClaimTrip(tripObj);
        setAppealNotes(tripObj.provider_appeal_notes || '');
        setAppealFiles([]);
        setIncidentMessages([]);
        setShowClaimModal(true);
        setLoadingMessages(true);
        try {
            const res = await apiClient.get(`/transport/trips/${tripObj.id}/incident-messages`);
            const msgs = res.data || [];
            if (msgs.length === 0 && tripObj.external_reference) {
                // Si no hay mensajes guardados aún, intentar buscar automáticamente en Gmail
                try {
                    await apiClient.post(`/transport/trips/${tripObj.id}/sync-email`);
                    const refreshed = await apiClient.get(`/transport/trips/${tripObj.id}/incident-messages`);
                    setIncidentMessages(refreshed.data || []);
                } catch {
                    setIncidentMessages([]);
                }
            } else {
                setIncidentMessages(msgs);
            }
        } catch (err) {
            console.error("Error al cargar historial de incidentes:", err);
            setIncidentMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleSubmitAppeal = async () => {
        if (!claimTrip) return;
        if (!appealNotes.trim()) {
            toast.error("Por favor ingrese la explicación o descargos del reclamo.");
            return;
        }
        setSubmittingAppeal(true);
        try {
            const formData = new FormData();
            formData.append('notes', appealNotes);
            for (let i = 0; i < appealFiles.length; i++) {
                formData.append('files', appealFiles[i]);
            }
            const res = await apiClient.post(`/transport/trips/${claimTrip.id}/provider-appeal`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("🎉 ¡Respuesta registrada y enviada a Booking.com exitosamente!");
            setShowClaimModal(false);
            setTrips(prev => prev.map(t => t.id === claimTrip.id ? { 
                ...t, 
                provider_appeal_notes: appealNotes, 
                provider_appeal_status: 'submitted',
                booking_incident_dispute_sent: true,
                provider_appeal_evidence: res.data.evidence_urls || t.provider_appeal_evidence
            } : t));
        } catch (err) {
            console.error("Error al enviar apelación:", err);
            const errMsg = err.response?.data?.detail || "Error al enviar la apelación. Intente nuevamente.";
            toast.error(errMsg);
        } finally {
            setSubmittingAppeal(false);
        }
    };


    const handleRemoveLocalFile = (indexToRemove) => {
        setAppealFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleDeleteEvidence = async (s3Key) => {
        if (!claimTrip) return;
        if (!window.confirm("¿Está seguro de eliminar esta imagen de evidencia?")) return;
        try {
            const res = await apiClient.delete(`/transport/trips/${claimTrip.id}/provider-appeal/evidence`, {
                params: { file_key: s3Key }
            });
            toast.success("Evidencia eliminada correctamente.");
            const updatedEvidence = res.data.provider_appeal_evidence || [];
            setClaimTrip(prev => ({ ...prev, provider_appeal_evidence: updatedEvidence }));
            setTrips(prev => prev.map(t => t.id === claimTrip.id ? { ...t, provider_appeal_evidence: updatedEvidence } : t));
        } catch (err) {
            console.error("Error al eliminar evidencia:", err);
            toast.error("No se pudo eliminar la evidencia.");
        }
    };
    const [editingDriver, setEditingDriver] = useState(null);
    const [driverForm, setDriverForm] = useState({ full_name: '', rut: '', phone: '', email: '', is_active: true, password: '', show_pricing: true });

    const [showVehicleModal, setShowVehicleModal] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [vehicleForm, setVehicleForm] = useState({ 
        internal_code: '', plate: '', make: '', model: '', year: 2026, color: '', vehicle_type_id: '', current_driver_id: '', is_active: true 
    });

    const [showVehicleTypeModal, setShowVehicleTypeModal] = useState(false);
    const [editingVehicleType, setEditingVehicleType] = useState(null);
    const [vehicleTypeForm, setVehicleTypeForm] = useState({ name: '', capacity: 4, max_suitcases: 4, booking_category: 'STANDARD' });
    const [isLuxury, setIsLuxury] = useState(false);
    const [isExecutive, setIsExecutive] = useState(false);

    const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
    const [bulkUpdateForm, setBulkUpdateForm] = useState({ source_vehicle_type_id: '', target_vehicle_type_id: '', percentage_change: 0, fixed_change: 0, change_type: 'percentage' });

    const [savingTariffId, setSavingTariffId] = useState(null);
    const [tariffCosts, setTariffCosts] = useState({}); // tariffId: costLocal
    const [tariffDiscounts, setTariffDiscounts] = useState({}); // tariffId: discountPercent
    const [tariffGeniusDiscounts, setTariffGeniusDiscounts] = useState({}); // tariffId: geniusPercent
    const [isSavingAllTariffs, setIsSavingAllTariffs] = useState(false);

    // Genius Masivo Modal
    const [showGeniusBulkModal, setShowGeniusBulkModal] = useState(false);
    const [bulkGeniusPercent, setBulkGeniusPercent] = useState(5);
    const [bulkGeniusScope, setBulkGeniusScope] = useState('all');
    const [isSavingBulkGenius, setIsSavingBulkGenius] = useState(false);

    // WhatsApp Itinerarios
    const [showItineraryModal, setShowItineraryModal] = useState(false);
    const [itineraryDate, setItineraryDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    });
    const [itineraryTrips, setItineraryTrips] = useState([]);
    const [itineraryLoading, setItineraryLoading] = useState(false);

    // Auditoría GPS / Driver Events
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditTrip, setAuditTrip] = useState(null);
    const handleOpenAudit = (trip) => {
        setAuditTrip(trip);
        setShowAuditModal(true);
    };
    const [isSendingItinerary, setIsSendingItinerary] = useState({});

    // Mapeos useMemo para filtros
    const driverOrVehicleOptions = useMemo(() => {
        const list = [];
        vehicles.forEach(v => {
            list.push({
                id: `vehicle_${v.internal_code}`,
                name: `Móvil ${v.internal_code}`
            });
        });
        drivers.forEach(d => {
            list.push({
                id: `driver_${d.id}`,
                name: d.full_name
            });
        });
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [drivers, vehicles]);

    const areaOptions = useMemo(() => {
        const filteredAreas = selectedClient.length > 0 
            ? areas.filter(a => selectedClient.includes(a.client_id)) 
            : areas;
        return filteredAreas.map(a => ({
            id: a.id,
            name: a.name
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [areas, selectedClient]);

    const costCenterOptions = useMemo(() => {
        const filteredCCs = selectedClient.length > 0 
            ? costCenters.filter(cc => selectedClient.includes(cc.client_id)) 
            : costCenters;
        return filteredCCs.map(cc => ({
            id: cc.id,
            name: cc.code + (cc.name ? ` - ${cc.name}` : '')
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [costCenters, selectedClient]);

    const requesterOptions = useMemo(() => {
        const filteredRequesters = selectedClient.length > 0 
            ? requesters.filter(r => selectedClient.includes(r.client_id)) 
            : requesters;
        return filteredRequesters.map(r => ({
            id: r.id,
            name: r.full_name + (r.email ? ` (${r.email})` : '')
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [requesters, selectedClient]);

    const filteredTariffs = useMemo(() => {
        const filtered = tariffs.filter(t => {
            const originName = (t.origin_zone?.name || t.origin_group?.name || '').toLowerCase();
            const destName = (t.destination_zone?.name || t.destination_group?.name || '').toLowerCase();
            
            const originLower = tariffOriginSearch.toLowerCase().trim();
            const matchesOrigin = !originLower || originName.includes(originLower);
            
            const destLower = tariffDestSearch.toLowerCase().trim();
            const matchesDest = !destLower || destName.includes(destLower);
            
            const vehicleTypeName = (t.vehicle_type?.name || '').toLowerCase();
            const matchesVehicleType = !tariffVehicleType || vehicleTypeName === tariffVehicleType.toLowerCase();
            
            const cap = t.max_pax !== null && t.max_pax !== undefined ? t.max_pax : t.vehicle_type?.capacity;
            const matchesCapacity = !tariffCapacity || cap === parseInt(tariffCapacity);
            
            return matchesOrigin && matchesDest && matchesVehicleType && matchesCapacity;
        });

        return [...filtered].sort((a, b) => {
            const aOrig = a.origin_zone?.name || a.origin_group?.name || '';
            const aDest = a.destination_zone?.name || a.destination_group?.name || '';
            const bOrig = b.origin_zone?.name || b.origin_group?.name || '';
            const bDest = b.destination_zone?.name || b.destination_group?.name || '';

            const aKey = [aOrig, aDest].sort().join(' - ');
            const bKey = [bOrig, bDest].sort().join(' - ');

            if (aKey !== bKey) {
                return aKey.localeCompare(bKey);
            }

            // Dentro del mismo par de rutas, ordenar la Ida (inicia en aeropuerto o similar) antes de la Vuelta
            const airportKeywords = ['aeropuerto', 'airport', 'puj', 'scl', 'sdq', 'apu', 'apt'];
            const isAAirportOrigin = airportKeywords.some(kw => aOrig.toLowerCase().includes(kw));
            const isBAirportOrigin = airportKeywords.some(kw => bOrig.toLowerCase().includes(kw));
            
            if (isAAirportOrigin && !isBAirportOrigin) return -1;
            if (!isAAirportOrigin && isBAirportOrigin) return 1;

            // Si ninguno es aeropuerto o ambos lo son, ordenar por origen alfabéticamente para mantener orden estable
            if (aOrig !== bOrig) {
                return aOrig.localeCompare(bOrig);
            }

            // Ordenar por costo local
            const aPrice = a.cost_local || 0;
            const bPrice = b.cost_local || 0;
            if (aPrice !== bPrice) {
                return aPrice - bPrice;
            }

            // Ordenar por tipo de vehículo
            const aType = a.vehicle_type?.name || '';
            const bType = b.vehicle_type?.name || '';
            return aType.localeCompare(bType);
        });
    }, [tariffs, tariffOriginSearch, tariffDestSearch, tariffVehicleType, tariffCapacity]);

    const tariffVehicleTypeOptions = useMemo(() => {
        const types = new Set();
        tariffs.forEach(t => {
            if (t.vehicle_type?.name) {
                types.add(t.vehicle_type.name);
            }
        });
        return Array.from(types).sort();
    }, [tariffs]);

    const tariffCapacityOptions = useMemo(() => {
        const caps = new Set();
        tariffs.forEach(t => {
            const cap = t.max_pax !== null && t.max_pax !== undefined ? t.max_pax : t.vehicle_type?.capacity;
            if (cap) {
                caps.add(cap);
            }
        });
        return Array.from(caps).sort((a, b) => a - b);
    }, [tariffs]);

    // Paginación de tarifas para alto rendimiento
    useEffect(() => {
        setTariffPage(1);
    }, [tariffOriginSearch, tariffDestSearch, tariffCapacity, tariffVehicleType]);

    const paginatedTariffs = useMemo(() => {
        const start = (tariffPage - 1) * tariffPageSize;
        return (filteredTariffs || []).slice(start, start + tariffPageSize);
    }, [filteredTariffs, tariffPage, tariffPageSize]);

    // Helpers ultra-defensivos para visualización de tarifas sin crash
    const formatTariffDate = (val) => {
        if (!val) return '—';
        try {
            const d = new Date(val);
            return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
        } catch {
            return '—';
        }
    };

    const formatTariffShortDate = (val) => {
        if (!val) return '';
        try {
            const d = new Date(val);
            return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });
        } catch {
            return '';
        }
    };

    const formatTariffNumber = (val) => {
        if (val === null || val === undefined || val === '') return '—';
        const num = Number(val);
        return isNaN(num) ? String(val) : num.toLocaleString();
    };

    const vehicleCodeValidation = useMemo(() => {
        const code = vehicleForm.internal_code ? vehicleForm.internal_code.trim() : '';
        if (!code) return { isValid: true, message: null, suggestions: [] };
        
        // 1. Validar unicidad (excluyendo el vehículo actual si se está editando)
        // Se valida tanto contra los vehículos cargados del proveedor como contra todos los códigos ocupados del sistema
        const allUsedCodes = Array.from(new Set([
            ...vehicles.map(v => v.internal_code),
            ...occupiedCodes
        ].filter(Boolean).map(c => String(c).trim())));

        const isOccupied = allUsedCodes.some(c => 
            c === code && (!editingVehicle || String(editingVehicle.internal_code).trim() !== code)
        );
        
        // Calcular sugerencias excluyendo todos los móviles ocupados
        const getSuggestions = () => {
            const used = new Set(allUsedCodes.map(c => parseInt(c)).filter(c => !isNaN(c) && (!editingVehicle || String(editingVehicle.internal_code).trim() !== String(c))));
            const list = [];
            const start = providerDetails && providerDetails.mobile_range_start !== null && providerDetails.mobile_range_start !== undefined 
                ? providerDetails.mobile_range_start 
                : 1;
            const end = providerDetails && providerDetails.mobile_range_end !== null && providerDetails.mobile_range_end !== undefined 
                ? providerDetails.mobile_range_end 
                : 9999;
            for (let i = start; i <= end; i++) {
                if (!used.has(i)) {
                    list.push(i);
                    if (list.length >= 5) break;
                }
            }
            return list;
        };

        if (isOccupied) {
            return {
                isValid: false,
                type: 'occupied',
                message: '❌ Número ocupado por otro vehículo.',
                suggestions: getSuggestions()
            };
        }

        // 2. Validar rango (si el proveedor tiene rango configurado)
        if (providerDetails && 
            providerDetails.mobile_range_start !== null && providerDetails.mobile_range_start !== undefined &&
            providerDetails.mobile_range_end !== null && providerDetails.mobile_range_end !== undefined) {
            
            const codeNum = parseInt(code);
            if (isNaN(codeNum)) {
                return {
                    isValid: false,
                    type: 'non_numeric',
                    message: '⚠️ El código del móvil debe ser numérico.',
                    suggestions: getSuggestions()
                };
            }
            
            if (codeNum < providerDetails.mobile_range_start || codeNum > providerDetails.mobile_range_end) {
                return {
                    isValid: false,
                    type: 'out_of_range',
                    message: `⚠️ Fuera del rango permitido (${providerDetails.mobile_range_start} - ${providerDetails.mobile_range_end}).`,
                    suggestions: getSuggestions()
                };
            }
        }

        return { isValid: true, message: null, suggestions: [] };
    }, [vehicleForm.internal_code, vehicles, occupiedCodes, editingVehicle, providerDetails]);

    const statusOptions = useMemo(() => [
        { id: 'pending', name: t('status_pending') },
        { id: 'assigned', name: t('status_assigned') },
        { id: 'confirmed', name: t('status_confirmed') },
        { id: 'en_route', name: t('status_en_route') },
        { id: 'arrived', name: t('status_arrived') },
        { id: 'on_board', name: t('status_on_board') },
        { id: 'completed', name: t('status_completed') },
        { id: 'cancelled', name: t('status_cancelled') },
        { id: 'cancelled_paid', name: t('status_cancelled_paid') },
        { id: 'no_show', name: t('status_no_show') },
        { id: 'anulado', name: t('status_anulado') }
    ], [t]);

    // Limpieza de selecciones dependientes de clientes
    useEffect(() => {
        if (selectedClient.length > 0) {
            setSelectedCostCenters(prev => {
                const activeCCs = costCenters.filter(cc => selectedClient.includes(cc.client_id));
                const activeCCIds = activeCCs.map(cc => cc.id);
                return prev.filter(ccId => activeCCIds.includes(ccId));
            });
            setSelectedAreas(prev => {
                const activeAreas = areas.filter(a => selectedClient.includes(a.client_id));
                const activeAreaIds = activeAreas.map(a => a.id);
                return prev.filter(areaId => activeAreaIds.includes(areaId));
            });
            setSelectedRequesters(prev => {
                const activeReqs = requesters.filter(r => selectedClient.includes(r.client_id));
                const activeReqIds = activeReqs.map(r => r.id);
                return prev.filter(reqId => activeReqIds.includes(reqId));
            });
        }
    }, [selectedClient, costCenters, areas, requesters]);

    const isTripCritical = (trip) => {
        try {
            const now = new Date();
            const scheduledTime = new Date(trip.scheduled_time);
            if (isNaN(scheduledTime.getTime())) return false;

            const minutesLeft = (scheduledTime - now) / 60000;
            const threshold = trip.client?.lead_time_alert || 60;
            const statusLower = trip.status?.toLowerCase();

            const isWithin48Hours = minutesLeft <= 2880; // 48 horas (2 días)

            const isDelayed = ['pending', 'assigned', 'confirmed'].includes(statusLower) && scheduledTime < now;
            const isProactiveAlert = ['pending', 'assigned', 'confirmed'].includes(statusLower) && minutesLeft <= threshold;

            return isWithin48Hours && (isDelayed || isProactiveAlert);
        } catch (e) {
            return false;
        }
    };

    const isTripIncomplete = (trip) => {
        try {
            const now = new Date();
            const scheduledTime = new Date(trip.scheduled_time);
            if (isNaN(scheduledTime.getTime())) return false;

            const minutesLeft = (scheduledTime - now) / 60000;
            const statusLower = trip.status?.toLowerCase();

            const isWithin48Hours = minutesLeft <= 2880; // 48 horas (2 días)

            return isWithin48Hours && (!trip.driver_id || ['pending', 'assigned'].includes(statusLower));
        } catch (e) {
            return false;
        }
    };

    const playAlertSound = useCallback(() => {
        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = audioContextRef.current;
            
            if (ctx.state === 'suspended') {
                ctx.resume().catch(err => console.log("Audio bloqueado"));
            }

            const now = ctx.currentTime;
            
            const playTone = (freq, start, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                
                osc.type = 'sawtooth'; 
                osc.frequency.setValueAtTime(freq, start);
                
                gain.gain.setValueAtTime(0.3, start);
                gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
                
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(start);
                osc.stop(start + duration);
            };

            for (let i = 0; i < 6; i++) {
                const time = now + (i * 0.15);
                const freq = 880; 
                playTone(freq, time, 0.1);
            }
        } catch (e) {
            console.error("Audio error:", e);
        }
    }, []);

    const updateTripsSorted = useCallback((data) => {
        const sorted = (data || []).sort((a, b) => {
            const aCrit = isTripCritical(a);
            const bCrit = isTripCritical(b);
            
            if (aCrit && !bCrit) return -1;
            if (!aCrit && bCrit) return 1;

            const aInc = isTripIncomplete(a);
            const bInc = isTripIncomplete(b);

            if (aInc && !bInc) return -1;
            if (!aInc && bInc) return 1;
            
            return new Date(a.scheduled_time) - new Date(b.scheduled_time);
        });
        setTrips(sorted);
    }, []);

    const getProviderDefaultStartDate = useCallback(() => {
        const d = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
        return d.toISOString();
    }, []);

    // Cargar datos
    const loadPortalData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [vTypes, baseList, clientList, areaList, ccList, reqList] = await Promise.all([
                getVehicleTypes(),
                getBases(),
                getClients().catch(() => []),
                getAreas().catch(() => []),
                getCostCenters().catch(() => []),
                getRequesters().catch(() => [])
            ]);
            setVehicleTypes(vTypes || []);
            setBases(baseList || []);
            setClients(clientList || []);
            setAreas(areaList || []);
            setCostCenters(ccList || []);
            setRequesters(reqList || []);
            
            // Cargar viajes (últimos 60 días + todos los futuros para evitar truncado por límite de 5000 en proveedores con alto volumen histórico)
            const tripsData = await getTrips({ limit: 5000, start_date: getProviderDefaultStartDate() });
            updateTripsSorted(tripsData);
            
            // Cargar conductores y vehículos
            const driversData = await getDrivers({ limit: 500 });
            setDrivers(driversData || []);
            
            const [vehiclesData, occupiedData] = await Promise.all([
                getVehicles({ limit: 500 }),
                getOccupiedVehicleCodes()
            ]);
            setVehicles(vehiclesData || []);
            setOccupiedCodes(occupiedData || []);

                  if (user && user.linked_provider_id) {
                try {
                    const res = await apiClient.get(`/transport/providers/${user.linked_provider_id}`);
                    setProviderDetails(res.data);
                } catch (pe) {
                    console.error("Error al obtener detalles del proveedor:", pe);
                }
            }
            const tariffsData = await getTariffs();
            setTariffs(tariffsData || []);
            
            const costMap = {};
            const discountMap = {};
            const geniusMap = {};
            (tariffsData || []).forEach(t => {
                costMap[t.id] = t.cost_local || 0;
                discountMap[t.id] = t.booking_bid_discount || 0;
                geniusMap[t.id] = t.booking_genius_discount || 0;
            });
            setTariffCosts(costMap);
            setTariffDiscounts(discountMap);
            setTariffGeniusDiscounts(geniusMap);

        } catch (error) {
            console.error("Error al cargar datos del portal del proveedor", error);
            toast.error("Error al cargar los datos del portal.");
        } finally {
            setIsLoading(false);
        }
    }, [updateTripsSorted, user, getProviderDefaultStartDate]);

    useEffect(() => {
        if (!user || user.role !== 'provider') {
            toast.error("Acceso restringido a Proveedores.");
            navigate('/login');
            return;
        }
        loadPortalData();
    }, [user, navigate, loadPortalData]);

    // Cargar Datos de Calidad
    const fetchQuality = useCallback(async () => {
        setQualityLoading(true);
        try {
            const params = {};
            if (qualityStartDate) params.start_date = qualityStartDate;
            if (qualityEndDate) params.end_date = qualityEndDate;
            if (qualityDriverFilter) params.driver_id = qualityDriverFilter;
            const res = await getQualityInsights(params);
            setQualityData(res);
        } catch (err) {
            console.error("Error al cargar insights de calidad del proveedor:", err);
        } finally {
            setQualityLoading(false);
        }
    }, [qualityStartDate, qualityEndDate, qualityDriverFilter]);

    useEffect(() => {
        if (activeTab === 'quality') {
            fetchQuality();
        }
    }, [activeTab, fetchQuality]);

    useEffect(() => {
        const hasCritical = trips.some(t => isTripCritical(t));
        if (hasCritical) {
            playAlertSound();
            const interval = setInterval(() => {
                playAlertSound();
            }, 30000);
            return () => clearInterval(interval);
        }
    }, [trips, playAlertSound]);

    const handleLogout = () => {
        logout().then(() => {
            navigate('/login');
        });
    };

    // --- ACCIONES VIAJES ---
    const openAssignDialog = (trip) => {
        setSelectedTrip(trip);
        setAssignConflictWarning(null);
        setAssignForm({
            driver_id: trip.driver_id || '',
            vehicle_id: trip.vehicle_id || '',
            notes: trip.notes || '',
            driver_notes: trip.driver_notes || ''
        });
        setShowAssignModal(true);
    };

    useEffect(() => {
        if (!showAssignModal || !selectedTrip?.scheduled_time || (!assignForm.driver_id && !assignForm.vehicle_id)) {
            setAssignConflictWarning(null);
            return;
        }
        let cancelled = false;
        apiClient.post('transport/trips/check-driver-conflict', {
            driver_id: assignForm.driver_id ? parseInt(assignForm.driver_id, 10) : null,
            vehicle_id: assignForm.vehicle_id ? parseInt(assignForm.vehicle_id, 10) : null,
            scheduled_time: selectedTrip.scheduled_time,
            estimated_duration_minutes: selectedTrip.estimated_duration_minutes || 40,
            trip_id: selectedTrip.id,
            origin_base_id: selectedTrip.origin_base_id || null,
            timezone: selectedTrip.origin_base?.timezone || selectedTrip.timezone || null,
            flight_number: selectedTrip.flight_number || null,
            pickup_address: selectedTrip.pickup_address || null,
            pickup_lat: selectedTrip.pickup_lat ? parseFloat(selectedTrip.pickup_lat) : null,
            pickup_lng: selectedTrip.pickup_lng ? parseFloat(selectedTrip.pickup_lng) : null,
            dropoff_lat: selectedTrip.dropoff_lat ? parseFloat(selectedTrip.dropoff_lat) : null,
            dropoff_lng: selectedTrip.dropoff_lng ? parseFloat(selectedTrip.dropoff_lng) : null,
        }).then(res => {
            if (!cancelled) {
                if (res.data?.has_conflict && Array.isArray(res.data.conflicts) && res.data.conflicts.length > 0) {
                    setAssignConflictWarning(res.data.conflicts);
                } else {
                    setAssignConflictWarning(null);
                }
            }
        }).catch(err => {
            console.warn("⚠️ Error verificando cercanía de reservas en Portal Proveedor:", err);
        });
        return () => { cancelled = true; };
    }, [showAssignModal, assignForm.driver_id, assignForm.vehicle_id, selectedTrip]);

    const handleVehicleChange = (vehicleId) => {
        if (!vehicleId) {
            setAssignForm(prev => ({ ...prev, vehicle_id: '', driver_id: '' }));
            return;
        }
        const vId = parseInt(vehicleId);
        const selectedVehicle = vehicles.find(v => v.id === vId);
        const driverId = selectedVehicle?.current_driver_id || '';
        setAssignForm(prev => ({
            ...prev,
            vehicle_id: vehicleId,
            driver_id: driverId ? String(driverId) : ''
        }));
    };

    const handleAssignSubmit = async (e) => {
        e.preventDefault();
        if (!assignForm.vehicle_id) {
            toast.warning("Debe seleccionar un móvil.");
            return;
        }
        if (!assignForm.driver_id) {
            toast.warning("El móvil no tiene conductor asociado. Vincule uno en 'Mi Flota'.");
            return;
        }
        if (assignConflictWarning && assignConflictWarning.length > 0) {
            const lines = assignConflictWarning
                .map(c => `• Reserva #${c.trip_id} (${c.scheduled_time} - ${c.passenger_name || 'Pasajero'}): ${c.conflict_reason}`)
                .join('\n');
            const proceed = window.confirm(
                `⚠️ ATENCIÓN: El conductor/móvil seleccionado ya tiene reservas muy cercanas en horario:\n\n${lines}\n\n¿Desea asignarlo de todas formas?`
            );
            if (!proceed) return;
        }
        const currentStatus = (selectedTrip?.status || '').toLowerCase();
        const newDriverId = parseInt(assignForm.driver_id);
        const driverChanged = selectedTrip?.driver_id !== newDriverId;
        const payload = {
            driver_id: newDriverId,
            vehicle_id: parseInt(assignForm.vehicle_id),
            notes: assignForm.notes || null,
            driver_notes: assignForm.driver_notes || null,
            audit_override: true,
        };
        if (['pending', 'assigned'].includes(currentStatus) || driverChanged) {
            payload.status = 'assigned';
        }
        try {
            setIsLoading(true);
            await updateTrip(selectedTrip.id, payload);
            toast.success("✅ Viaje actualizado exitosamente.");
            setShowAssignModal(false);
            const tripsData = await getTrips({ limit: 5000, start_date: getProviderDefaultStartDate() });
            updateTripsSorted(tripsData);
        } catch (error) {
            console.error(error);
            const detail = error.response?.data?.detail || "Error al asignar el viaje.";
            if (typeof detail === 'string' && detail.includes("Requiere confirmación para asignación forzada")) {
                if (window.confirm(`⚠️ ${detail}\n\n¿Deseas forzar la asignación de este conductor de todas formas?`)) {
                    try {
                        await updateTrip(selectedTrip.id, { ...payload, force_driver_assignment: true });
                        toast.success("✅ Viaje actualizado exitosamente (asignación forzada).");
                        setShowAssignModal(false);
                        const tripsData = await getTrips({ limit: 5000, start_date: getProviderDefaultStartDate() });
                        updateTripsSorted(tripsData);
                        return;
                    } catch (forceErr) {
                        toast.error(forceErr.response?.data?.detail || "Error al forzar la asignación.");
                        return;
                    }
                }
                return;
            }
            toast.error(detail);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReleaseTrip = async (tripId) => {
        if (!window.confirm("¿Seguro que deseas liberar este viaje?")) return;
        try {
            setIsLoading(true);
            const payload = {
                driver_id: null,
                vehicle_id: null,
                status: 'pending'
            };
            await updateTrip(tripId, payload);
            toast.success("✅ Viaje liberado.");
            const tripsData = await getTrips({ limit: 5000, start_date: getProviderDefaultStartDate() });
            updateTripsSorted(tripsData);
        } catch (error) {
            console.error(error);
            toast.error("Error al liberar.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- CRUD CONDUCTORES ---
    const handleOpenDriverCreate = () => {
        setEditingDriver(null);
        setDriverForm({ full_name: '', rut: '', phone: '', email: '', is_active: true, password: '', show_pricing: true });
        setShowDriverModal(true);
    };

    const handleOpenDriverEdit = (driver) => {
        setEditingDriver(driver);
        setDriverForm({
            full_name: driver.full_name,
            rut: driver.rut,
            phone: driver.phone || '',
            email: driver.email || '',
            is_active: driver.is_active,
            password: '',
            show_pricing: driver.show_pricing !== false
        });
        setShowDriverModal(true);
    };

    const handleDriverSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const payload = {
                ...driverForm,
                is_driver: true,
                is_owner: false,
                is_approved: true,
                provider_id: user.linked_provider_id
            };
            if (!payload.password) {
                delete payload.password;
            }
            
            if (editingDriver) {
                await updateDriver(editingDriver.id, payload);
                toast.success("✅ Conductor actualizado.");
            } else {
                await createDriver(payload);
                toast.success("✅ Conductor registrado.");
            }
            setShowDriverModal(false);
            const driversData = await getDrivers({ limit: 500 });
            setDrivers(driversData || []);
        } catch (error) {
            console.error(error);
            toast.error("Error al registrar conductor. Verifique RUT.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- CRUD VEHÍCULOS ---
    const handleOpenVehicleCreate = () => {
        setEditingVehicle(null);
        setVehicleForm({ 
            internal_code: '', plate: '', make: '', model: '', year: 2026, color: '', vehicle_type_id: '', current_driver_id: '', is_active: true 
        });
        setShowVehicleModal(true);
    };

    const handleOpenVehicleEdit = (vehicle) => {
        setEditingVehicle(vehicle);
        setVehicleForm({
            internal_code: vehicle.internal_code,
            plate: vehicle.plate,
            make: vehicle.make || '',
            model: vehicle.model || '',
            year: vehicle.year || 2026,
            color: vehicle.color || '',
            vehicle_type_id: vehicle.vehicle_type_id,
            current_driver_id: vehicle.current_driver_id || '',
            is_active: vehicle.is_active
        });
        setShowVehicleModal(true);
    };

    const handleVehicleSubmit = async (e) => {
        e.preventDefault();
        
        if (!vehicleCodeValidation.isValid) {
            toast.error(vehicleCodeValidation.message);
            return;
        }
        
        try {
            setIsLoading(true);
            const payload = {
                ...vehicleForm,
                year: parseInt(vehicleForm.year),
                vehicle_type_id: parseInt(vehicleForm.vehicle_type_id),
                current_driver_id: vehicleForm.current_driver_id ? parseInt(vehicleForm.current_driver_id) : null,
                provider_id: user.linked_provider_id
            };
            
            if (editingVehicle) {
                await updateVehicle(editingVehicle.id, payload);
                toast.success("✅ Vehículo actualizado.");
            } else {
                await createVehicle(payload);
                toast.success("✅ Vehículo registrado.");
            }
            setShowVehicleModal(false);
            const [vehiclesData, occupiedData] = await Promise.all([
                getVehicles({ limit: 500 }),
                getOccupiedVehicleCodes()
            ]);
            setVehicles(vehiclesData || []);
            setOccupiedCodes(occupiedData || []);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.detail || "Error al registrar vehículo.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenVehicleTypeCreate = () => {
        setEditingVehicleType(null);
        setVehicleTypeForm({ name: '', capacity: 4, max_suitcases: 4, booking_category: 'PEOPLE_CARRIER' });
        setIsLuxury(false);
        setIsExecutive(false);
        setShowVehicleTypeModal(true);
    };

    const handleOpenVehicleTypeEdit = (vt) => {
        setEditingVehicleType(vt);
        const cat = vt.booking_category || 'STANDARD';
        setVehicleTypeForm({
            name: vt.name,
            capacity: vt.capacity,
            max_suitcases: vt.max_suitcases || 4,
            booking_category: cat
        });
        setIsLuxury(cat === 'LUXURY');
        setIsExecutive(cat === 'EXECUTIVE' || cat === 'EXECUTIVE_PEOPLE_CARRIER');
        setShowVehicleTypeModal(true);
    };

    const computeBookingCategory = (capacity, luxury, executive) => {
        const pax = parseInt(capacity) || 0;
        if (luxury && pax <= 3) {
            return 'LUXURY';
        }
        if (executive) {
            if (pax <= 3) {
                return 'EXECUTIVE';
            } else if (pax >= 4 && pax <= 6) {
                return 'EXECUTIVE_PEOPLE_CARRIER';
            }
        }
        
        if (pax >= 1 && pax <= 2) {
            return 'STANDARD';
        } else if (pax >= 3 && pax <= 5) {
            return 'PEOPLE_CARRIER';
        } else if (pax >= 6 && pax <= 9) {
            return 'LARGE_PEOPLE_CARRIER';
        } else if (pax >= 10) {
            return 'MINIBUS';
        }
        return 'STANDARD';
    };

    useEffect(() => {
        if (!showVehicleTypeModal) return;
        const computed = computeBookingCategory(vehicleTypeForm.capacity, isLuxury, isExecutive);
        setVehicleTypeForm(prev => {
            if (prev.booking_category !== computed) {
                return { ...prev, booking_category: computed };
            }
            return prev;
        });
    }, [vehicleTypeForm.capacity, isLuxury, isExecutive, showVehicleTypeModal]);

    const handleLuxuryChange = (checked) => {
        if (checked) {
            setIsLuxury(true);
            setIsExecutive(false);
            if (parseInt(vehicleTypeForm.capacity) > 3) {
                setVehicleTypeForm(prev => ({ ...prev, capacity: 3 }));
                toast.info("ℹ️ Categoría LUXURY limitada a 3 pasajeros. Capacidad ajustada.");
            }
        } else {
            setIsLuxury(false);
        }
    };

    const handleExecutiveChange = (checked) => {
        if (checked) {
            setIsExecutive(true);
            setIsLuxury(false);
            if (parseInt(vehicleTypeForm.capacity) > 6) {
                setVehicleTypeForm(prev => ({ ...prev, capacity: 6 }));
                toast.info("ℹ️ Categoría EXECUTIVE limitada a 6 pasajeros. Capacidad ajustada.");
            }
        } else {
            setIsExecutive(false);
        }
    };

    const handleCapacityChange = (val) => {
        const num = parseInt(val) || 0;
        setVehicleTypeForm(prev => ({ ...prev, capacity: val }));
        if (num > 3 && isLuxury) {
            setIsLuxury(false);
            toast.warning("⚠️ LUXURY se desactivó porque la capacidad es mayor a 3 pasajeros.");
        }
        if (num > 6 && isExecutive) {
            setIsExecutive(false);
            toast.warning("⚠️ EXECUTIVE se desactivó porque la capacidad es mayor a 6 pasajeros.");
        }
    };

    const handleVehicleTypeSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const payload = {
                name: vehicleTypeForm.name,
                capacity: parseInt(vehicleTypeForm.capacity),
                max_suitcases: parseInt(vehicleTypeForm.max_suitcases),
                booking_category: vehicleTypeForm.booking_category,
                provider_id: user.linked_provider_id
            };
            
            if (editingVehicleType) {
                await apiClient.put(`/transport/vehicle-types/${editingVehicleType.id}`, payload);
                toast.success("✅ Tipo de vehículo actualizado.");
            } else {
                await apiClient.post('/transport/vehicle-types/', payload);
                toast.success("✅ Tipo de vehículo registrado. Rutas en $0 generadas de forma automática.");
            }
            setShowVehicleTypeModal(false);
            
            const vTypes = await getVehicleTypes();
            setVehicleTypes(vTypes || []);
            const tariffsData = await getTariffs({ limit: 5000 });
            setTariffs(tariffsData || []);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.detail || "Error al guardar tipo de vehículo.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkUpdateSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const isPercentage = bulkUpdateForm.change_type === 'percentage';
            const payload = {
                source_vehicle_type_id: parseInt(bulkUpdateForm.source_vehicle_type_id),
                target_vehicle_type_id: parseInt(bulkUpdateForm.target_vehicle_type_id),
                percentage_change: isPercentage ? parseFloat(bulkUpdateForm.percentage_change || 0) : 0.0,
                fixed_change: !isPercentage ? parseFloat(bulkUpdateForm.fixed_change || 0) : 0.0
            };
            
            const res = await apiClient.post('/transport/providers/tariffs/bulk-update', payload);
            toast.success(`✅ ${res.data.message || "Actualización masiva completada."}`);
            setShowBulkUpdateModal(false);
            
            const tariffsData = await getTariffs({ limit: 5000 });
            setTariffs(tariffsData || []);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.detail || "Error al realizar actualización masiva.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleDriverShift = async (vehicle) => {
        if (vehicle.current_driver_id) {
            if (window.confirm(`¿Deseas finalizar el turno de este móvil?`)) {
                try {
                    setIsLoading(true);
                    await closeDriverSession(vehicle.current_driver_id);
                    toast.success("✅ Turno finalizado.");
                    const vehiclesData = await getVehicles({ limit: 500 });
                    setVehicles(vehiclesData || []);
                } catch (error) {
                    console.error(error);
                    toast.error("Error al finalizar el turno.");
                } finally {
                    setIsLoading(false);
                }
            }
        } else {
            const driverIdStr = window.prompt("Ingrese el ID del conductor para iniciar turno:\n" + 
                drivers.map(d => `${d.id}: ${d.full_name}`).join('\n')
            );
            if (!driverIdStr) return;
            const driverId = parseInt(driverIdStr);
            const driverExists = drivers.some(d => d.id === driverId);
            if (!driverExists) {
                toast.error("Conductor inválido.");
                return;
            }
            try {
                setIsLoading(true);
                await updateVehicle(vehicle.id, { current_driver_id: driverId });
                await openDriverSession(driverId);
                toast.success("✅ Turno iniciado correctamente.");
                const vehiclesData = await getVehicles({ limit: 500 });
                setVehicles(vehiclesData || []);
            } catch (error) {
                console.error(error);
                toast.error("Error al iniciar turno.");
            } finally {
                setIsLoading(false);
            }
        }
    };

    // --- MI TARIFARIO ---
    const handleTariffCostChange = (tariffId, val) => {
        setTariffCosts(prev => ({ ...prev, [tariffId]: val }));
    };

    const handleTariffDiscountChange = (tariffId, val) => {
        setTariffDiscounts(prev => ({ ...prev, [tariffId]: val }));
    };

    const handleTariffGeniusChange = (tariffId, val) => {
        setTariffGeniusDiscounts(prev => ({ ...prev, [tariffId]: val }));
    };

    const isTariffModified = useCallback((t) => {
        const costVal = tariffCosts[t.id];
        const discVal = tariffDiscounts[t.id];
        const geniusVal = tariffGeniusDiscounts[t.id];
        const costChanged = costVal !== undefined && parseFloat(costVal) !== parseFloat(t.cost_local || 0);
        const discChanged = discVal !== undefined && parseFloat(discVal) !== parseFloat(t.booking_bid_discount || 0);
        const geniusChanged = geniusVal !== undefined && parseFloat(geniusVal) !== parseFloat(t.booking_genius_discount || 0);
        return costChanged || discChanged || geniusChanged;
    }, [tariffCosts, tariffDiscounts, tariffGeniusDiscounts]);

    const modifiedTariffsCount = useMemo(() => {
        return tariffs.filter(t => isTariffModified(t)).length;
    }, [tariffs, isTariffModified]);

    const handleSaveTariffCost = async (tariffId) => {
        const cost = parseFloat(tariffCosts[tariffId]);
        if (isNaN(cost) || cost < 0) {
            toast.error("Ingrese un valor de costo válido.");
            return;
        }
        
        let discount = parseFloat(tariffDiscounts[tariffId]);
        if (isNaN(discount)) discount = 0;
        if (discount < 0 || discount > 50) {
            toast.error("El descuento máximo para la puja es 50%.");
            return;
        }

        let geniusDisc = parseFloat(tariffGeniusDiscounts[tariffId] !== undefined ? tariffGeniusDiscounts[tariffId] : (tariffs.find(t => t.id === tariffId)?.booking_genius_discount || 0));
        if (isNaN(geniusDisc)) geniusDisc = 0;
        if (geniusDisc !== 0 && (geniusDisc < 5 || geniusDisc > 10)) {
            toast.error("El descuento Genius debe ser 0% (desactivado) o estar entre 5% y 10% según Booking.com.");
            return;
        }

        try {
            setSavingTariffId(tariffId);
            const res = await apiClient.put(`/transport/tariffs/${tariffId}/provider-cost`, { 
                cost_local: cost,
                booking_bid_discount: discount,
                booking_genius_discount: geniusDisc,
                booking_genius_shared: true
            });
            toast.success("✅ Tarifa actualizada.");
            setTariffs(prev => prev.map(t => t.id === tariffId ? res.data : t));
        } catch (error) {
            console.error(error);
            toast.error("Error al actualizar la tarifa.");
        } finally {
            setSavingTariffId(null);
        }
    };

    const handleSaveAllTariffs = async () => {
        const modified = tariffs.filter(t => isTariffModified(t));
        if (modified.length === 0) {
            toast.info("No hay cambios pendientes de guardar.");
            return;
        }

        // Validación previa
        for (const t of modified) {
            const cost = parseFloat(tariffCosts[t.id] !== undefined ? tariffCosts[t.id] : (t.cost_local || 0));
            if (isNaN(cost) || cost < 0) {
                toast.error(`Valor de costo inválido en ruta ${t.origin_zone?.name || 'Origen'} ➔ ${t.destination_zone?.name || 'Destino'}.`);
                return;
            }
            let discount = parseFloat(tariffDiscounts[t.id] !== undefined ? tariffDiscounts[t.id] : (t.booking_bid_discount || 0));
            if (isNaN(discount)) discount = 0;
            if (discount < 0 || discount > 50) {
                toast.error(`El descuento máximo es 50% en ruta ${t.origin_zone?.name || 'Origen'}.`);
                return;
            }
            let geniusDisc = parseFloat(tariffGeniusDiscounts[t.id] !== undefined ? tariffGeniusDiscounts[t.id] : (t.booking_genius_discount || 0));
            if (isNaN(geniusDisc)) geniusDisc = 0;
            if (geniusDisc !== 0 && (geniusDisc < 5 || geniusDisc > 10)) {
                toast.error(`El descuento Genius debe ser 0% o entre 5% y 10% en ruta ${t.origin_zone?.name || 'Origen'}.`);
                return;
            }
        }

        try {
            setIsSavingAllTariffs(true);
            const updates = modified.map(async (t) => {
                const cost = parseFloat(tariffCosts[t.id] !== undefined ? tariffCosts[t.id] : (t.cost_local || 0));
                let discount = parseFloat(tariffDiscounts[t.id] !== undefined ? tariffDiscounts[t.id] : (t.booking_bid_discount || 0));
                if (isNaN(discount)) discount = 0;
                let geniusDisc = parseFloat(tariffGeniusDiscounts[t.id] !== undefined ? tariffGeniusDiscounts[t.id] : (t.booking_genius_discount || 0));
                if (isNaN(geniusDisc)) geniusDisc = 0;
                const res = await apiClient.put(`/transport/tariffs/${t.id}/provider-cost`, {
                    cost_local: cost,
                    booking_bid_discount: discount,
                    booking_genius_discount: geniusDisc,
                    booking_genius_shared: true
                });
                return res.data;
            });

            const savedTariffs = await Promise.all(updates);
            const savedMap = new Map(savedTariffs.map(t => [t.id, t]));
            setTariffs(prev => prev.map(t => savedMap.has(t.id) ? savedMap.get(t.id) : t));
            toast.success(`🎉 Se guardaron exitosamente ${savedTariffs.length} tarifas.`);
        } catch (error) {
            console.error(error);
            toast.error("Ocurrió un error al guardar algunas tarifas.");
        } finally {
            setIsSavingAllTariffs(false);
        }
    };

    const handleApplyBulkGenius = async () => {
        const disc = parseFloat(bulkGeniusPercent);
        if (isNaN(disc) || (disc !== 0 && (disc < 5 || disc > 10))) {
            toast.error("El descuento Genius debe ser 0% (desactivado) o entre 5% y 10%.");
            return;
        }

        const targetTariffs = bulkGeniusScope === 'filtered' ? filteredTariffs : tariffs;
        if (targetTariffs.length === 0) {
            toast.info("No hay rutas seleccionadas para aplicar el descuento.");
            return;
        }

        try {
            setIsSavingBulkGenius(true);
            const tariffIds = targetTariffs.map(t => t.id);
            const providerId = providerDetails?.id || user?.linked_provider_id;

            const res = await apiClient.put('/transport/tariffs/bulk-genius-discount', {
                tariff_ids: tariffIds,
                genius_discount: disc,
                genius_shared: true,
                provider_id: providerId ? parseInt(providerId) : undefined
            });

            const countUpdated = (res.data && res.data.count !== undefined) ? res.data.count : tariffIds.length;
            toast.success(`⭐ Descuento Genius fijado en ${disc}% para ${countUpdated} rutas.`);
            setShowGeniusBulkModal(false);

            setTariffGeniusDiscounts(prev => {
                const next = { ...prev };
                tariffIds.forEach(id => { next[id] = disc; });
                return next;
            });
            setTariffs(prev => prev.map(t => tariffIds.includes(t.id) ? { ...t, booking_genius_discount: disc } : t));
        } catch (error) {
            console.error("Error aplicando Genius masivo:", error);
            const detail = error.response?.data?.detail || "Error al aplicar Genius en lote.";
            toast.error(detail);
        } finally {
            setIsSavingBulkGenius(false);
        }
    };

    // --- ACCIÓN DE ESTADO DESPACHO ---
    const handleStatusChange = async (trip, nextStatus) => {
        try {
            setIsLoading(true);
            await updateTrip(trip.id, { status: nextStatus });
            toast.success(`Estado actualizado a ${STATUS_LABELS[nextStatus]}`);
            const tripsData = await getTrips({ limit: 5000, start_date: getProviderDefaultStartDate() });
            updateTripsSorted(tripsData);
        } catch (e) {
            toast.error("Error al actualizar estado.");
        } finally {
            setIsLoading(false);
        }
    };

    // --- AUX FECHAS ---
    const getTripDateString = (scheduledTime) => {
        if (!scheduledTime) return '';
        try {
            const dateObj = new Date(scheduledTime);
            if (isNaN(dateObj.getTime())) return '';
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch (e) {
            return '';
        }
    };

    // --- FILTRADO LOCAL COMPLETO ---
    const filteredTrips = useMemo(() => {
        // En el historial, no mostrar nada a menos que se aplique algún filtro
        if (dispatchSubTab === 'finished') {
            const hasAnyFilter = !!(
                searchTerm ||
                searchTripId ||
                searchAquivoyId ||
                searchOsId ||
                searchFile ||
                selectedClient.length > 0 ||
                selectedDriverOrVehicle.length > 0 ||
                filterStatus.length > 0 ||
                selectedAreas.length > 0 ||
                selectedCostCenters.length > 0 ||
                selectedRequesters.length > 0 ||
                filterMode !== 'all_active'
            );
            if (!hasAnyFilter) return [];
        }

        return trips.filter(t => {
            // 0. ID de Reserva
            if (searchTripId) {
                if (!t.id || !String(t.id).includes(searchTripId)) return false;
            }

            // 1. Buscador general
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matches = 
                    (t.passenger_name && t.passenger_name.toLowerCase().includes(term)) ||
                    (t.origin_address && t.origin_address.toLowerCase().includes(term)) ||
                    (t.destination_address && t.destination_address.toLowerCase().includes(term)) ||
                    (t.flight_number && t.flight_number.toLowerCase().includes(term)) ||
                    (t.external_reference && t.external_reference.toLowerCase().includes(term)) ||
                    (t.id && String(t.id).includes(term));
                if (!matches) return false;
            }

            // 2. ID AQ
            if (searchAquivoyId) {
                if (!t.aquivoy_id || !String(t.aquivoy_id).includes(searchAquivoyId)) return false;
            }

            // 3. OS
            if (searchOsId) {
                if (!t.external_reference || !t.external_reference.toLowerCase().includes(searchOsId.toLowerCase())) return false;
            }

            // 4. File
            if (searchFile) {
                if (!t.file || !t.file.toLowerCase().includes(searchFile.toLowerCase())) return false;
            }

            // 5. Clientes
            if (selectedClient.length > 0) {
                if (!selectedClient.includes(t.client_id)) return false;
            }

            // 6. Estados
            if (filterStatus.length > 0) {
                if (!filterStatus.includes(t.status?.toLowerCase())) return false;
            }

            // 7. Áreas
            if (selectedAreas.length > 0) {
                if (!selectedAreas.includes(t.area_id)) return false;
            }

            // 8. Centros de Costo
            if (selectedCostCenters.length > 0) {
                if (!selectedCostCenters.includes(t.cost_center_id)) return false;
            }

            // 9. Solicitantes
            if (selectedRequesters.length > 0) {
                if (!selectedRequesters.includes(t.requester_id)) return false;
            }

            // 10. Conductor o Móvil
            if (selectedDriverOrVehicle.length > 0) {
                const matchesDriverOrVehicle = selectedDriverOrVehicle.some(opt => {
                    if (opt.startsWith('driver_')) {
                        const driverId = parseInt(opt.replace('driver_', ''));
                        return t.driver_id === driverId;
                    }
                    if (opt.startsWith('vehicle_')) {
                        const vehicleCode = opt.replace('vehicle_', '');
                        const vCode = t.vehicle?.internal_code || vehicles.find(v => v.current_driver_id === t.driver_id && v.is_active)?.internal_code || t.driver?.vehicle_plate;
                        return vCode === vehicleCode;
                    }
                    return false;
                });
                if (!matchesDriverOrVehicle) return false;
            }

            // 11. Filtro de Fechas
            const tripDateStr = getTripDateString(t.scheduled_time);
            if (filterMode === 'today') {
                if (tripDateStr !== getTodayStr()) return false;
            } else if (filterMode === 'this_month') {
                if (!tripDateStr || !tripDateStr.startsWith(getThisMonthStr())) return false;
            } else if (filterMode === 'custom') {
                if (tripDateStr) {
                    if (startDateFilter && tripDateStr < startDateFilter) return false;
                    if (endDateFilter && tripDateStr > endDateFilter) return false;
                } else {
                    return false;
                }
            } else if (filterMode === 'all_active') {
                // Por defecto (Todos), en locución activa mostramos todos los servicios vigentes desde hoy en adelante
                const hasSearch = !!(searchTerm || searchAquivoyId || searchOsId || searchFile || searchTripId || selectedClient.length > 0 || selectedDriverOrVehicle.length > 0 || filterStatus.length > 0 || selectedAreas.length > 0 || selectedCostCenters.length > 0 || selectedRequesters.length > 0 || filterUnassignedOnly);
                if (dispatchSubTab === 'active' && !hasSearch) {
                    const todayStr = getTodayStr();
                    if (!tripDateStr || tripDateStr < todayStr) return false;
                }
            }

            // 12. Sub-tab Vigentes vs Historial
            const statusLower = t.status?.toLowerCase() || '';
            const isFinished = ['completed', 'cancelled', 'cancelled_paid', 'no_show', 'anulado'].includes(statusLower);
            const matchesSubTab = dispatchSubTab === 'active' ? !isFinished : isFinished;
            if (!matchesSubTab) return false;

            // 13. Mostrar Sin Asignar (solo en activos)
            if (dispatchSubTab === 'active' && filterUnassignedOnly) {
                const isAssigned = t.vehicle_id !== null && t.vehicle_id !== undefined;
                if (isAssigned) return false;
            }

            return true;
        });
    }, [trips, searchTerm, searchAquivoyId, searchOsId, searchFile, searchTripId, selectedClient, filterStatus, selectedAreas, selectedCostCenters, selectedRequesters, selectedDriverOrVehicle, filterMode, startDateFilter, endDateFilter, dispatchSubTab, vehicles, filterUnassignedOnly]);

    // Dividir activos en libres vs asignados (solo en modo 'active')
    const availableTrips = useMemo(() => {
        return filteredTrips.filter(t => t.vehicle_id === null || t.vehicle_id === undefined);
    }, [filteredTrips]);



    // Viajes paginados
    const paginatedTrips = useMemo(() => {
        return filteredTrips.slice((page - 1) * pageSize, page * pageSize);
    }, [filteredTrips, page, pageSize]);

    // Estadísticas
    const summaryStats = useMemo(() => {
        const count = filteredTrips.length;
        const totalsByCurrency = {};
        filteredTrips.forEach(t => {
            const currency = t.provider_currency_code || providerDetails?.currency_code || t.origin_base?.currency_code || t.local_currency_code || 'CLP';
            const rawAmount = (currency !== 'CLP' && t.provider_cost_local !== null && t.provider_cost_local !== undefined)
                ? t.provider_cost_local 
                : (t.driver_earnings || 0);
            totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + Number(rawAmount);
        });

        const keys = Object.keys(totalsByCurrency);
        const formattedTotal = keys.length === 0 ? '$0 CLP' : keys.map(cur => {
            const amount = (cur === 'USD' || cur === 'PEN') 
                ? Number(totalsByCurrency[cur].toFixed(2)).toLocaleString() 
                : Math.round(totalsByCurrency[cur]).toLocaleString();
            if (cur === 'USD') return `US$ ${amount}`;
            if (cur === 'PEN') return `S/ ${amount}`;
            return `$${amount} ${cur}`;
        }).join(' / ');

        return { count, formattedTotal };
    }, [filteredTrips, providerDetails]);

    // --- EXPORTAR EXCEL ---
    const handleExportExcel = () => {
        if (!filteredTrips || filteredTrips.length === 0) {
            toast.warning("No hay datos para exportar.");
            return;
        }

        const dataRows = filteredTrips.map(t => {
            let tripTimezone = t.origin_base?.timezone || t.timezone;
            if (!tripTimezone) {
                if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                else tripTimezone = 'America/Santiago';
            }

            const localTimeStr = new Intl.DateTimeFormat('es-CL', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
                timeZone: tripTimezone,
                hour12: false
            }).format(new Date(t.scheduled_time));

            const chileTimeStr = new Intl.DateTimeFormat('es-CL', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
                timeZone: 'America/Santiago',
                hour12: false
            }).format(new Date(t.scheduled_time));

            const vehicleCode = t.vehicle?.internal_code || vehicles.find(v => v.current_driver_id === t.driver_id && v.is_active)?.internal_code || t.driver?.vehicle_plate || '';

            return {
                'ID Reserva': t.id,
                'Fecha/Hora Local': localTimeStr,
                'Fecha/Hora Chile': chileTimeStr,
                'Zona Horaria': tripTimezone,
                'Cliente': t.client?.name || '',
                'Pasajero Principal': t.passenger_name || '',
                'Origen': t.pickup_address || '',
                'Destino': t.dropoff_address || '',
                'Móvil': vehicleCode,
                'Conductor': t.driver?.full_name || '',
                'Patente Conductor/Móvil': t.vehicle?.plate || t.driver?.vehicle_plate || '',
                'Teléfono Conductor': t.driver?.phone || '',
                'Tipo Vehículo': t.requested_vehicle_type?.name || '',
                'Nº Vuelo': t.flight_number || '',
                'Estado': STATUS_LABELS[t.status] || t.status || '',
                'Moneda': t.provider_currency_code || providerDetails?.currency_code || t.origin_base?.currency_code || t.local_currency_code || 'CLP',
                'Pago Estimado (Proveedor)': Number(t.driver_earnings || 0),
                'Observaciones Operador': t.notes || '',
                'Observaciones Conductor': t.driver_notes || ''
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(dataRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Viajes");

        const maxLen = {};
        dataRows.forEach(row => {
            Object.keys(row).forEach(key => {
                const val = String(row[key] || '');
                maxLen[key] = Math.max(maxLen[key] || 10, val.length);
            });
        });
        worksheet['!cols'] = Object.keys(maxLen).map(key => ({ wch: Math.min(maxLen[key] + 2, 50) }));

        const dateStr = format(new Date(), 'yyyyMMdd_HHmmss');
        XLSX.writeFile(workbook, `viajes_proveedor_${dateStr}.xlsx`);
        toast.success("Excel exportado correctamente.");
    };

    // --- ITINERARIOS REMOTOS ---
    const loadItineraryTrips = useCallback(async (date) => {
        setItineraryLoading(true);
        try {
            const startStr = date + 'T00:00:00';
            const endStr = date + 'T23:59:59';
            const data = await getTrips({
                start_date: startStr,
                end_date: endStr,
                limit: 1000
            });
            setItineraryTrips(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Error al cargar itinerarios:", e);
            toast.error("Error al cargar viajes para los itinerarios.");
        } finally {
            setItineraryLoading(false);
        }
    }, []);

    const formatItineraryMessage = (group, date) => {
        const formattedDate = date.split('-').reverse().join('/');
        const driverName = group.driver.full_name;
        const vehicleCode = group.vehicle.internal_code || group.driver.vehicle_plate || '---';

        let msg = `*EMIGO - ITINERARIO DE VIAJES*\n`;
        msg += `📅 *Fecha:* ${formattedDate}\n`;
        msg += `👤 *Conductor:* ${driverName} (Móvil: ${vehicleCode})\n\n`;

        group.trips.forEach((t, index) => {
            const timeStr = format(new Date(t.scheduled_time), 'HH:mm');
            msg += `---\n`;
            msg += `*SERVICIO ${index + 1} - ${timeStr} hrs*\n`;
            msg += `🏢 *Cliente:* ${t.client?.name || '---'}\n`;
            msg += `📍 *Origen:* ${t.pickup_address}\n`;
            msg += `🏁 *Destino:* ${t.dropoff_address}\n`;

            const totalPax = 1 + (t.additional_passengers?.length || 0);
            msg += `👥 *Pasajeros (${totalPax}):*\n`;
            msg += `- ${t.passenger_name} (${t.passenger_phone || 'Sin tel.'})\n`;
            if (t.additional_passengers && t.additional_passengers.length > 0) {
                t.additional_passengers.forEach(ap => {
                    msg += `- ${ap.full_name} (${ap.phone || 'Sin tel.'})\n`;
                });
            }

            if (t.flight_number) msg += `✈️ *Vuelo:* ${t.flight_number}\n`;
            if (t.notes) msg += `📝 *Notas:* ${t.notes}\n`;
            msg += `\n`;
        });

        return msg;
    };

    const handleSendItinerary = async (group) => {
        const phoneClean = group.driver.phone ? group.driver.phone.replace(/[^0-9]/g, '') : '';
        if (!phoneClean) {
            toast.warning(`El conductor ${group.driver.full_name} no tiene un teléfono válido.`);
            return;
        }

        const nowIso = new Date().toISOString();
        const tripIds = group.trips.map(t => t.id);

        try {
            await Promise.all(tripIds.map(id => 
                updateTrip(id, { itinerary_sent_at: nowIso })
            ));
            
            setItineraryTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) return { ...t, itinerary_sent_at: nowIso };
                return t;
            }));

            setTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) return { ...t, itinerary_sent_at: nowIso };
                return t;
            }));

            toast.success(`Itinerario marcado como enviado para ${group.driver.full_name}`);
        } catch (e) {
            console.error(e);
            toast.error("Error al registrar envío.");
        }

        const text = formatItineraryMessage(group, itineraryDate);
        const url = `https://wa.me/${phoneClean}?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    const handleSendItineraryOfficial = async (group) => {
        if (!group.driver.id) return;
        const driverId = group.driver.id;

        setIsSendingItinerary(prev => ({ ...prev, [driverId]: true }));
        try {
            await sendDriverDailySchedule(driverId, itineraryDate);
            toast.success(`Itinerario enviado oficialmente por WhatsApp a ${group.driver.full_name}`);
            
            const nowIso = new Date().toISOString();
            const tripIds = group.trips.map(t => t.id);
            setItineraryTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) return { ...t, itinerary_sent_at: nowIso };
                return t;
            }));
            setTrips(prev => prev.map(t => {
                if (tripIds.includes(t.id)) return { ...t, itinerary_sent_at: nowIso };
                return t;
            }));
        } catch (e) {
            console.error(e);
            toast.error("Error al enviar itinerario oficial por WhatsApp.");
        } finally {
            setIsSendingItinerary(prev => ({ ...prev, [driverId]: false }));
        }
    };

    // Itinerarios agrupados para recordatorios
    const groupedItineraries = useMemo(() => {
        const validTrips = itineraryTrips.filter(t => 
            t.driver_id && 
            !['cancelled', 'cancelled_paid', 'no_show', 'anulado'].includes(t.status?.toLowerCase())
        );

        const groups = {};
        validTrips.forEach(t => {
            const dId = t.driver_id;
            if (!groups[dId]) {
                groups[dId] = {
                    driver: t.driver || drivers.find(d => d.id === dId) || { id: dId, full_name: `Chofer #${dId}`, phone: '' },
                    vehicle: t.vehicle || vehicles.find(v => v.current_driver_id === dId) || { internal_code: t.driver?.vehicle_plate || '---' },
                    trips: []
                };
            }
            groups[dId].trips.push(t);
        });

        Object.keys(groups).forEach(dId => {
            groups[dId].trips.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        });

        return Object.values(groups).sort((a, b) => a.driver.full_name.localeCompare(b.driver.full_name));
    }, [itineraryTrips, drivers, vehicles]);

    const getItineraryStatusBadge = (group) => {
        const sentTrips = group.trips.filter(t => t.itinerary_sent_at);
        if (sentTrips.length === 0) {
            return <Badge bg="secondary">⏳ No Enviado</Badge>;
        }
        if (sentTrips.length === group.trips.length) {
            const latestSent = sentTrips.reduce((max, t) => {
                const d = new Date(t.itinerary_sent_at);
                return d > max ? d : max;
            }, new Date(0));
            return (
                <Badge bg="success">
                    ✅ Enviado ({format(latestSent, 'HH:mm')})
                </Badge>
            );
        }
        return <Badge bg="warning" text="dark">⚠️ Parcial</Badge>;
    };

    // --- RENDERIZADO DE TABLA VISTA LOCUCION ---
    const renderTripsTable = (tripsList, showActions = true) => {
        if (tripsList.length === 0) {
            return (
                <div className="bg-light border rounded p-5 text-center text-muted small shadow-sm bg-white">
                    {translate('no_services_found')}
                </div>
            );
        }

        return (
            <div>
                {/* --- VISTA MÓVIL: TARJETAS (d-md-none) --- */}
                <div className="d-md-none d-flex flex-column gap-3 mb-3">
                    {tripsList.map(t => {
                        const statusLower = t.status?.toLowerCase();
                        const isCrit = isTripCritical(t);
                        const vehicleCode = t.vehicle?.internal_code || vehicles.find(v => v.current_driver_id === t.driver_id && v.is_active)?.internal_code;
                        const isAssigned = t.vehicle_id !== null && t.vehicle_id !== undefined;
                        const assignedDriver = drivers.find(d => d.id === t.driver_id);

                        let tripTimezone = t.origin_base?.timezone || t.timezone;
                        if (!tripTimezone) {
                            if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                            else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                            else if (t.local_currency_code === 'EUR') tripTimezone = 'Europe/Madrid';
                            else if (t.local_currency_code === 'UYU') tripTimezone = 'America/Montevideo';
                            else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                            else if (t.pickup_lat >= -35.5 && t.pickup_lat <= -30.0 && t.pickup_lng >= -58.5 && t.pickup_lng <= -53.0) tripTimezone = 'America/Montevideo';
                            else if (t.pickup_lat >= 36.0 && t.pickup_lat <= 44.0 && t.pickup_lng >= -9.5 && t.pickup_lng <= 4.5) tripTimezone = 'Europe/Madrid';
                            else tripTimezone = 'America/Santiago';
                        }
                        const isInternational = tripTimezone !== 'America/Santiago';
                        const flag = getFlagEmoji(tripTimezone, t.local_currency_code, t.pickup_lat, t.pickup_lng, t.origin_base);

                        const passengerPhoneClean = t.passenger_phone ? t.passenger_phone.replace(/[^0-9]/g, '') : '';
                        const cardBorderColor = isCrit 
                            ? '#dc3545' 
                            : !isAssigned 
                                ? '#ffc107' 
                                : getStatusColor(t.status, false);

                        return (
                            <Card 
                                key={`mob-trip-${t.id}`} 
                                className="border-0 bg-white" 
                                style={{
                                    borderLeft: `5px solid ${cardBorderColor}`,
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)'
                                }}
                            >
                                <Card.Body className="p-3">
                                    {/* Cabecera Tarjeta: ID + Tags + Tarifa */}
                                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                        <div className="d-flex align-items-center gap-1.5 flex-wrap">
                                            <span 
                                                className="fw-bold text-primary cursor-pointer text-decoration-none hover:underline" 
                                                style={{ fontSize: '1.05rem', letterSpacing: '-0.3px' }} 
                                                onClick={() => openAssignDialog(t)}
                                            >
                                                #{t.id}
                                            </span>
                                            <span 
                                                className="cursor-pointer text-muted p-1" 
                                                title="Ver Mapa / Auditoría" 
                                                onClick={() => handleOpenAudit(t)} 
                                                style={{ fontSize: '1rem', lineHeight: 1 }}
                                            >
                                                🗺️
                                            </span>
                                            {t.client?.name && (
                                                <Badge bg="light" className="text-secondary border fw-semibold" style={{ fontSize: '11px' }}>
                                                    {t.client.name}
                                                </Badge>
                                            )}
                                            {t.external_reference && (
                                                <span className="text-secondary fw-semibold bg-light px-1.5 py-0.5 rounded border" style={{ fontSize: '11px' }}>
                                                    OS: {t.external_reference}
                                                </span>
                                            )}
                                            <VehicleTypeIcon type={t.requested_vehicle_type} />
                                        </div>

                                        <div className="text-end">
                                            <div className="fw-bold text-success" style={{ fontSize: '1.1rem', letterSpacing: '-0.3px', lineHeight: 1.1 }}>
                                                {formatTripEarnings(t, providerDetails?.currency_code)}
                                            </div>
                                            {isCrit && (
                                                <span className="badge bg-danger text-white uppercase mt-1" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>
                                                    🚨 {translate('urgent')}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sub-header: Estado y Horario Local / Destino */}
                                    <div className="d-flex justify-content-between align-items-center bg-light bg-opacity-75 p-2 rounded-3 mb-2.5">
                                        <div className="d-flex align-items-center gap-1.5">
                                            <Clock size={14} className="text-primary flex-shrink-0" />
                                            {isInternational ? (
                                                <div>
                                                    <span className="fw-bold text-primary" style={{ fontSize: '0.85rem' }}>
                                                        {flag} {new Intl.DateTimeFormat('es-CL', {
                                                            day: '2-digit', month: '2-digit',
                                                            hour: '2-digit', minute: '2-digit',
                                                            timeZone: tripTimezone,
                                                            hour12: false
                                                        }).format(new Date(t.scheduled_time))} LOCAL
                                                    </span>
                                                    <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                                        ({format(new Date(t.scheduled_time), 'HH:mm')} Chile)
                                                    </span>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                                                        {flag} {format(new Date(t.scheduled_time), 'HH:mm')}
                                                    </span>
                                                    <span className="text-muted ms-1.5" style={{ fontSize: '0.75rem' }}>
                                                        {format(new Date(t.scheduled_time), 'dd/MM/yyyy')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Badge de Estado con fondo suave y punto */}
                                        <div 
                                            className="d-inline-flex align-items-center gap-1.5 px-2 py-1 rounded-pill"
                                            style={{
                                                backgroundColor: `${getStatusColor(t.status, isCrit)}18`,
                                                border: `1px solid ${getStatusColor(t.status, isCrit)}35`
                                            }}
                                        >
                                            <span 
                                                style={{
                                                    width: '7px',
                                                    height: '7px',
                                                    borderRadius: '50%',
                                                    backgroundColor: getStatusColor(t.status, isCrit),
                                                    display: 'inline-block'
                                                }}
                                            />
                                            <span className="fw-bold" style={{ color: getStatusColor(t.status, isCrit), fontSize: '0.75rem' }}>
                                                {getStatusText(t.status, translate)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Pasajero y Contacto */}
                                    <div className="d-flex justify-content-between align-items-center mb-2.5 px-1">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                                <User size={16} />
                                            </div>
                                            <div>
                                                <div className="fw-bold text-dark" style={{ fontSize: '0.88rem', lineHeight: 1.2 }}>
                                                    {t.passenger_name}
                                                    <Badge bg="secondary" className="ms-1.5 px-1.5 py-0.5 fw-semibold" style={{ fontSize: '10px' }}>
                                                        x{Math.max(t.passenger_count || 1, 1 + (t.additional_passengers?.length || 0))}
                                                    </Badge>
                                                </div>
                                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    {t.passenger_phone || 'Sin teléfono'}
                                                </div>
                                            </div>
                                        </div>

                                        {passengerPhoneClean && (
                                            <div className="d-flex gap-1.5">
                                                <a 
                                                    href={`tel:${passengerPhoneClean}`} 
                                                    className="btn btn-outline-primary btn-sm rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                                    title="Llamar al Pasajero"
                                                >
                                                    <Phone size={14} />
                                                </a>
                                                <a 
                                                    href={`https://wa.me/${passengerPhoneClean}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="btn btn-outline-success btn-sm rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                                    title="Enviar WhatsApp"
                                                >
                                                    <MessageCircle size={14} />
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {/* Ruta con Diseño Stepper Elegante */}
                                    <div className="bg-light rounded-3 p-2.5 mb-2.5" style={{ fontSize: '0.8rem' }}>
                                        <div className="d-flex align-items-start gap-2 mb-2">
                                            <div className="text-success mt-0.5 flex-shrink-0" style={{ fontSize: '0.9rem' }}>🟢</div>
                                            <div className="text-break flex-grow-1" style={{ lineHeight: 1.3 }}>
                                                <div className="text-muted fw-semibold" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {isAirport(t.pickup_address) ? '✈️ Aeropuerto / Origen' : 'Origen'}
                                                </div>
                                                <div className="text-dark fw-medium">{t.pickup_address}</div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-start gap-2 pt-1 border-top border-secondary border-opacity-10">
                                            <div className="text-danger mt-0.5 flex-shrink-0" style={{ fontSize: '0.9rem' }}>🏁</div>
                                            <div className="text-break flex-grow-1" style={{ lineHeight: 1.3 }}>
                                                <div className="text-muted fw-semibold" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {isAirport(t.dropoff_address) ? '✈️ Aeropuerto / Destino' : 'Destino'}
                                                </div>
                                                <div className="text-dark fw-medium">{t.dropoff_address}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Observaciones si las hay */}
                                    {(t.notes || t.driver_notes) && (
                                        <div className="bg-amber-50 bg-opacity-50 border border-amber-200 text-amber-900 p-2 rounded-3 mb-2.5" style={{ fontSize: '0.75rem', lineHeight: 1.3 }}>
                                            {t.notes && <div><strong>Obs:</strong> {t.notes}</div>}
                                            {t.driver_notes && <div className="text-muted mt-1"><strong>Chofer:</strong> {t.driver_notes}</div>}
                                        </div>
                                    )}

                                    {/* Asignación de Móvil / Conductor */}
                                    <div className={`d-flex justify-content-between align-items-center p-2 rounded-3 mb-2.5 border ${
                                        isAssigned ? 'bg-light border-light-subtle' : 'bg-warning bg-opacity-10 border-warning border-opacity-25'
                                    }`}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className={`rounded-circle p-1.5 d-flex align-items-center justify-content-center ${
                                                isAssigned ? 'bg-secondary bg-opacity-10 text-secondary' : 'bg-warning bg-opacity-25 text-warning-emphasis'
                                            }`} style={{ width: '28px', height: '28px' }}>
                                                <Car size={15} />
                                            </div>
                                            <div>
                                                <div className="fw-bold text-dark" style={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                                                    {isAssigned ? `${translate('vehicle_word')}: ${vehicleCode || 'M-?'}` : translate('vehicle_not_assigned')}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                    {assignedDriver?.full_name || translate('no_driver')}
                                                </div>
                                            </div>
                                        </div>
                                        <Button 
                                            size="sm" 
                                            variant={isAssigned ? "outline-primary" : "warning"}
                                            className="px-2.5 py-1 fw-bold"
                                            style={{ fontSize: '11px', borderRadius: '6px' }}
                                            onClick={() => openAssignDialog(t)}
                                        >
                                            {isAssigned ? translate('change_btn') : translate('assign')}
                                        </Button>
                                    </div>

                                    {/* Botones de Acción Táctiles */}
                                    {showActions && (
                                        <div className="d-flex flex-wrap gap-1.5 pt-2 border-top justify-content-between align-items-center">
                                            <div className="d-flex flex-wrap gap-1.5 flex-grow-1">
                                                {statusLower === 'pending' && !t.driver_id && (
                                                    <Button size="sm" variant="danger" className="py-1.5 px-3 fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => openAssignDialog(t)}>
                                                        {translate('assign_vehicle')}
                                                    </Button>
                                                )}
                                                {statusLower === 'pending' && t.driver_id && (
                                                    <Button size="sm" variant="primary" className="py-1.5 px-3 fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => handleStatusChange(t, 'assigned')}>
                                                        Confirmar
                                                    </Button>
                                                )}
                                                {statusLower === 'assigned' && (
                                                    <Button size="sm" variant="info" className="py-1.5 px-3 text-white fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => handleStatusChange(t, 'confirmed')}>
                                                        Aceptar Servicio
                                                    </Button>
                                                )}
                                                {statusLower === 'confirmed' && (
                                                    <Button size="sm" variant="primary" className="py-1.5 px-3 fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => handleStatusChange(t, 'en_route')}>
                                                        {translate('start_route')}
                                                    </Button>
                                                )}
                                                {statusLower === 'en_route' && (
                                                    <Button size="sm" variant="info" className="py-1.5 px-3 text-white fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => handleStatusChange(t, 'arrived')}>
                                                        {translate('arrived_origin')}
                                                    </Button>
                                                )}
                                                {statusLower === 'arrived' && (
                                                    <Button size="sm" variant="warning" className="py-1.5 px-3 fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => handleStatusChange(t, 'on_board')}>
                                                        {translate('passenger_onboard')}
                                                    </Button>
                                                )}
                                                {statusLower === 'on_board' && (
                                                    <Button size="sm" variant="success" className="py-1.5 px-3 fw-bold flex-grow-1" style={{ fontSize: '12px', borderRadius: '8px' }} onClick={() => handleStatusChange(t, 'completed')}>
                                                        {translate('finish_trip')}
                                                    </Button>
                                                )}
                                            </div>

                                            <div className="d-flex gap-1 ms-auto">
                                                {isAssigned && ['pending', 'assigned'].includes(statusLower) && (
                                                    <Button size="sm" variant="outline-danger" className="py-1 px-2" style={{ fontSize: '11px', borderRadius: '6px' }} onClick={() => handleReleaseTrip(t.id)} title="Liberar servicio">
                                                        {translate('release')}
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline-secondary" className="py-1 px-2" style={{ fontSize: '11px', borderRadius: '6px' }} onClick={() => openAssignDialog(t)} title="Editar">
                                                    ✏️
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>

                {/* --- VISTA DESKTOP: TABLA COMPLETA (d-none d-md-block) --- */}
                <Card className="shadow-sm border-0 d-none d-md-block bg-white overflow-hidden mb-3">
                    <Table hover responsive size="sm" className="align-middle small mb-0 bg-white">
                        <thead className="bg-light text-muted uppercase" style={{ fontSize: '0.75rem' }}>
                            <tr>
                                <th style={{ width: '60px' }}>ID</th>
                                <th style={{ width: '110px' }}>{translate('th_datetime')}</th>
                                <th style={{ width: '60px' }} className="text-center">{translate('th_type')}</th>
                                <th>{translate('th_client')}</th>
                                <th>{translate('th_passenger')}</th>
                                <th>{translate('th_route')}</th>
                                <th>{translate('th_general_notes')}</th>
                                <th>{translate('th_driver_notes')}</th>
                                <th className="text-center" style={{ width: '60px' }}>{translate('th_estimated_time')}</th>
                                <th>{translate('th_status')}</th>
                                <th className="text-end">{dispatchSubTab === 'active' ? translate('th_est_payment') : translate('th_payment')}</th>
                                <th className="text-center">{translate('th_vehicle')}</th>
                                {showActions && <th className="text-end pe-3">{translate('th_actions')}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {tripsList.map(t => {
                                const statusLower = t.status?.toLowerCase();
                                const isCrit = isTripCritical(t);
                                const vehicleCode = t.vehicle?.internal_code || vehicles.find(v => v.current_driver_id === t.driver_id && v.is_active)?.internal_code;
                                const isAssigned = t.vehicle_id !== null && t.vehicle_id !== undefined;

                                let rowStyle = {};
                                if (isCrit) {
                                    rowStyle = { borderLeft: '4px solid #dc3545', backgroundColor: '#fff8f8' };
                                }

                                return (
                                    <tr key={t.id} style={{ ...rowStyle, '--bs-table-bg': rowStyle.backgroundColor }}>
                                        <td className="ps-2">
                                            <div className="d-flex align-items-center gap-1.5">
                                                <span className="fw-bold cursor-pointer text-primary" onClick={() => openAssignDialog(t)}>#{t.id}</span>
                                                <span className="cursor-pointer" title="Ver Eventos y Ruta de Conductor" onClick={() => handleOpenAudit(t)} style={{ fontSize: '1.1em' }}>🗺️</span>
                                            </div>
                                            {t.external_reference && (
                                                <div className="text-primary fw-bold" style={{ fontSize: '0.65em', lineHeight: 1.1, marginTop: '2px' }}>
                                                    OS: {t.external_reference}
                                                </div>
                                            )}
                                            {isCrit && (
                                                <span className="d-block text-danger fw-bold uppercase" style={{ fontSize: '8px', letterSpacing: '0.5px' }}>
                                                    🚨 {translate('urgent')}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {(() => {
                                                let tripTimezone = t.origin_base?.timezone || t.timezone;
                                                 if (!tripTimezone) {
                                                     if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                                                     else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                                                     else if (t.local_currency_code === 'EUR') tripTimezone = 'Europe/Madrid';
                                                     else if (t.local_currency_code === 'UYU') tripTimezone = 'America/Montevideo';
                                                     else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                                                     else if (t.pickup_lat >= -35.5 && t.pickup_lat <= -30.0 && t.pickup_lng >= -58.5 && t.pickup_lng <= -53.0) tripTimezone = 'America/Montevideo';
                                                     else if (t.pickup_lat >= 36.0 && t.pickup_lat <= 44.0 && t.pickup_lng >= -9.5 && t.pickup_lng <= 4.5) tripTimezone = 'Europe/Madrid';
                                                     else tripTimezone = 'America/Santiago';
                                                 }
                                                 const isInternational = tripTimezone !== 'America/Santiago';
                                                 const flag = getFlagEmoji(tripTimezone, t.local_currency_code, t.pickup_lat, t.pickup_lng, t.origin_base);
                                                if (isInternational) {
                                                    return (
                                                        <>
                                                            <div className="fw-bold text-primary" style={{ fontSize: '0.85rem' }}>
                                                                {flag} {new Intl.DateTimeFormat('es-CL', {
                                                                    day: '2-digit', month: '2-digit', year: '2-digit',
                                                                    hour: '2-digit', minute: '2-digit',
                                                                    timeZone: tripTimezone,
                                                                    hour12: false
                                                                }).format(new Date(t.scheduled_time))} LOCAL
                                                            </div>
                                                            <div className="text-muted" style={{ fontSize: '0.7em' }}>
                                                                {format(new Date(t.scheduled_time), 'HH:mm')} CHILE {format(new Date(t.scheduled_time), 'dd/MM/yy')}
                                                            </div>
                                                        </>
                                                    );
                                                } else {
                                                    return (
                                                        <>
                                                            <div className="fw-bold">{flag} {format(new Date(t.scheduled_time), 'HH:mm')}</div>
                                                            <div className="text-muted small">{format(new Date(t.scheduled_time), 'dd/MM/yy')}</div>
                                                        </>
                                                    );
                                                }
                                            })()}
                                        </td>
                                        <td className="text-center"><VehicleTypeIcon type={t.requested_vehicle_type} /></td>
                                        <td className="fw-bold text-dark">{t.client?.name || '—'}</td>
                                        <td>
                                            <div className="fw-bold">
                                                👤 {t.passenger_name}
                                                <Badge bg="secondary" className="ms-1.5 px-1 py-0.5" style={{ fontSize: '10px' }}>
                                                    x{Math.max(t.passenger_count || 1, 1 + (t.additional_passengers?.length || 0))}
                                                </Badge>
                                            </div>
                                            <div className="text-primary small" style={{ fontSize: '0.85em' }}>{t.passenger_phone || 'Sin tel.'}</div>
                                        </td>
                                        <td style={{ maxWidth: '200px' }}>
                                            <div className="small text-truncate" title={t.pickup_address}>
                                                <span className="text-success me-1">🟢</span>
                                                {isAirport(t.pickup_address) && <span className="text-info me-1" style={{ fontSize: '10px' }}>✈️</span>}
                                                {t.pickup_address}
                                            </div>
                                            <div className="small text-truncate mt-1" title={t.dropoff_address}>
                                                <span className="text-danger me-1">🏁</span>
                                                {isAirport(t.dropoff_address) && <span className="text-info me-1" style={{ fontSize: '10px' }}>✈️</span>}
                                                {t.dropoff_address}
                                            </div>
                                        </td>
                                        <td className="text-muted" style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {t.notes ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip id={`tooltip-notes-${t.id}`}>{t.notes}</Tooltip>}
                                                >
                                                    <span style={{ cursor: 'pointer' }}>{t.notes}</span>
                                                </OverlayTrigger>
                                            ) : '—'}
                                        </td>
                                        <td className="text-muted" style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {t.driver_notes ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip id={`tooltip-dnotes-${t.id}`}>{t.driver_notes}</Tooltip>}
                                                >
                                                    <span style={{ cursor: 'pointer' }}>{t.driver_notes}</span>
                                                </OverlayTrigger>
                                            ) : '—'}
                                        </td>
                                        <td className="text-center" style={{ backgroundColor: 'rgba(13, 110, 253, 0.02)' }}>
                                            <div className="fw-bold text-primary">{t.estimated_duration_minutes || '-'} '</div>
                                            {t.distance_km > 0 && <div className="text-muted" style={{ fontSize: '0.65em' }}>{t.distance_km.toFixed(1)} km</div>}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span 
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: getStatusColor(t.status, isCrit),
                                                        display: 'inline-block',
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <span className="fw-bold" style={{ color: getStatusColor(t.status, isCrit), fontSize: '0.85rem' }}>
                                                    {getStatusText(t.status, translate)}
                                                </span>
                                            </div>
                                            {isCrit && <div className="text-danger fw-bold" style={{ fontSize: '0.65rem' }}>{translate('manage_urgent')}</div>}
                                        </td>
                                        <td className="text-end fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                                            {formatTripEarnings(t, providerDetails?.currency_code)}
                                        </td>
                                        <td className="text-center">
                                            {isAssigned ? (
                                                <div className="fw-bold text-dark cursor-pointer" onClick={() => openAssignDialog(t)}>
                                                    🚙 {vehicleCode || 'M-?'}
                                                    <div className="text-muted" style={{ fontSize: '9px', fontWeight: 'normal' }}>
                                                        {drivers.find(d => d.id === t.driver_id)?.full_name || 'Chofer'}
                                                    </div>
                                                </div>
                                            ) : (
                                                <Badge bg="danger">{translate("unassigned")}</Badge>
                                            )}
                                        </td>
                                        {showActions && (
                                            <td className="text-end pe-3">
                                                <div className="d-flex justify-content-end gap-1">
                                                    {statusLower === 'pending' && !t.driver_id && <Button size="sm" variant="danger" className="py-0.5 px-2" style={{ fontSize: '11px' }} onClick={() => openAssignDialog(t)}>{translate('assign')}</Button>}
                                                    {statusLower === 'pending' && t.driver_id && <Button size="sm" variant="primary" className="py-0.5 px-2" style={{ fontSize: '11px' }} onClick={() => handleStatusChange(t, 'assigned')}>{translate('confirm')}</Button>}
                                                    {statusLower === 'assigned' && <Button size="sm" variant="info" className="py-0.5 px-2 text-white" style={{ fontSize: '11px' }} onClick={() => handleStatusChange(t, 'confirmed')}>{translate('accept')}</Button>}
                                                    {statusLower === 'confirmed' && <Button size="sm" variant="outline-primary" className="py-0.5 px-1.5" style={{ fontSize: '11px' }} onClick={() => handleStatusChange(t, 'en_route')}>▶</Button>}
                                                    {statusLower === 'en_route' && <Button size="sm" variant="outline-info" className="py-0.5 px-1.5" style={{ fontSize: '11px' }} onClick={() => handleStatusChange(t, 'arrived')}>📍</Button>}
                                                    {statusLower === 'arrived' && <Button size="sm" variant="outline-warning" className="py-0.5 px-1.5" style={{ fontSize: '11px' }} onClick={() => handleStatusChange(t, 'on_board')}>👋</Button>}
                                                    {statusLower === 'on_board' && <Button size="sm" variant="success" className="py-0.5 px-1.5" style={{ fontSize: '11px' }} onClick={() => handleStatusChange(t, 'completed')}>{translate('finish_trip')}</Button>}
                                                    
                                                    {isAssigned && ['pending', 'assigned'].includes(statusLower) && (
                                                        <Button size="sm" variant="outline-danger" className="py-0.5 px-1.5" style={{ fontSize: '11px' }} onClick={() => handleReleaseTrip(t.id)}>✕</Button>
                                                    )}
                                                    <Button size="sm" variant="outline-secondary" className="py-0.5 px-1.5" style={{ fontSize: '11px' }} onClick={() => openAssignDialog(t)}>✏️</Button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card>

                {/* Footer Paginación Compartida (Móvil y Desktop) */}
                <div className="d-flex justify-content-between align-items-center bg-white border rounded shadow-sm py-2 px-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">{translate('view_per_page')}</span>
                        <Form.Select 
                            size="sm" 
                            value={pageSize} 
                            onChange={e => {
                                setPageSize(parseInt(e.target.value));
                                setPage(1);
                            }} 
                            style={{ width: '70px' }}
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </Form.Select>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button variant="outline-secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>&lt;</Button>
                        <span className="small fw-semibold">{translate('page_x_of_y')} {page} de {Math.max(1, Math.ceil(filteredTrips.length / pageSize))}</span>
                        <Button variant="outline-secondary" size="sm" disabled={page >= Math.ceil(filteredTrips.length / pageSize)} onClick={() => setPage(p => p + 1)}>&gt;</Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Container fluid className="px-2 py-3 px-md-4 py-md-4 bg-white min-h-screen text-dark font-sans">
            {isLoading && (
                <div className="fixed top-4 right-4 bg-white border rounded shadow-lg px-4 py-2 flex items-center gap-2 text-xs text-primary z-50">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span>{translate('provider_syncing')}</span>
                </div>
            )}

            {/* HEADER SUPERIOR */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 border-bottom pb-3">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="bg-primary p-2 rounded text-white d-inline-flex justify-content-center align-items-center" style={{ width: '40px', height: '40px' }}>
                        <Navigation size={22} className="rotate-45" />
                    </div>
                    <div>
                        <h2 className="mb-0 fw-bold text-dark d-inline align-middle" style={{ fontSize: '1.25rem' }}>{translate('provider_title')}</h2>
                        <Badge bg="purple" className="ms-2" style={{ backgroundColor: '#8b5cf6', fontSize: '0.65rem', verticalAlign: 'middle' }}>{translate('provider_role_badge')}</Badge>
                    </div>
                </div>
                <div className="d-flex gap-2 align-items-center w-100 w-md-auto justify-content-between justify-content-md-end flex-wrap">
                    <LanguageSelector />
                    {user && (
                        <div className="d-flex align-items-center gap-2">
                            <div className="w-8 h-8 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center font-bold" style={{ width: '32px', height: '32px' }}>
                                {user.name ? user.name.slice(0,2).toUpperCase() : 'PV'}
                            </div>
                            <span className="text-muted fw-semibold small text-truncate" style={{ maxWidth: '180px' }}>
                                {user.name}
                            </span>
                        </div>
                    )}
                    <Button variant="outline-danger" size="sm" onClick={handleLogout} className="d-flex align-items-center gap-1">
                        <LogOut size={14} /> {translate('logout')}
                    </Button>
                </div>
            </div>

            {/* MENUS TABS SUPERIORES */}
            <Nav variant="tabs" className="mb-4 overflow-auto flex-nowrap" activeKey={activeTab} onSelect={setActiveTab} style={{ WebkitOverflowScrolling: 'touch' }}>
                <Nav.Item>
                    <Nav.Link eventKey="dispatch" className="fw-bold text-nowrap">{translate('provider_dispatch_tab')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="claims" className="fw-bold text-danger position-relative text-nowrap">
                        {translate('provider_claims_tab')}
                        {pendingIncidentsCount > 0 && (
                            <Badge bg="danger" pill className="ms-2">
                                {pendingIncidentsCount}
                            </Badge>
                        )}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="fleet" className="fw-bold text-nowrap">{translate('provider_fleet_tab')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="tariffs" className="fw-bold text-nowrap">{translate('provider_tariffs_tab')}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="sentinel" className="fw-bold text-primary d-flex align-items-center gap-1 text-nowrap">
                        {translate('provider_sentinel_tab')}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="meeting_points" className="fw-bold d-flex align-items-center gap-1 text-nowrap" style={{ color: '#003580' }}>
                        📍 Puntos de Encuentro (Booking)
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="quality" className="fw-bold text-warning d-flex align-items-center gap-1 text-nowrap">
                        <Star size={16} className="text-warning fill-warning" /> {translate('provider_quality_tab')}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="settlements" className="fw-bold text-success d-flex align-items-center gap-1 text-nowrap">
                        🧾 Liquidaciones
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {/* ─── TABS CONTENT: DESPACHO ────────────────────────────────────────── */}
            {activeTab === 'dispatch' && (
                <div className="space-y-4">
                    {/* Fila superior con filtros rápidos e itinerarios */}
                    <div className="d-flex justify-content-between mb-3 flex-wrap gap-2 align-items-end">
                        <div>
                            <h3 className="fw-bold text-dark mb-1">{t('daily_dispatch')}</h3>
                            <div className="overflow-auto pb-1" style={{ maxWidth: '100%' }}>
                                <ButtonGroup size="sm">
                                    <Button variant={filterMode === 'all_active' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('all_active')}>{t('all')}</Button>
                                    <Button variant={filterMode === 'today' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('today')}>{t('today_filter')}</Button>
                                    <Button variant={filterMode === 'this_month' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('this_month')}>{t('month')}</Button>
                                    <Button variant={filterMode === 'custom' ? 'primary' : 'outline-primary'} onClick={() => setFilterMode('custom')}>{t('range')}</Button>
                                </ButtonGroup>
                            </div>
                            {filterMode === 'custom' && (
                                <div className="d-inline-flex gap-1 mt-2 align-middle">
                                    <Form.Control type="date" size="sm" value={startDateFilter} onChange={e => setStartDateFilter(e.target.value)}/>
                                    <Form.Control type="date" size="sm" value={endDateFilter} onChange={e => setEndDateFilter(e.target.value)}/>
                                </div>
                            )}
                        </div>


                    </div>

                    {/* SUB-TABS: LOCUCIÓN VS HISTORIAL */}
                    <Card className="mb-3 bg-light border-0 shadow-sm">
                        <Card.Body className="py-2 px-3">
                            <Row className="g-2 align-items-center">
                                <Col xs={12} md="auto" className="d-flex align-items-center gap-2 flex-wrap">
                                    <Nav variant="pills" activeKey={dispatchSubTab} onSelect={setDispatchSubTab} className="small me-2">
                                        <Nav.Item>
                                            <Nav.Link eventKey="active" className="py-1 px-3">
                                                {t('dispatch_subtab_speech')}
                                                {availableTrips.length > 0 && (
                                                    <span className="badge bg-amber-500 text-dark font-bold ms-1" style={{ fontSize: '9px' }}>
                                                        {availableTrips.length}
                                                    </span>
                                                )}
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item><Nav.Link eventKey="finished" className="py-1 px-3">{t('history')}</Nav.Link></Nav.Item>
                                    </Nav>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="py-0 px-2 d-flex align-items-center justify-content-center" 
                                        style={{ height: '28px', fontSize: '11px' }}
                                        onClick={() => loadPortalData()}
                                        disabled={isLoading}
                                    >
                                        {t('reload')}
                                    </Button>
                                    {dispatchSubTab === 'active' && (
                                        <Button 
                                            variant={filterUnassignedOnly ? 'warning' : 'outline-warning'} 
                                            size="sm" 
                                            className="py-0 px-2 d-flex align-items-center justify-content-center fw-bold" 
                                            style={{ height: '28px', fontSize: '11px' }}
                                            onClick={() => setFilterUnassignedOnly(prev => !prev)}
                                        >
                                            🔍 {filterUnassignedOnly ? t('all') : t('unassigned')}
                                        </Button>
                                    )}
                                    {/* Botón Filtros Avanzados para Móvil */}
                                    <Button 
                                        variant={showMobileFilters ? "primary" : "outline-secondary"}
                                        size="sm" 
                                        className="d-md-none py-0 px-2 d-flex align-items-center gap-1 ms-auto" 
                                        style={{ height: '28px', fontSize: '11px' }}
                                        onClick={() => setShowMobileFilters(prev => !prev)}
                                    >
                                        <Filter size={12} /> {t('filters')} {showMobileFilters ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                    </Button>
                                </Col>
                                <div className="vr mx-2 d-none d-md-block"></div>
                                
                                {/* FILTROS: Visibles en desktop o cuando el toggle móvil está activo */}
                                <Col xs={12} md className={showMobileFilters ? "d-block pt-2 pt-md-0" : "d-none d-md-block"}>
                                    <div className="d-flex gap-2 flex-wrap align-items-center">
                                        <div style={{ minWidth: '130px', flex: '1 1 130px', position: 'relative', zIndex: 10 }}>
                                            <SearchableMultiSelect 
                                                options={clients} 
                                                selected={selectedClient} 
                                                onChange={setSelectedClient} 
                                                placeholder={t("filter_clients")}
                                                displayField="name"
                                            />
                                        </div>
                                        <div style={{ minWidth: '150px', flex: '1 1 150px', position: 'relative', zIndex: 10 }}>
                                            <SearchableMultiSelect 
                                                options={driverOrVehicleOptions} 
                                                selected={selectedDriverOrVehicle} 
                                                onChange={setSelectedDriverOrVehicle} 
                                                placeholder={t("filter_driver_vehicle")}
                                                displayField="name"
                                            />
                                        </div>
                                        <div style={{ minWidth: '120px', flex: '1 1 120px', position: 'relative', zIndex: 10 }}>
                                            <SearchableMultiSelect 
                                                options={statusOptions} 
                                                selected={filterStatus} 
                                                onChange={setFilterStatus} 
                                                placeholder={t("filter_status")}
                                                displayField="name"
                                            />
                                        </div>
                                        <div style={{ minWidth: '130px', flex: '1 1 130px', position: 'relative', zIndex: 10 }}>
                                            <SearchableMultiSelect 
                                                options={areaOptions} 
                                                selected={selectedAreas} 
                                                onChange={setSelectedAreas} 
                                                placeholder={t("filter_areas")}
                                                displayField="name"
                                            />
                                        </div>
                                        <div style={{ minWidth: '130px', flex: '1 1 130px', position: 'relative', zIndex: 10 }}>
                                            <SearchableMultiSelect 
                                                options={costCenterOptions} 
                                                selected={selectedCostCenters} 
                                                onChange={setSelectedCostCenters} 
                                                placeholder={t("filter_cost_centers")}
                                                displayField="name"
                                            />
                                        </div>
                                        <div style={{ minWidth: '130px', flex: '1 1 130px', position: 'relative', zIndex: 10 }}>
                                            <SearchableMultiSelect 
                                                options={requesterOptions} 
                                                selected={selectedRequesters} 
                                                onChange={setSelectedRequesters} 
                                                placeholder={t("filter_requesters")}
                                                displayField="name"
                                            />
                                        </div>
                                        
                                        <Form.Control 
                                            type="text" 
                                            placeholder="ID" 
                                            size="sm" 
                                            value={searchTripId} 
                                            onChange={e => setSearchTripId(e.target.value)} 
                                            style={{ width: '70px', flex: '0 0 70px' }} 
                                        />
                                        <Form.Control 
                                            type="text" 
                                            placeholder="ID AQ" 
                                            size="sm" 
                                            value={searchAquivoyId} 
                                            onChange={e => setSearchAquivoyId(e.target.value)} 
                                            style={{ width: '80px', flex: '0 0 80px' }} 
                                        />
                                        <Form.Control 
                                            type="text" 
                                            placeholder="OS" 
                                            size="sm" 
                                            value={searchOsId} 
                                            onChange={e => setSearchOsId(e.target.value)} 
                                            style={{ width: '100px', flex: '0 0 100px' }} 
                                        />
                                        <Form.Control 
                                            type="text" 
                                            placeholder="File" 
                                            size="sm" 
                                            value={searchFile} 
                                            onChange={e => setSearchFile(e.target.value)} 
                                            style={{ width: '90px', flex: '0 0 90px' }} 
                                        />
                                        <Form.Control 
                                            type="text" 
                                            placeholder={t("filter_passenger")} 
                                            size="sm" 
                                            value={searchTerm} 
                                            onChange={e => setSearchTerm(e.target.value)} 
                                            style={{ width: '110px', flex: '1 1 110px' }} 
                                        />
                                        
                                        <Button 
                                            variant="success" 
                                            size="sm" 
                                            onClick={handleExportExcel} 
                                            disabled={isLoading || filteredTrips.length === 0} 
                                            className="d-flex align-items-center gap-1"
                                        >
                                            📥 Excel ({filteredTrips.length})
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Resumen de Montos (Desktop: tarjetas elegantes; Móvil: banner compacto y limpio) */}
                    {/* Versión Desktop (d-none d-md-block) */}
                    <div className="d-none d-md-flex justify-content-end align-items-center gap-2 mb-3">
                        <div className="bg-white border rounded shadow-sm px-3 py-2 d-flex flex-column align-items-end">
                            <span className="text-muted fw-bold mb-0" style={{ fontSize: '10px', letterSpacing: '1px' }}>{t('services_count')}</span>
                            <h4 className="mb-0 fw-bold text-primary">{summaryStats.count}</h4>
                        </div>
                        <div className="bg-dark text-white rounded shadow-sm px-3 py-2 d-flex flex-column align-items-end border-start border-4 border-success">
                            <span className="text-success fw-bold mb-0" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                                {dispatchSubTab === 'active' ? t('total_estimated_payment') : t('total_payment')}
                            </span>
                            <h4 className="mb-0 fw-bold text-success">{summaryStats.formattedTotal}</h4>
                        </div>
                    </div>

                    {/* Versión Móvil: Barrita compacta sin desbordes (d-md-none) */}
                    <div className="d-md-none bg-dark text-white rounded-3 p-2.5 mb-3 shadow-sm d-flex justify-content-between align-items-center border-start border-4 border-success">
                        <div>
                            <div className="text-muted text-uppercase" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>
                                {dispatchSubTab === 'active' ? t('estimated_payment') : t('total_payment')}
                            </div>
                            <div className="fw-bold text-success" style={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                                {summaryStats.formattedTotal}
                            </div>
                        </div>
                        <div className="text-end border-start border-secondary ps-3">
                            <div className="text-muted text-uppercase" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>{t('services_count')}</div>
                            <div className="fw-bold text-white" style={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                                {summaryStats.count}
                            </div>
                        </div>
                    </div>

                    {/* TABLAS SEGÚN SUB-TAB */}
                    {dispatchSubTab === 'active' ? (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2.5">
                                <h5 className="fw-bold text-primary mb-0 d-flex align-items-center gap-1.5" style={{ fontSize: '1.05rem' }}>
                                    <span>📅</span> {t('active_dispatch_title')}
                                </h5>
                                <Badge bg="primary" pill className="px-2 py-1" style={{ fontSize: '11px' }}>
                                    {filteredTrips.length} {t('in_course')}
                                </Badge>
                            </div>
                            {renderTripsTable(filteredTrips, true)}
                        </div>
                    ) : (
                        /* Historial */
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2.5">
                                <h5 className="fw-bold text-secondary mb-0 d-flex align-items-center gap-1.5" style={{ fontSize: '1.05rem' }}>
                                    <span>✅</span> {t('history')}
                                </h5>
                                <Badge bg="secondary" pill className="px-2 py-1" style={{ fontSize: '11px' }}>
                                    {filteredTrips.length} {t('completed_count')}
                                </Badge>
                            </div>
                            {renderTripsTable(paginatedTrips, false)}
                        </div>
                    )}
                </div>
            )}

            {/* ─── TABS CONTENT: RECLAMOS E INCIDENCIAS ───────────────────────── */}
            {activeTab === 'claims' && (
                <div className="space-y-4">
                    <div className="d-flex justify-content-between mb-3 align-items-center flex-wrap gap-2">
                        <div>
                            <h3 className="fw-bold text-danger mb-1">{t('claims_title')}</h3>
                            <p className="text-muted small mb-0">{t('claims_subtitle')}</p>
                        </div>
                        <div className="d-flex align-items-center gap-2 overflow-auto pb-1" style={{ maxWidth: '100%' }}>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={handleSyncClaims}
                                disabled={isSyncingClaims}
                                className="d-flex align-items-center gap-1.5 text-nowrap fw-bold shadow-sm px-3"
                                title="Consultar nuevos reclamos de Booking.com directamente desde Gmail"
                            >
                                <RefreshCw size={13} className={isSyncingClaims ? "spin" : ""} />
                                {isSyncingClaims ? "Sincronizando..." : "🔄 Sincronizar Reclamos (Gmail)"}
                            </Button>
                            <ButtonGroup size="sm">
                                <Button 
                                    variant={claimsSubTab === 'pending' ? 'danger' : 'outline-danger'} 
                                    onClick={() => setClaimsSubTab('pending')}
                                    className="fw-bold text-nowrap"
                                >
                                    {t('claims_subtab_pending')} ({pendingIncidentsCount})
                                </Button>
                                <Button 
                                    variant={claimsSubTab === 'submitted' ? 'success' : 'outline-success'} 
                                    onClick={() => setClaimsSubTab('submitted')}
                                    className="fw-bold text-nowrap"
                                >
                                    {t('claims_subtab_submitted')} ({submittedIncidentsCount})
                                </Button>
                                <Button 
                                    variant={claimsSubTab === 'closed' ? 'secondary' : 'outline-secondary'} 
                                    onClick={() => setClaimsSubTab('closed')}
                                    className="fw-bold text-nowrap"
                                >
                                    {t('claims_subtab_history')} ({closedIncidentsCount})
                                </Button>
                                <Button 
                                    variant={claimsSubTab === 'all' ? 'dark' : 'outline-dark'} 
                                    onClick={() => setClaimsSubTab('all')}
                                    className="text-nowrap"
                                >
                                    {t('claims_subtab_all')} ({incidentsTrips.length})
                                </Button>
                            </ButtonGroup>
                        </div>
                    </div>

                    {filteredIncidentsTrips.length === 0 ? (
                        <Card className="text-center p-5 border-0 shadow-sm">
                            <Card.Body>
                                <span style={{ fontSize: '48px' }}>🎉</span>
                                <h5 className="mt-3 fw-bold text-success">
                                    {claimsSubTab === 'pending' ? "¡Sin reclamos pendientes por responder!" : "No hay registros en esta sección."}
                                </h5>
                                <p className="text-muted small mb-0">
                                    {claimsSubTab === 'pending' 
                                        ? "Tu flota no registra reclamos activos pendientes de descargos." 
                                        : "No se encontraron incidencias en el filtro seleccionado."}
                                </p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div>
                            {/* VISTA MÓVIL: Tarjetas de Reclamos */}
                            <div className="d-md-none d-flex flex-column gap-3 mb-3">
                                {filteredIncidentsTrips.map(trip => {
                                    const closed = isClaimClosed(trip);
                                    return (
                                        <Card key={`mob-claim-${trip.id}`} className={`border-0 shadow-sm ${closed ? 'opacity-75 bg-light' : 'bg-white'}`} style={{ borderLeft: closed ? '5px solid #6c757d' : '5px solid #dc3545', borderRadius: '10px' }}>
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <div className="fw-bold text-primary" style={{ fontSize: '0.95rem' }}>
                                                            {trip.external_reference || `#${trip.id}`}
                                                        </div>
                                                        <div className="text-muted small">Reserva #{trip.id}</div>
                                                    </div>
                                                    <Badge bg={closed ? "secondary" : "danger"} className="text-wrap">
                                                        {trip.booking_incident_type || 'INCIDENCIA BOOKING'}
                                                    </Badge>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded mb-2" style={{ fontSize: '0.8rem' }}>
                                                    <div>
                                                        👤 <strong>{trip.passenger_name || 'Sin Pasajero'}</strong>
                                                        {trip.passenger_phone && <div className="text-muted">{trip.passenger_phone}</div>}
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-bold">{formatDateSafe(trip.scheduled_time || trip.scheduled_date)}</div>
                                                        <div className="text-muted small">{formatTimeSafe(trip.scheduled_time)}</div>
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: '0.8rem' }}>
                                                    <div>
                                                        <span className="text-muted">Penalización: </span>
                                                        {trip.booking_incident_penalty_pct ? (
                                                            <span className="text-danger fw-bold">{trip.booking_incident_penalty_pct}%</span>
                                                        ) : (
                                                            <span className="text-muted">Por definir</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        {trip.booking_incident_status === 'RESOLVED' ? (
                                                            <Badge bg="success">Resuelta</Badge>
                                                        ) : (trip.booking_incident_status === 'PENALIZED' || (trip.booking_incident_penalty_pct && trip.booking_incident_penalty_pct > 0)) ? (
                                                            <Badge bg="danger">Penalizada</Badge>
                                                        ) : (trip.provider_appeal_status === 'submitted' || trip.booking_incident_dispute_sent) ? (
                                                            <Badge bg="success" className="d-inline-flex align-items-center gap-1 shadow-sm">
                                                                <CheckCircle2 size={12} /> Respondido
                                                            </Badge>
                                                        ) : trip.provider_appeal_status === 'reviewed' ? (
                                                            <Badge bg="primary">{translate('status_under_review')}</Badge>
                                                        ) : (
                                                            <Badge bg="warning" text="dark">Pendiente</Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="pt-2 border-top text-end">
                                                    {closed ? (
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            size="sm" 
                                                            onClick={() => handleOpenClaimModal(trip)}
                                                            className="w-100 py-1.5 d-inline-flex justify-content-center align-items-center gap-1"
                                                        >
                                                            👁️ {translate('btn_view_detail')}
                                                        </Button>
                                                    ) : (trip.provider_appeal_status === 'submitted' || trip.provider_appeal_status === 'reviewed' || trip.booking_incident_dispute_sent) ? (
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm" 
                                                            onClick={() => handleOpenClaimModal(trip)}
                                                            className="w-100 py-1.5 d-inline-flex justify-content-center align-items-center gap-1 fw-semibold"
                                                        >
                                                            <CheckCircle2 size={14} className="text-success" /> Ver Respuesta Enviada
                                                        </Button>
                                                    ) : (
                                                        <Button 
                                                            variant="danger" 
                                                            size="sm"
                                                            onClick={() => handleOpenClaimModal(trip)}
                                                            className="w-100 py-1.5 d-inline-flex justify-content-center align-items-center gap-1 fw-bold"
                                                        >
                                                            <AlertTriangle size={14} /> {translate('btn_respond_claim')}
                                                        </Button>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    );
                                })}
                            </div>

                            {/* VISTA DESKTOP: Tabla de Reclamos */}
                            <Card className="border-0 shadow-sm d-none d-md-block">
                                <Card.Body className="p-0">
                                    <Table responsive hover align="middle" className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-3">{translate('th_os_booking')}</th>
                                                <th>{translate('lbl_passenger')}</th>
                                                <th>{translate('th_datetime')}</th>
                                                <th>{translate('th_claim_type')}</th>
                                                <th>{translate('th_penalty')}</th>
                                                <th>{translate('th_appeal_status')}</th>
                                                <th className="pe-3 text-end">{translate('th_action')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredIncidentsTrips.map(trip => {
                                                const closed = isClaimClosed(trip);
                                                return (
                                                    <tr key={trip.id} className={closed ? "table-light opacity-75" : ""}>
                                                        <td className="ps-3 fw-bold">
                                                            <div className="text-primary">{trip.external_reference || `#${trip.id}`}</div>
                                                            <small className="text-muted">Reserva #{trip.id}</small>
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold">{trip.passenger_name || 'Sin Pasajero'}</div>
                                                            <small className="text-muted">{trip.passenger_phone || ''}</small>
                                                        </td>
                                                        <td>
                                                            <div className="fw-bold">{formatDateSafe(trip.scheduled_time || trip.scheduled_date)}</div>
                                                            <div className="small text-muted">{formatTimeSafe(trip.scheduled_time)}</div>
                                                        </td>
                                                        <td>
                                                            <Badge bg={closed ? "secondary" : "danger"} className="text-wrap">
                                                                {trip.booking_incident_type || 'INCIDENCIA BOOKING'}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {trip.booking_incident_penalty_pct ? (
                                                                <span className="text-danger fw-bold">{trip.booking_incident_penalty_pct}%</span>
                                                            ) : (
                                                                <span className="text-muted">Por definir</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {trip.booking_incident_status === 'RESOLVED' ? (
                                                                <Badge bg="success">Resuelta (Ganada)</Badge>
                                                            ) : (trip.booking_incident_status === 'PENALIZED' || (trip.booking_incident_penalty_pct && trip.booking_incident_penalty_pct > 0)) ? (
                                                                <Badge bg="danger">Penalizada (Cerrada)</Badge>
                                                            ) : (trip.provider_appeal_status === 'submitted' || trip.booking_incident_dispute_sent) ? (
                                                                <div>
                                                                    <Badge bg="success" className="d-inline-flex align-items-center gap-1 shadow-sm">
                                                                        <CheckCircle2 size={12} /> Respondido a Booking
                                                                    </Badge>
                                                                    {trip.provider_appeal_at && (
                                                                        <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                                                                            {formatDateSafe(trip.provider_appeal_at)} {formatTimeSafe(trip.provider_appeal_at)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : trip.provider_appeal_status === 'reviewed' ? (
                                                                <Badge bg="primary">{translate('status_under_review')}</Badge>
                                                            ) : (
                                                                <Badge bg="warning" text="dark">Apelación Pendiente</Badge>
                                                            )}
                                                        </td>
                                                        <td className="pe-3 text-end">
                                                            {closed ? (
                                                                <Button 
                                                                    variant="outline-secondary" 
                                                                    size="sm" 
                                                                    onClick={() => handleOpenClaimModal(trip)}
                                                                    className="d-inline-flex align-items-center gap-1"
                                                                >
                                                                    👁️ {translate('btn_view_detail')}
                                                                </Button>
                                                            ) : (trip.provider_appeal_status === 'submitted' || trip.provider_appeal_status === 'reviewed' || trip.booking_incident_dispute_sent) ? (
                                                                <Button 
                                                                    variant="outline-success" 
                                                                    size="sm" 
                                                                    onClick={() => handleOpenClaimModal(trip)}
                                                                    className="d-inline-flex align-items-center gap-1 fw-semibold"
                                                                >
                                                                    <CheckCircle2 size={14} className="text-success" /> Ver Respuesta
                                                                </Button>
                                                            ) : (
                                                                <Button 
                                                                    variant="danger" 
                                                                    size="sm" 
                                                                    onClick={() => handleOpenClaimModal(trip)}
                                                                    className="d-inline-flex align-items-center gap-1 fw-bold"
                                                                >
                                                                    <AlertTriangle size={14} /> {translate('btn_respond_claim')}
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </div>
                    )}
                </div>
            )}

            {/* MODAL DE APELACIÓN DEL PROVEEDOR */}
            <Modal show={showClaimModal} onHide={() => setShowClaimModal(false)} size="lg" centered>
                <Modal.Header 
                    closeButton 
                    className={
                        isClaimClosed(claimTrip) 
                            ? "bg-secondary text-white" 
                            : (claimTrip?.provider_appeal_status === 'submitted' || claimTrip?.booking_incident_dispute_sent)
                                ? "bg-success text-white"
                                : "bg-danger text-white"
                    }
                >
                    <Modal.Title className="h6 fw-bold d-flex align-items-center gap-2">
                        {isClaimClosed(claimTrip) ? (
                            <>👁️ Detalle de Reclamo (Cerrado)</>
                        ) : (claimTrip?.provider_appeal_status === 'submitted' || claimTrip?.booking_incident_dispute_sent) ? (
                            <><CheckCircle2 size={18} /> Reclamo Respondido a Booking.com</>
                        ) : (
                            <><AlertTriangle size={18} /> Apelación de Reclamo</>
                        )} - Reserva {claimTrip?.external_reference || `#${claimTrip?.id}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {claimTrip && (
                        <>
                            {isClaimClosed(claimTrip) && (
                                <div className="alert alert-secondary border-0 mb-4 d-flex align-items-center gap-2">
                                    <div>
                                        <strong>🔒 Disputa Finalizada:</strong> El proceso de resolución de esta incidencia en Booking.com ha concluido.
                                        {claimTrip.booking_incident_status === 'PENALIZED' || (claimTrip.booking_incident_penalty_pct && claimTrip.booking_incident_penalty_pct > 0) ? (
                                            <span className="text-danger fw-bold ms-1">Estado Final: Penalizado ({claimTrip.booking_incident_penalty_pct || 150}%)</span>
                                        ) : (
                                            <span className="text-success fw-bold ms-1">Estado Final: Resuelto / Sin Penalización</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Banner de Estado Respondido a Booking */}
                            {(claimTrip?.provider_appeal_status === 'submitted' || claimTrip?.booking_incident_dispute_sent) && !isClaimClosed(claimTrip) && (
                                <div className="alert alert-success border-0 mb-4 d-flex align-items-center gap-3 shadow-sm">
                                    <CheckCircle2 size={32} className="text-success flex-shrink-0" />
                                    <div className="w-100">
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                                            <strong className="text-success fs-6">✅ Respuesta Oficial Despachada a Booking.com</strong>
                                            {claimTrip.provider_appeal_at && (
                                                <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-25">
                                                    {formatDateSafe(claimTrip.provider_appeal_at)} {formatTimeSafe(claimTrip.provider_appeal_at)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="small text-dark mt-1">
                                            Este reclamo ya fue contestado y disputado formalmente ante Booking.com Taxi Support con los respaldos de la flota. Puedes revisar el correo exacto enviado abajo en el historial.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="alert alert-warning border-0 mb-4">
                                <h6 className="fw-bold mb-1">Motivo del Reclamo (Booking.com):</h6>
                                <p className="mb-1 text-dark fw-semibold">
                                    {claimTrip.booking_incident_type || 'Queja del cliente / Disrupción en el servicio'}
                                </p>
                                <div className="small text-muted">
                                    Pasajero: <strong>{claimTrip.passenger_name}</strong> | Fecha: <strong>{(claimTrip.scheduled_time || claimTrip.scheduled_date) ? new Date(claimTrip.scheduled_time || claimTrip.scheduled_date).toLocaleDateString('es-CL') : ''}</strong>
                                </div>
                            </div>

                            {/* HILO DE CONVERSACIÓN / CORREOS DE BOOKING */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                    <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                        <Mail size={16} className="text-primary" /> Historial de Correos y Reclamos Booking.com
                                    </h6>
                                    <div className="d-flex align-items-center gap-2">
                                        {loadingMessages && <Spinner animation="border" size="sm" variant="primary" />}
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="py-1 px-2.5 d-flex align-items-center gap-1 shadow-sm fw-bold text-nowrap"
                                            style={{ fontSize: '12px' }}
                                            onClick={() => handleSyncSingleTripClaim(claimTrip?.id)}
                                            disabled={isSyncingSingleTrip || loadingMessages}
                                            title="Buscar si Booking envió un correo para esta O.S. en Gmail"
                                        >
                                            <RefreshCw size={12} className={isSyncingSingleTrip ? "spin" : ""} />
                                            {isSyncingSingleTrip ? "Buscando en Gmail..." : "🔄 Buscar Correo en Gmail"}
                                        </Button>
                                    </div>
                                </div>

                                {incidentMessages.length > 0 ? (
                                    <div className="d-flex flex-column gap-3 p-3 bg-light rounded border" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                        {incidentMessages.map((msg, idx) => (
                                            <div 
                                                key={idx} 
                                                className={`p-3 rounded shadow-sm border ${msg.direction === 'inbound' ? 'bg-white border-primary border-opacity-25' : 'bg-success bg-opacity-10 border-success border-opacity-25'}`}
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
                                                    <span className={`fw-bold small d-flex align-items-center gap-1 ${msg.direction === 'inbound' ? 'text-primary' : 'text-success'}`}>
                                                        {msg.direction === 'inbound' ? '📩 Booking.com Support' : '📤 Respuesta Oficial Enviada'}
                                                        <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>({msg.sender})</span>
                                                    </span>
                                                    <span className="text-muted" style={{ fontSize: '11px' }}>
                                                        {msg.created_at ? new Date(msg.created_at).toLocaleString('es-CL') : ''}
                                                    </span>
                                                </div>
                                                <div className="small fw-semibold text-secondary mb-1">
                                                    Asunto: {msg.subject}
                                                </div>
                                                <div className="small text-dark text-break" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                                    {msg.body_text || 'Sin contenido de texto disponible'}
                                                </div>
                                                {msg.evidence_urls && msg.evidence_urls.length > 0 && (
                                                    <div className="mt-2 pt-1 border-top d-flex gap-1 flex-wrap">
                                                        <small className="text-muted fw-bold w-100" style={{ fontSize: '10px' }}>Adjuntos enviados:</small>
                                                        {msg.evidence_urls.map((u, i) => (
                                                            <a key={i} href={u} target="_blank" rel="noreferrer" className="badge bg-secondary text-decoration-none">
                                                                📎 Evidencia #{i+1}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : !loadingMessages ? (
                                    <div className="p-4 bg-light rounded border text-muted text-center">
                                        <p className="small mb-3">
                                            ℹ️ Aún no hay correos enlazados en este hilo para la O.S. #{claimTrip?.external_reference || claimTrip?.id}.
                                        </p>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            onClick={() => handleSyncSingleTripClaim(claimTrip?.id)}
                                            disabled={isSyncingSingleTrip}
                                            className="fw-bold d-inline-flex align-items-center gap-1.5 shadow-sm px-3 py-1.5"
                                        >
                                            <RefreshCw size={14} className={isSyncingSingleTrip ? "spin" : ""} />
                                            {isSyncingSingleTrip ? "Buscando en Gmail..." : "🔍 Buscar y Vincular Correo de Gmail"}
                                        </Button>
                                    </div>
                                ) : null}
                            </div>

                            <Form.Group className="mb-4">
                                <Form.Label className="fw-bold text-dark d-flex justify-content-between align-items-center">
                                    <span>
                                        {(claimTrip.provider_appeal_status === 'submitted' || claimTrip.booking_incident_dispute_sent) 
                                            ? "Descargo Registrado del Proveedor / Conductor:" 
                                            : "Descargo y Versión del Conductor / Proveedor *"}
                                    </span>
                                    {(claimTrip.provider_appeal_status === 'submitted' || claimTrip.booking_incident_dispute_sent) && (
                                        <Badge bg="success" className="fw-normal d-inline-flex align-items-center gap-1">
                                            <CheckCircle2 size={12} /> Enviado en la Disputa
                                        </Badge>
                                    )}
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Explique en detalle lo sucedido (ej: El conductor llegó a tiempo y esperó 20 min, el pasajero no se presentó, etc.)..."
                                    value={appealNotes}
                                    onChange={e => setAppealNotes(e.target.value)}
                                    disabled={submittingAppeal || isClaimClosed(claimTrip)}
                                />
                                {(claimTrip.provider_appeal_status === 'submitted' || claimTrip.booking_incident_dispute_sent) && !isClaimClosed(claimTrip) && (
                                    <Form.Text className="text-muted">
                                        ℹ️ Si necesitas aportar más antecedentes a Booking.com, puedes complementar el texto aquí y adjuntar nuevas fotos a continuación.
                                    </Form.Text>
                                )}
                            </Form.Group>

                            {!isClaimClosed(claimTrip) && (
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold text-dark d-flex align-items-center gap-1">
                                        📸 Subir Imágenes de Evidencia / Respaldos
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={e => {
                                            const selected = Array.from(e.target.files);
                                            if (selected.length === 0) return;
                                            setAppealFiles(prev => {
                                                const existingKeys = new Set(prev.map(f => `${f.name}_${f.size}`));
                                                const filteredNew = selected.filter(f => !existingKeys.has(`${f.name}_${f.size}`));
                                                return [...prev, ...filteredNew];
                                            });
                                        }}
                                        disabled={submittingAppeal}
                                    />
                                    <Form.Text className="text-muted">
                                        Puedes subir múltiples fotos (ej: comprobante de peaje, foto del vehículo en destino, capturas de pantalla de chats o llamadas).
                                    </Form.Text>
                                </Form.Group>
                            )}

                            {appealFiles.length > 0 && (
                                <div className="mb-3">
                                    <small className="fw-bold text-success d-block mb-1">Archivos seleccionados para subir:</small>
                                    <ul className="small text-muted ps-3 mb-0">
                                        {appealFiles.map((f, i) => (
                                            <li key={i} className="d-flex align-items-center gap-2 mb-1">
                                                <span>{f.name} ({Math.round(f.size / 1024)} KB)</span>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm" 
                                                    className="py-0 px-1 border-0" 
                                                    style={{ fontSize: '11px', lineHeight: 1 }}
                                                    onClick={() => handleRemoveLocalFile(i)}
                                                    title="Quitar este archivo de la lista"
                                                >
                                                    ❌ Quitar
                                                </Button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {claimTrip.provider_appeal_evidence && claimTrip.provider_appeal_evidence.length > 0 && (
                                <div className="mt-3 p-3 bg-light rounded border">
                                    <small className="fw-bold text-muted d-block mb-2">📸 Evidencias / Fotos enviadas anteriormente:</small>
                                    <div className="d-flex flex-wrap gap-2">
                                        {claimTrip.provider_appeal_evidence.map((key, idx) => {
                                            const fullUrl = key.startsWith('http') ? key : `${process.env.REACT_APP_API_URL || 'https://flujo-app-backend.onrender.com'}/transport/documents/download/${key}`;
                                            return (
                                                <div key={idx} className="d-flex flex-column align-items-center bg-white p-1 border rounded shadow-sm">
                                                    <img 
                                                        src={fullUrl} 
                                                        alt={`Evidencia ${idx + 1}`} 
                                                        style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '4px' }}
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <div className="d-flex gap-1 mt-1">
                                                        <a
                                                            href={fullUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-outline-primary btn-sm py-0 px-2 text-decoration-none"
                                                            style={{ fontSize: '10px' }}
                                                        >
                                                            🔍 Ver
                                                        </a>
                                                        {!isClaimClosed(claimTrip) && (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="py-0 px-1"
                                                                style={{ fontSize: '10px' }}
                                                                onClick={() => handleDeleteEvidence(key)}
                                                                title="Eliminar esta imagen de evidencia"
                                                            >
                                                                🗑️ Borrar
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => setShowClaimModal(false)} disabled={submittingAppeal}>
                        Cerrar
                    </Button>
                    {!isClaimClosed(claimTrip) && (
                        (claimTrip?.provider_appeal_status === 'submitted' || claimTrip?.booking_incident_dispute_sent) ? (
                            <Button 
                                variant="outline-primary" 
                                onClick={handleSubmitAppeal} 
                                disabled={submittingAppeal} 
                                className="d-flex align-items-center gap-1 shadow-sm fw-semibold"
                            >
                                {submittingAppeal ? <Spinner animation="border" size="sm" /> : <Send size={14} />}
                                {submittingAppeal ? "Enviando actualización..." : "📤 Enviar Información Adicional a Booking"}
                            </Button>
                        ) : (
                            <Button 
                                variant="danger" 
                                onClick={handleSubmitAppeal} 
                                disabled={submittingAppeal} 
                                className="d-flex align-items-center gap-1 fw-bold shadow-sm"
                            >
                                {submittingAppeal ? <Spinner animation="border" size="sm" /> : <Send size={14} />}
                                {submittingAppeal ? "Redactando con IA y enviando a Booking..." : "✨ Responder y Enviar a Booking.com"}
                            </Button>
                        )
                    )}
                </Modal.Footer>
            </Modal>

            {/* ─── TABS CONTENT: MI FLOTA ────────────────────────────────────────── */}
            {activeTab === 'fleet' && (
                <div className="space-y-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 border-bottom pb-3">
                        <div>
                            <h3 className="fw-bold text-dark mb-0">{t('fleet_title')}</h3>
                            <p className="text-muted small mb-0">{t('fleet_subtitle')}</p>
                        </div>
                        
                        <div className="overflow-auto pb-1" style={{ maxWidth: '100%' }}>
                            <ButtonGroup size="sm">
                                <Button variant={fleetTab === 'drivers' ? 'primary' : 'outline-primary'} onClick={() => setFleetTab('drivers')}>{t('tab_drivers')} ({drivers.length})</Button>
                                <Button variant={fleetTab === 'vehicles' ? 'primary' : 'outline-primary'} onClick={() => setFleetTab('vehicles')}>{t('tab_vehicles')} ({vehicles.length})</Button>
                                <Button variant={fleetTab === 'vehicleTypes' ? 'primary' : 'outline-primary'} onClick={() => setFleetTab('vehicleTypes')}>{t('tab_capacities')} ({vehicleTypes.filter(vt => vt.provider_id !== null).length})</Button>
                            </ButtonGroup>
                        </div>
                    </div>

                    {/* Sub-tab: Conductores */}
                    {fleetTab === 'drivers' && (
                        <Card className="shadow-sm border-0 bg-white p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="fw-bold mb-0 text-dark">{t('drivers_list_title')}</h4>
                                <Button variant="primary" size="sm" onClick={handleOpenDriverCreate} className="d-flex align-items-center gap-1.5">
                                    <Plus size={16} /> {t('register_driver_btn')}
                                </Button>
                            </div>

                            {/* VISTA MÓVIL: Tarjetas de Conductores */}
                            <div className="d-md-none d-flex flex-column gap-3 mb-2">
                                {drivers.length === 0 ? (
                                    <div className="text-center py-4 text-muted small bg-light rounded">{t('no_drivers_registered')}</div>
                                ) : (
                                    drivers.map(d => {
                                        const cleanPhone = d.phone ? d.phone.replace(/[^0-9]/g, '') : '';
                                        return (
                                            <div key={`mob-drv-${d.id}`} className="border rounded p-3 bg-white shadow-sm">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                                            <User size={18} />
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark">{d.full_name}</div>
                                                            <div className="text-muted small">RUT: {d.rut || 'Sin RUT'}</div>
                                                        </div>
                                                    </div>
                                                    <Button variant="outline-primary" size="sm" className="py-1 px-2.5" onClick={() => handleOpenDriverEdit(d)}>
                                                        <Edit3 size={13} className="me-1" /> {t('edit')}
                                                    </Button>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded mb-2" style={{ fontSize: '0.8rem' }}>
                                                    <div>
                                                        <span className="text-muted">{t('shift_lbl')}: </span>
                                                        <span className={`inline-flex align-items-center gap-1 px-2 py-0.5 rounded-pill text-xs font-semibold ${
                                                            d.has_active_session 
                                                                ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' 
                                                                : 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
                                                        }`}>
                                                            <span className={`rounded-circle d-inline-block ${d.has_active_session ? 'bg-success' : 'bg-secondary'}`} style={{ width: '6px', height: '6px' }} />
                                                            {d.has_active_session ? t('online') : t('offline')}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        {d.assigned_vehicle_code ? (
                                                            <Badge bg="light" className="text-primary border">
                                                                🚙 {d.assigned_vehicle_code}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-muted small">{t("no_vehicle")}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center pt-1" style={{ fontSize: '0.8rem' }}>
                                                    <div className="text-muted text-truncate me-2">
                                                        {d.phone || 'Sin teléfono'} {d.email && `• ${d.email}`}
                                                    </div>
                                                    {cleanPhone && (
                                                        <div className="d-flex gap-1 flex-shrink-0">
                                                            <a href={`tel:${cleanPhone}`} className="btn btn-outline-primary btn-sm px-2 py-1" title="Llamar">
                                                                <Phone size={13} />
                                                            </a>
                                                            <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline-success btn-sm px-2 py-1" title="WhatsApp">
                                                                <MessageCircle size={13} />
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* VISTA DESKTOP: Tabla de Conductores */}
                            <div className="d-none d-md-block">
                                <Table hover responsive size="sm" className="align-middle bg-white mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>{t("driver_th_name_rut")}</th>
                                            <th>{t("driver_th_contact_email")}</th>
                                            <th>{t("driver_th_shift_status")}</th>
                                            <th>{t("driver_th_assigned_vehicle")}</th>
                                            <th className="text-end">{t('th_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drivers.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted small">{t('no_drivers_registered')}</td>
                                            </tr>
                                        ) : (
                                            drivers.map(d => (
                                                <tr key={d.id}>
                                                    <td>
                                                        <div className="fw-bold">{d.full_name}</div>
                                                        <div className="text-muted small">{d.rut}</div>
                                                    </td>
                                                    <td>
                                                        <div>{d.phone || '-'}</div>
                                                        <div className="text-muted small">{d.email}</div>
                                                    </td>
                                                    <td>
                                                        <span className={`inline-flex align-items-center gap-1 px-2 py-0.5 rounded-pill text-xs font-semibold ${
                                                            d.has_active_session 
                                                                ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' 
                                                                : 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
                                                        }`}>
                                                            <span className={`rounded-circle d-inline-block ${d.has_active_session ? 'bg-success' : 'bg-secondary'}`} style={{ width: '6px', height: '6px' }} />
                                                            {d.has_active_session ? t('on_shift_online') : t('off_service')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {d.assigned_vehicle_code ? (
                                                            <Badge bg="light" className="text-primary border">
                                                                🚙 {d.assigned_vehicle_code}
                                                            </Badge>
                                                        ) : <span className="text-muted small">{t("no_vehicle")}</span>}
                                                    </td>
                                                    <td className="text-end">
                                                        <Button variant="outline-primary" size="sm" className="py-0.5 px-2" onClick={() => handleOpenDriverEdit(d)}>
                                                            <Edit3 size={12} className="me-1" /> {t('edit')}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    )}

                    {/* Sub-tab: Vehículos */}
                    {fleetTab === 'vehicles' && (
                        <Card className="shadow-sm border-0 bg-white p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="fw-bold mb-0 text-dark">{t('vehicles_list_title')}</h4>
                                <Button variant="primary" size="sm" onClick={handleOpenVehicleCreate} className="d-flex align-items-center gap-1.5">
                                    <Plus size={16} /> {t('register_vehicle_btn')}
                                </Button>
                            </div>

                            {/* VISTA MÓVIL: Tarjetas de Móviles */}
                            <div className="d-md-none d-flex flex-column gap-3 mb-2">
                                {vehicles.length === 0 ? (
                                    <div className="text-center py-4 text-muted small bg-light rounded">{t('no_vehicles_registered')}</div>
                                ) : (
                                    vehicles.map(v => {
                                        const typeName = vehicleTypes.find(t => t.id === v.vehicle_type_id)?.name || 'Standard';
                                        const assignedDriverObj = drivers.find(d => d.id === v.current_driver_id);
                                        return (
                                            <div key={`mob-veh-${v.id}`} className="border rounded p-3 bg-white shadow-sm">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="bg-primary text-white fw-bold px-2 py-1 rounded" style={{ fontSize: '0.85rem' }}>
                                                            {v.internal_code}
                                                        </span>
                                                        <div>
                                                            <div className="fw-bold text-dark">{v.plate}</div>
                                                            <div className="text-muted small">{v.make} {v.model} ({v.year || '-'})</div>
                                                        </div>
                                                    </div>
                                                    <Button variant="outline-primary" size="sm" className="py-1 px-2.5" onClick={() => handleOpenVehicleEdit(v)}>
                                                        <Edit3 size={13} className="me-1" /> {t('edit')}
                                                    </Button>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded mb-2" style={{ fontSize: '0.8rem' }}>
                                                    <div>
                                                        <span className="text-muted">{t('veh_th_type')}: </span>
                                                        <Badge bg="secondary" className="text-white ms-1">{typeName}</Badge>
                                                    </div>
                                                    <div className="text-muted small">
                                                        {v.color || t('no_color_specified')}
                                                    </div>
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center pt-1 border-top" style={{ fontSize: '0.8rem' }}>
                                                    <div>
                                                        <span className="text-muted">{t('driver_word')}: </span>
                                                        {assignedDriverObj ? (
                                                            <span className="fw-bold text-primary">{assignedDriverObj.full_name}</span>
                                                        ) : (
                                                            <span className="text-muted italic">- Apagado -</span>
                                                        )}
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant={v.current_driver_id ? 'outline-danger' : 'outline-primary'}
                                                        className="py-1 px-2 fw-semibold"
                                                        style={{ fontSize: '11px' }}
                                                        onClick={() => handleToggleDriverShift(v)}
                                                    >
                                                        {v.current_driver_id ? t('btn_end_shift') : t('btn_start_shift')}
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* VISTA DESKTOP: Tabla de Móviles */}
                            <div className="d-none d-md-block">
                                <Table hover responsive size="sm" className="align-middle bg-white mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>{t('veh_th_code_plate')}</th>
                                            <th>{t('veh_th_features')}</th>
                                            <th>{t('veh_th_type')}</th>
                                            <th>{t('veh_th_shift_driver')}</th>
                                            <th className="text-end">{t('th_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vehicles.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted small">{t('no_vehicles_registered')}</td>
                                            </tr>
                                        ) : (
                                            vehicles.map(v => (
                                                <tr key={v.id}>
                                                    <td>
                                                        <span className="bg-light text-dark fw-bold px-2 py-1 rounded border me-2">
                                                            {v.internal_code}
                                                        </span>
                                                        <span className="fw-bold">{v.plate}</span>
                                                    </td>
                                                    <td>
                                                        <div>{v.make} {v.model}</div>
                                                        <div className="text-muted small">{v.color} - {v.year}</div>
                                                    </td>
                                                    <td>
                                                        {vehicleTypes.find(t => t.id === v.vehicle_type_id)?.name || 'Standard'}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-3">
                                                            {v.current_driver_id ? (
                                                                <Badge bg="light" className="border fw-bold" style={{ color: '#8b5cf6' }}>
                                                                    👤 {drivers.find(d => d.id === v.current_driver_id)?.full_name || 'ID: ' + v.current_driver_id}
                                                                </Badge>
                                                            ) : <span className="text-muted small">- Apagado -</span>}
                                                            
                                                            <Button
                                                                size="sm"
                                                                variant={v.current_driver_id ? 'outline-danger' : 'outline-primary'}
                                                                className="py-0.5 px-2"
                                                                style={{ fontSize: '11px' }}
                                                                onClick={() => handleToggleDriverShift(v)}
                                                            >
                                                                {v.current_driver_id ? t('btn_end_shift') : t('btn_start_shift')}
                                                            </Button>
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <Button variant="outline-primary" size="sm" className="py-0.5 px-2" onClick={() => handleOpenVehicleEdit(v)}>
                                                            <Edit3 size={12} className="me-1" /> {t('edit')}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    )}

                    {/* Sub-tab: Tipos de Vehículo */}
                    {fleetTab === 'vehicleTypes' && (
                        <Card className="shadow-sm border-0 bg-white p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h4 className="fw-bold mb-0 text-dark">{t('tariffs_custom_types_title')}</h4>
                                    <p className="text-muted small mb-0">{t('tariffs_custom_types_subtitle')}</p>
                                </div>
                                <Button variant="primary" size="sm" onClick={handleOpenVehicleTypeCreate} className="d-flex align-items-center gap-1.5">
                                    <Plus size={16} /> {t('btn_create_vehicle_type')}
                                </Button>
                            </div>
                            <Table hover responsive size="sm" className="align-middle bg-white">
                                <thead className="bg-light">
                                    <tr>
                                        <th>{t('veh_type_name_th')}</th>
                                        <th>{t('veh_type_max_pax_th')}</th>
                                        <th>{t('veh_type_max_suitcases_th')}</th>
                                        <th>{t('veh_type_booking_cat_th')}</th>
                                        <th>{t('veh_th_type')}</th>
                                        <th className="text-end">{t('th_actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicleTypes.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4 text-muted small">{t('no_vehicle_types_registered')}</td>
                                        </tr>
                                    ) : (
                                        vehicleTypes.map(vt => (
                                            <tr key={vt.id}>
                                                <td>
                                                    <span className="fw-bold">{vt.name}</span>
                                                </td>
                                                <td>
                                                    <Badge bg="info" className="px-2.5 py-1 text-white">👤 {vt.capacity} {t('pax_word')}</Badge>
                                                </td>
                                                <td>
                                                    <Badge bg="secondary" className="px-2.5 py-1 text-dark">💼 {vt.max_suitcases || 4} {t('suitcases_word')}</Badge>
                                                </td>
                                                <td>
                                                    <span className="font-monospace text-muted">{vt.booking_category || t('none_option')}</span>
                                                </td>
                                                <td>
                                                    {vt.provider_id ? (
                                                        <Badge bg="purple" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>{t('own_badge')}</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">{t('global_system_badge')}</Badge>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    {vt.provider_id ? (
                                                        <Button variant="outline-primary" size="sm" className="py-0.5 px-2" onClick={() => handleOpenVehicleTypeEdit(vt)}>
                                                            <Edit3 size={12} className="me-1" /> {t('edit')}
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted small">{t('blocked_badge')}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </div>
            )}

            {/* ─── TABS CONTENT: MI TARIFARIO ────────────────────────────────────── */}
            {activeTab === 'tariffs' && (
                <ErrorBoundary title="Error al cargar Mi Tarifario" onNavigateHome={() => setActiveTab('dispatch')}>
                    <div className="space-y-4">
                        {/* Sub-selector: Tarifas por Ruta vs Mis Reglas y Horarios */}
                        <div className="d-flex flex-wrap gap-2 bg-white p-2 rounded shadow-sm border">
                            <Button
                                size="sm"
                                variant={tariffsSubTab === 'routes' ? 'primary' : 'light'}
                                className={`fw-bold flex-grow-1 flex-sm-grow-0 px-3 py-2 ${tariffsSubTab !== 'routes' ? 'text-secondary border' : 'shadow-sm'}`}
                                onClick={() => setTariffsSubTab('routes')}
                            >
                                📋 {language === 'pt' ? 'Tarifas por Rota' : (language === 'en' ? 'Route Rates' : 'Tarifas por Ruta')}
                            </Button>
                            <Button
                                size="sm"
                                variant={tariffsSubTab === 'rules' ? 'primary' : 'light'}
                                className={`fw-bold flex-grow-1 flex-sm-grow-0 px-3 py-2 ${tariffsSubTab !== 'rules' ? 'text-secondary border' : 'shadow-sm'}`}
                                onClick={() => setTariffsSubTab('rules')}
                            >
                                ⚡ {language === 'pt' ? 'Minhas Regras e Horários (Pico / Folgas)' : (language === 'en' ? 'My Rules & Schedules (Peak / Days Off)' : 'Mis Reglas y Horarios (Punta / Días Libres)')}
                            </Button>
                        </div>

                        {tariffsSubTab === 'rules' ? (
                            <ProviderRulesManager
                                providerId={user?.linked_provider_id || providerDetails?.id || null}
                                tariffs={tariffs}
                                currencyCode={providerDetails?.currency_code || 'CLP'}
                            />
                        ) : (
                        <>
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                            <div>
                                <h3 className="fw-bold text-dark mb-0">{t('tariffs_title')}</h3>
                                <p className="text-muted small mb-0">{t('tariffs_subtitle')}</p>
                            </div>
                            <div className="d-flex align-items-center gap-2 w-100 w-md-auto">
                                <Button 
                                    variant="warning" 
                                    size="sm" 
                                    className="fw-bold text-dark d-flex align-items-center justify-content-center gap-1 flex-grow-1 flex-md-grow-0"
                                    onClick={() => setShowBulkUpdateModal(true)}
                                >
                                    {t('tariffs_bulk_btn')}
                                </Button>
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    style={{ backgroundColor: '#003580', borderColor: '#003580' }}
                                    className="fw-bold text-white d-flex align-items-center justify-content-center gap-1 flex-grow-1 flex-md-grow-0 shadow-sm"
                                    onClick={() => {
                                        const hasActiveFilter = Boolean(tariffOriginSearch || tariffDestSearch || tariffCapacity || tariffVehicleType);
                                        setBulkGeniusScope(hasActiveFilter && filteredTariffs.length > 0 ? 'filtered' : 'all');
                                        setShowGeniusBulkModal(true);
                                    }}
                                >
                                    {t('tariffs_genius_btn')}
                                </Button>
                                {modifiedTariffsCount > 0 && (
                                    <Button
                                        variant="success"
                                        size="sm"
                                        className="fw-bold text-white d-flex align-items-center justify-content-center gap-1 shadow-sm flex-grow-1 flex-md-grow-0 animate__animated animate__pulse animate__infinite"
                                        onClick={handleSaveAllTariffs}
                                        disabled={isSavingAllTariffs}
                                    >
                                        {isSavingAllTariffs ? (
                                            <>
                                                <Spinner size="sm" animation="border" className="me-1" />
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={15} /> {t('tariffs_save_all')} ({modifiedTariffsCount})
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Alerta flotante o visible si hay cambios sin guardar */}
                        {modifiedTariffsCount > 0 && (
                            <Alert variant="warning" className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center py-2 px-3 mb-0 shadow-sm border-warning">
                                <div className="d-flex align-items-center gap-2 mb-2 mb-sm-0">
                                    <span className="fs-5">⚠️</span>
                                    <div className="small">
                                        <strong>Tienes {modifiedTariffsCount} cambios sin guardar.</strong> Presiona <kbd>Enter</kbd> en el campo, usa el botón "Guardar" de cada ruta, o pulsa "Guardar Todos".
                                    </div>
                                </div>
                                <Button 
                                    variant="dark" 
                                    size="sm" 
                                    className="fw-bold px-3"
                                    onClick={handleSaveAllTariffs}
                                    disabled={isSavingAllTariffs}
                                >
                                    {isSavingAllTariffs ? 'Guardando...' : '💾 Guardar Todo Ahora'}
                                </Button>
                            </Alert>
                        )}

                        <Card className="shadow-sm border-0 bg-white p-3">
                            {/* Filtros de Tarifario */}
                            <div className="row g-2 mb-3 align-items-center">
                                <div className="col-12 col-sm-6 col-md-3">
                                    <Form.Control
                                        type="text"
                                        placeholder={t('tariffs_search_origin')}
                                        size="sm"
                                        value={tariffOriginSearch}
                                        onChange={e => setTariffOriginSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-12 col-sm-6 col-md-3">
                                    <Form.Control
                                        type="text"
                                        placeholder={t('tariffs_search_dest')}
                                        size="sm"
                                        value={tariffDestSearch}
                                        onChange={e => setTariffDestSearch(e.target.value)}
                                    />
                                </div>
                                <div className="col-6 col-md-2">
                                    <Form.Select
                                        size="sm"
                                        value={tariffCapacity}
                                        onChange={e => setTariffCapacity(e.target.value)}
                                    >
                                        <option value="">{t('tariffs_passengers')}</option>
                                        {tariffCapacityOptions.map(cap => (
                                            <option key={cap} value={cap}>{cap} Pasajeros</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="col-6 col-md-3">
                                    <Form.Select
                                        size="sm"
                                        value={tariffVehicleType}
                                        onChange={e => setTariffVehicleType(e.target.value)}
                                    >
                                        <option value="">{t('tariffs_vehicles_all')}</option>
                                        {tariffVehicleTypeOptions.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                {(tariffOriginSearch || tariffDestSearch || tariffCapacity || tariffVehicleType) && (
                                    <div className="col-12 col-md-1 d-flex">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm" 
                                            className="w-100"
                                            onClick={() => { 
                                                setTariffOriginSearch(''); 
                                                setTariffDestSearch(''); 
                                                setTariffCapacity(''); 
                                                setTariffVehicleType(''); 
                                            }}
                                        >
                                            {t('clear')}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* 📱 VISTA MÓVIL (TARJETAS d-md-none) */}
                            <div className="d-md-none space-y-3">
                                {paginatedTariffs.length === 0 ? (
                                    <div className="text-center py-5 text-muted small bg-light rounded">
                                        {t('no_tariffs_found')}
                                    </div>
                                ) : (
                                    paginatedTariffs.map(tariff => {
                                        if (!tariff) return null;
                                        const originBaseId = tariff.origin_zone?.base_id || tariff.origin_group?.base_id;
                                        const base = Array.isArray(bases) ? bases.find(b => b.id === originBaseId) : null;
                                        const currency = tariff.provider?.currency_code || providerDetails?.currency_code || base?.currency_code || 'USD';
                                        const isModified = isTariffModified(tariff);
                                        const isSaving = savingTariffId === tariff.id;

                                        return (
                                            <div 
                                                key={tariff.id} 
                                                className={`p-3 rounded border transition-all ${isModified ? 'border-warning bg-warning bg-opacity-10' : 'bg-light border-gray-200'}`}
                                                style={{ boxShadow: isModified ? '0 0 0 1px #ffc107' : 'none' }}
                                            >
                                                {/* Encabezado Ruta y Moneda */}
                                                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                                    <div className="font-bold text-dark text-sm">
                                                        <span>{tariff.origin_zone?.name || tariff.origin_group?.name || 'Origen'}</span>
                                                        <span className="mx-1 text-muted">➔</span>
                                                        <span>{tariff.destination_zone?.name || tariff.destination_group?.name || 'Destino'}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                                        <Badge bg="light" className="border uppercase fw-bold" style={{ color: '#4f46e5', fontSize: '11px' }}>
                                                            {currency}
                                                        </Badge>
                                                        {isModified && (
                                                            <Badge bg="warning" text="dark" style={{ fontSize: '10px' }}>
                                                                {translate('modified_badge')}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Badges de Tipo y Pasajeros */}
                                                <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                                                    <Badge bg="secondary" className="fw-normal">
                                                        🚗 {tariff.vehicle_type?.name || 'Standard'}
                                                    </Badge>
                                                    <Badge bg="light" className="text-dark border font-normal">
                                                        👤 {tariff.max_pax !== null && tariff.max_pax !== undefined ? tariff.max_pax : (tariff.vehicle_type?.capacity || '4')} Pax
                                                    </Badge>
                                                    {tariff.client_ids && tariff.client_ids !== 'ALL' && (() => {
                                                        const cIds = String(tariff.client_ids).split(',').map(s => s.trim()).filter(Boolean);
                                                        if (cIds.length === 0) return null;
                                                        const names = cIds.map(id => (Array.isArray(clients) ? clients.find(c => String(c.id) === id)?.name : null) || `Convenio #${id}`);
                                                        return (
                                                            <Badge bg="info" text="dark" className="fw-normal" title={names.join(', ')}>
                                                                🎯 {names.length === 1 ? names[0] : `${names.length} Clientes`}
                                                            </Badge>
                                                        );
                                                    })()}
                                                    {tariff.last_cost_change_at && (
                                                        <span className="text-muted small ms-auto" style={{ fontSize: '11px' }}>
                                                            🕒 {formatTariffShortDate(tariff.last_cost_change_at)}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Campos de Costo y Descuento */}
                                                <div className="row g-2 align-items-center mb-3">
                                                    <div className="col-12 col-sm-5">
                                                        <label className="form-label text-muted small mb-1 fw-bold">Costo Proveedor ({currency})</label>
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text bg-white fw-bold text-muted">{currency === 'USD' ? 'u$s' : '$'}</span>
                                                            <Form.Control 
                                                                type="number" 
                                                                step="0.01"
                                                                value={tariffCosts[tariff.id] !== undefined ? tariffCosts[tariff.id] : (tariff.cost_local || '')}
                                                                onChange={e => handleTariffCostChange(tariff.id, e.target.value)}
                                                                onKeyDown={e => { if (e.key === 'Enter') handleSaveTariffCost(tariff.id); }}
                                                                className={`font-bold text-dark ${isModified ? 'border-warning' : ''}`}
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-6 col-sm-3">
                                                        <label className="form-label text-muted small mb-1 fw-bold">% Puja Desc</label>
                                                        <div className="input-group input-group-sm">
                                                            <Form.Control 
                                                                type="number" 
                                                                min="0" 
                                                                max="50"
                                                                step="1"
                                                                value={tariffDiscounts[tariff.id] !== undefined ? tariffDiscounts[tariff.id] : (tariff.booking_bid_discount || 0)}
                                                                onChange={e => handleTariffDiscountChange(tariff.id, e.target.value)}
                                                                onKeyDown={e => { if (e.key === 'Enter') handleSaveTariffCost(tariff.id); }}
                                                                className={`text-center font-bold text-dark ${isModified ? 'border-warning' : ''}`}
                                                            />
                                                            <span className="input-group-text bg-white fw-bold text-muted">%</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-6 col-sm-4">
                                                        <label className="form-label text-primary small mb-1 fw-bold">⭐ % Genius</label>
                                                        <Form.Select 
                                                            size="sm" 
                                                            value={tariffGeniusDiscounts[tariff.id] !== undefined ? tariffGeniusDiscounts[tariff.id] : (tariff.booking_genius_discount || 0)}
                                                            onChange={e => handleTariffGeniusChange(tariff.id, parseFloat(e.target.value))}
                                                            className={`text-center font-bold ${isModified ? 'border-warning' : ''}`}
                                                            style={{ color: (tariffGeniusDiscounts[tariff.id] || tariff.booking_genius_discount) >= 5 ? '#003580' : '#495057' }}
                                                        >
                                                            <option value={0}>0% ({translate('inactive_male')})</option>
                                                            <option value={5}>5% (Genius)</option>
                                                            <option value={6}>6%</option>
                                                            <option value={7}>7%</option>
                                                            <option value={8}>8%</option>
                                                            <option value={9}>9%</option>
                                                            <option value={10}>10%</option>
                                                        </Form.Select>
                                                    </div>
                                                </div>

                                                {/* Botón Guardar individual */}
                                                <Button
                                                    variant={isModified ? "primary" : "outline-primary"}
                                                    size="sm"
                                                    className="w-100 fw-bold py-1.5 d-flex align-items-center justify-content-center gap-1 shadow-sm"
                                                    onClick={() => handleSaveTariffCost(tariff.id)}
                                                    disabled={isSaving || isSavingAllTariffs}
                                                >
                                                    {isSaving ? (
                                                        <>
                                                            <Spinner size="sm" animation="border" style={{ width: '13px', height: '13px' }} />
                                                            <span>{translate('tariffs_saving')}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Check size={14} /> {isModified ? translate('save_changes') : translate('refresh')}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* 💻 VISTA ESCRITORIO (TABLA d-none d-md-block) */}
                            <div className="d-none d-md-block">
                                <Table hover responsive size="sm" className="align-middle bg-white mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>{t('route_orig_dest')}</th>
                                            <th>{t('veh_th_type')}</th>
                                            <th>Pax Máx</th>
                                            <th>{t('th_currency')}</th>
                                            <th>{t('th_provider_cost')}</th>
                                            <th>{t('th_bid_discount')}</th>
                                            <th>⭐ {t('th_genius_discount')}</th>
                                            <th className="text-end">{t('th_action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedTariffs.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4 text-muted small">{t('no_tariffs_found')}</td>
                                            </tr>
                                        ) : (
                                            paginatedTariffs.map(tariff => {
                                                if (!tariff) return null;
                                                const originBaseId = tariff.origin_zone?.base_id || tariff.origin_group?.base_id;
                                                const base = Array.isArray(bases) ? bases.find(b => b.id === originBaseId) : null;
                                                const currency = tariff.provider?.currency_code || providerDetails?.currency_code || base?.currency_code || 'USD';
                                                const isModified = isTariffModified(tariff);
                                                const isSaving = savingTariffId === tariff.id;

                                                return (
                                                    <tr key={tariff.id} className={isModified ? 'table-warning bg-opacity-25' : ''}>
                                                        <td>
                                                            <div className="d-flex align-items-center flex-wrap gap-2 text-dark font-medium">
                                                                <span>{tariff.origin_zone?.name || tariff.origin_group?.name || 'Origen'}</span>
                                                                <ArrowRight size={14} className="text-muted" />
                                                                <span>{tariff.destination_zone?.name || tariff.destination_group?.name || 'Destino'}</span>
                                                                {tariff.client_ids && tariff.client_ids !== 'ALL' && (() => {
                                                                    const cIds = String(tariff.client_ids).split(',').map(s => s.trim()).filter(Boolean);
                                                                    if (cIds.length === 0) return null;
                                                                    const names = cIds.map(id => (Array.isArray(clients) ? clients.find(c => String(c.id) === id)?.name : null) || `Convenio #${id}`);
                                                                    return (
                                                                        <Badge bg="info" text="dark" className="fw-normal" style={{ fontSize: '10px' }} title={names.join(', ')}>
                                                                            🎯 {names.length === 1 ? names[0] : `${names.length} Clientes`}
                                                                        </Badge>
                                                                    );
                                                                })()}
                                                                {isModified && (
                                                                    <Badge bg="warning" text="dark" style={{ fontSize: '9px' }}>
                                                                        Modificado
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <strong>{tariff.vehicle_type?.name || 'Standard'}</strong>
                                                        </td>
                                                        <td>
                                                            <Badge bg="light" className="text-dark border font-normal">
                                                                👤 {tariff.max_pax !== null && tariff.max_pax !== undefined ? tariff.max_pax : (tariff.vehicle_type?.capacity || '4')} Pax
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge bg="light" className="border uppercase fw-bold" style={{ color: '#4f46e5' }}>
                                                                {currency}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1.5" style={{ width: '160px' }}>
                                                                <span className="text-muted fw-bold">{currency === 'USD' ? 'u$s' : '$'}</span>
                                                                <Form.Control 
                                                                    type="number" 
                                                                    step="0.01"
                                                                    size="sm"
                                                                    value={tariffCosts[tariff.id] !== undefined ? tariffCosts[tariff.id] : (tariff.cost_local || '')}
                                                                    onChange={e => handleTariffCostChange(tariff.id, e.target.value)}
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveTariffCost(tariff.id); }}
                                                                    className={`font-bold text-dark ${isModified ? 'border-warning fw-bolder' : 'border-secondary'}`}
                                                                />
                                                                {tariff.last_cost_change_at && (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={
                                                                            <Tooltip id={`change-tooltip-provider-${tariff.id}`}>
                                                                                <strong>Último Cambio</strong><br/>
                                                                                Por: {tariff.last_cost_change_user || '—'}<br/>
                                                                                Fecha: {formatTariffDate(tariff.last_cost_change_at)}<br/>
                                                                                Valor: {formatTariffNumber(tariff.last_cost_change_prev)} ➔ {formatTariffNumber(tariff.last_cost_change_new)} {currency}
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span className="cursor-help" style={{ color: '#0d6efd', fontSize: '11px' }}>🕒</span>
                                                                    </OverlayTrigger>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1" style={{ width: '90px' }}>
                                                                <Form.Control 
                                                                    type="number" 
                                                                    min="0" 
                                                                    max="50"
                                                                    step="1"
                                                                    size="sm"
                                                                    value={tariffDiscounts[tariff.id] !== undefined ? tariffDiscounts[tariff.id] : (tariff.booking_bid_discount || 0)}
                                                                    onChange={e => handleTariffDiscountChange(tariff.id, e.target.value)}
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleSaveTariffCost(tariff.id); }}
                                                                    className={`text-center font-bold text-dark ${isModified ? 'border-warning' : 'border-secondary'}`}
                                                                />
                                                                <span className="text-muted fw-bold">%</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ width: '110px' }}>
                                                                <Form.Select 
                                                                    size="sm" 
                                                                    value={tariffGeniusDiscounts[tariff.id] !== undefined ? tariffGeniusDiscounts[tariff.id] : (tariff.booking_genius_discount || 0)}
                                                                    onChange={e => handleTariffGeniusChange(tariff.id, parseFloat(e.target.value))}
                                                                    className={`font-bold ${isModified ? 'border-warning' : 'border-secondary'}`}
                                                                    style={{ fontSize: '11px', color: (tariffGeniusDiscounts[tariff.id] || tariff.booking_genius_discount) >= 5 ? '#003580' : '#495057' }}
                                                                >
                                                                    <option value={0}>0% ({translate('inactive_male')})</option>
                                                                    <option value={5}>5% (Genius)</option>
                                                                    <option value={6}>6%</option>
                                                                    <option value={7}>7%</option>
                                                                    <option value={8}>8%</option>
                                                                    <option value={9}>9%</option>
                                                                    <option value={10}>10%</option>
                                                                </Form.Select>
                                                            </div>
                                                        </td>
                                                        <td className="text-end">
                                                            <Button
                                                                variant={isModified ? "primary" : "outline-primary"}
                                                                size="sm"
                                                                onClick={() => handleSaveTariffCost(tariff.id)}
                                                                disabled={isSaving || isSavingAllTariffs}
                                                                className="shadow-sm"
                                                            >
                                                                {isSaving ? (
                                                                    <Spinner size="sm" animation="border" style={{ width: '12px', height: '12px' }} />
                                                                ) : (
                                                                    <>
                                                                        <Check size={12} className="me-1" /> {translate('save')}
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Paginación de Tarifas */}
                            {filteredTariffs.length > tariffPageSize && (
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 mt-3 pt-3 border-top">
                                    <div className="text-muted small">
                                        Mostrando {(tariffPage - 1) * tariffPageSize + 1} a {Math.min(tariffPage * tariffPageSize, filteredTariffs.length)} de {filteredTariffs.length} tarifas
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm" 
                                            disabled={tariffPage <= 1}
                                            onClick={() => setTariffPage(p => Math.max(1, p - 1))}
                                        >
                                            ◀ Anterior
                                        </Button>
                                        <span className="px-2 small fw-bold">
                                            Página {tariffPage} de {Math.max(1, Math.ceil(filteredTariffs.length / tariffPageSize))}
                                        </span>
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm" 
                                            disabled={tariffPage >= Math.ceil(filteredTariffs.length / tariffPageSize)}
                                            onClick={() => setTariffPage(p => Math.min(Math.ceil(filteredTariffs.length / tariffPageSize), p + 1))}
                                        >
                                            Siguiente ▶
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                        </>
                        )}
                    </div>
                </ErrorBoundary>
            )}

            {/* ─── TABS CONTENT: BOT CENTINELA BOOKING.COM ─────────────────────────── */}
            {activeTab === 'sentinel' && (
                <ErrorBoundary title="Error al cargar Bot Centinela" onNavigateHome={() => setActiveTab('dispatch')}>
                    <BookingSentinelView
                        providerId={user?.linked_provider_id || providerDetails?.id || null}
                        providerName={providerDetails?.name || user?.name || 'Proveedor'}
                        providerTariffs={tariffs}
                        onTariffUpdated={async () => {
                            try {
                                const td = await getTariffs({ limit: 5000 });
                                setTariffs(td || []);
                            } catch (e) {
                                console.error('Error refrescando tarifas tras cambio en Centinela:', e);
                            }
                        }}
                    />
                </ErrorBoundary>
            )}

            {/* ─── TABS CONTENT: PUNTOS DE ENCUENTRO BOOKING.COM ───────────────────── */}
            {activeTab === 'meeting_points' && (
                <ErrorBoundary title="Error al cargar Puntos de Encuentro" onNavigateHome={() => setActiveTab('dispatch')}>
                    <BookingMeetingPointsManager
                        providerId={user?.linked_provider_id || providerDetails?.id || null}
                        isAdmin={false}
                        providerTariffs={tariffs}
                        bases={bases}
                        providerDetails={providerDetails}
                    />
                </ErrorBoundary>
            )}

            {/* ─── TABS CONTENT: LIQUIDACIONES DE CONDUCTORES ──────────────────────── */}
            {activeTab === 'settlements' && (
                <ErrorBoundary title="Error al cargar Liquidaciones" onNavigateHome={() => setActiveTab('dispatch')}>
                    <ProviderSettlementsView
                        providerId={user?.linked_provider_id || providerDetails?.id || null}
                        drivers={drivers}
                        providerDetails={providerDetails}
                    />
                </ErrorBoundary>
            )}

            {/* ─── TABS CONTENT: CALIDAD & ENCUESTAS ────────────────────────────────── */}
            {activeTab === 'quality' && (
                <div className="space-y-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                            <h3 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                                <Award className="text-warning" size={24} /> ⭐ {t('quality_title')}
                            </h3>
                            <p className="text-muted small mb-0">
                                {t('quality_subtitle')}
                            </p>
                        </div>
                        <Button variant="outline-primary" size="sm" onClick={fetchQuality} disabled={qualityLoading} className="fw-bold">
                            {qualityLoading ? <Spinner size="sm" animation="border" /> : <>🔄 {t('refresh')}</>}
                        </Button>
                    </div>

                    {/* Filtros de Calidad */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-3 bg-light rounded">
                            <Row className="align-items-end g-3">
                                <Col xl={3} lg={3} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase mb-1">{t('filter_from')}</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        size="sm" 
                                        value={qualityStartDate} 
                                        onChange={(e) => setQualityStartDate(e.target.value)} 
                                    />
                                </Col>
                                <Col xl={3} lg={3} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase mb-1">{t('filter_to')}</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        size="sm" 
                                        value={qualityEndDate} 
                                        onChange={(e) => setQualityEndDate(e.target.value)} 
                                    />
                                </Col>
                                <Col xl={3} lg={3} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase mb-1">{t('driver_word')}</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={qualityDriverFilter}
                                        onChange={(e) => setQualityDriverFilter(e.target.value)}
                                    >
                                        <option value="">👥 {t('all_drivers_option')}</option>
                                        {drivers.map(d => (
                                            <option key={d.id} value={d.id}>{d.full_name || d.name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xl={3} lg={3} md={4} sm={6}>
                                    <Button variant="primary" size="sm" className="w-100 fw-bold" onClick={fetchQuality} disabled={qualityLoading}>
                                        <Filter size={14} className="me-1" /> {t('filter_results_btn')}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {qualityLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="warning" />
                            <p className="text-muted mt-2 fw-bold">Cargando evaluaciones de calidad...</p>
                        </div>
                    ) : !qualityData ? (
                        <div className="text-center py-5 text-muted">
                            <p>No se han cargado datos de calidad.</p>
                        </div>
                    ) : (
                        <>
                            {/* TARJETAS KPI DE CALIDAD PROVEEDOR */}
                            <Row className="g-3 mb-4">
                                <Col md={3} sm={6}>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-warning border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">{t('kpi_overall_avg')}</div>
                                        <h3 className={`fw-bold mb-0 ${
                                            qualityData.kpis?.average_score >= 4.6 ? 'text-success' : 
                                            qualityData.kpis?.average_score >= 4.0 ? 'text-warning' : 'text-danger'
                                        }`}>
                                            ⭐ {qualityData.kpis?.average_score ? qualityData.kpis.average_score.toFixed(2) : '0.00'}
                                        </h3>
                                        <small className="text-muted">{t('kpi_official_goal')}</small>
                                    </Card>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-success border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">{t('kpi_sla_compliance')}</div>
                                        <h3 className="fw-bold mb-0 text-success">{qualityData.kpis?.sla_percentage || 100}%</h3>
                                        <small className="text-muted">{t('kpi_scores_ge')}</small>
                                    </Card>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-info border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">{t('kpi_total_evaluations')}</div>
                                        <h3 className="fw-bold mb-0 text-info">{qualityData.kpis?.total_evaluations || 0}</h3>
                                        <small className="text-muted">{t('kpi_surveys_period')}</small>
                                    </Card>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Card 
                                        className={`border-0 shadow-sm text-center p-3 h-100 border-start border-danger border-4 ${
                                            qualityCriticalOnly ? 'bg-danger bg-opacity-10 shadow' : ''
                                        }`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setQualityCriticalOnly(!qualityCriticalOnly)}
                                    >
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">{t('kpi_evals_to_improve')}</div>
                                        <h3 className={`fw-bold mb-0 ${
                                            (qualityData.surveys || []).filter(s => s.score < 4.6).length > 0 ? 'text-danger' : 'text-muted'
                                        }`}>
                                            {(qualityData.surveys || []).filter(s => s.score < 4.6).length}
                                        </h3>
                                        <small className="text-muted">
                                            {qualityCriticalOnly ? t('kpi_seeing_lt') : t('kpi_click_to_see_lt')}
                                        </small>
                                    </Card>
                                </Col>
                            </Row>

                            {/* TABLA DE EVALUACIONES */}
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h5 className="fw-bold mb-0">📋 {t('surveys_section_title')}</h5>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('search_surveys_ph')}
                                        size="sm"
                                        style={{ maxWidth: '280px' }}
                                        value={qualitySearch}
                                        onChange={(e) => setQualitySearch(e.target.value)}
                                    />
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table responsive hover className="align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-3">{t('th_date')}</th>
                                                <th>{t('th_ref_booking')}</th>
                                                <th>{t('driver_word')}</th>
                                                <th>{t('th_zone_base')}</th>
                                                <th className="text-center">{t('th_passenger_score')}</th>
                                                <th>{t('th_passenger_comment')}</th>
                                                <th className="text-end pe-3">{t('th_detail')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                let list = qualityData.surveys || [];
                                                if (qualityCriticalOnly) {
                                                    list = list.filter(s => s.score < 4.6);
                                                }
                                                if (qualitySearch.trim()) {
                                                    const q = qualitySearch.toLowerCase();
                                                    list = list.filter(s => 
                                                        (s.external_reference || '').toLowerCase().includes(q) ||
                                                        (s.driver_name || '').toLowerCase().includes(q) ||
                                                        (s.comments || '').toLowerCase().includes(q) ||
                                                        (s.zone_name || '').toLowerCase().includes(q)
                                                    );
                                                }

                                                if (list.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-5 text-muted">
                                                                No se encontraron encuestas con los filtros seleccionados.
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return list.map(survey => (
                                                    <tr key={survey.id} className={survey.score < 4.0 ? 'table-danger' : survey.score < 4.6 ? 'table-warning' : ''}>
                                                        <td className="ps-3 small fw-bold">
                                                            {survey.scheduled_time ? format(new Date(survey.scheduled_time), 'dd/MM/yyyy HH:mm') : '-'}
                                                        </td>
                                                        <td>
                                                            <Badge bg="light" text="dark" className="border fw-bold">
                                                                {survey.external_reference || `#${survey.id}`}
                                                            </Badge>
                                                        </td>
                                                        <td className="fw-semibold text-dark">
                                                            {survey.driver_name || 'Sin Asignar'}
                                                        </td>
                                                        <td>
                                                            <span className="small text-muted">{survey.zone_name || '-'}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge 
                                                                bg={survey.score >= 4.6 ? 'success' : survey.score >= 4.0 ? 'warning' : 'danger'}
                                                                className="px-2 py-1 shadow-sm"
                                                                style={{ fontSize: '13px' }}
                                                            >
                                                                ⭐ {survey.score.toFixed(1)} / 5.0
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <span className="small text-dark italic">
                                                                {survey.comments ? `"${survey.comments}"` : <span className="text-muted italic">Sin comentario escrito</span>}
                                                            </span>
                                                        </td>
                                                        <td className="text-end pe-3">
                                                            <Button 
                                                                variant="outline-secondary" 
                                                                size="sm" 
                                                                className="py-0 px-2 text-nowrap"
                                                                onClick={() => {
                                                                    const foundTrip = trips.find(tr => tr.id === survey.id);
                                                                    if (foundTrip) {
                                                                        setAuditTrip(foundTrip);
                                                                        setShowAuditModal(true);
                                                                    } else {
                                                                        toast.info(`Reserva #${survey.external_reference || survey.id}`);
                                                                    }
                                                                }}
                                                                title="Auditar viaje"
                                                            >
                                                                🔍 Auditar
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>
            )}

            {/* ─── MODAL: AUTOASIGNAR / EDITAR VIAJE ─────────────────────────────────── */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">{t('assign_modal_title')}</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleAssignSubmit}>
                    <Modal.Body className="p-4 space-y-3">
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t("assign_modal_vehicle")}</Form.Label>
                            <Form.Select
                                required
                                value={assignForm.vehicle_id}
                                onChange={e => handleVehicleChange(e.target.value)}
                            >
                                <option value="">{t("assign_modal_select_vehicle")}</option>
                                {vehicles.filter(v => v.is_active).map(v => {
                                    const assocDriver = drivers.find(d => d.id === v.current_driver_id);
                                    return (
                                        <option key={v.id} value={v.id}>
                                            {v.internal_code} - {v.plate} ({assocDriver ? assocDriver.full_name : 'SIN CHOFER'})
                                        </option>
                                    );
                                })}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mt-2 d-block">{t("assign_modal_assigned_driver")}</Form.Label>
                            <div className="border rounded px-3 py-2 bg-light text-dark small fw-semibold">
                                {(() => {
                                    const assocDriver = drivers.find(d => d.id === parseInt(assignForm.driver_id));
                                    return assocDriver 
                                        ? `${assocDriver.full_name} (${assocDriver.has_active_session ? 'En Turno' : 'Apagado'})`
                                        : <span className="text-danger">{t('assign_modal_no_driver_warning')}</span>;
                                })()}
                            </div>
                            {!assignForm.driver_id && assignForm.vehicle_id && (
                                <Form.Text className="text-danger fw-bold d-block mt-1">
                                    ⚠️ El móvil seleccionado no tiene conductor asociado. Vincúlelo en "Mi Flota" antes de asignar.
                                </Form.Text>
                            )}
                        </Form.Group>

                        {assignConflictWarning && assignConflictWarning.length > 0 && (
                            <Alert variant="warning" className="mt-3 mb-2 py-2 px-3 border-warning shadow-sm small">
                                <div className="fw-bold text-dark mb-1">
                                    ⚠️ Atención: Reserva muy cercana en este conductor/móvil
                                </div>
                                {assignConflictWarning.map((c, idx) => (
                                    <div key={idx} className="mt-1 text-dark">
                                        • <strong>#{c.trip_id}</strong> ({c.scheduled_time} — {c.passenger_name || 'Pasajero'}): {c.conflict_reason}
                                    </div>
                                ))}
                            </Alert>
                        )}

                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mt-2">{t("assign_modal_notes")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={assignForm.notes || ''}
                                onChange={e => setAssignForm(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Observaciones generales..."
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase mt-2">{t("assign_modal_driver_notes")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={assignForm.driver_notes || ''}
                                onChange={e => setAssignForm(prev => ({ ...prev, driver_notes: e.target.value }))}
                                placeholder="Notas o instrucciones para el chofer..."
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" onClick={() => setShowAssignModal(false)}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit" disabled={!assignForm.driver_id}>{t("assign_modal_confirm")}</Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* ─── MODAL: CRUD CONDUCTOR ──────────────────────────────────────────── */}
            <Modal show={showDriverModal} onHide={() => setShowDriverModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">
                        {editingDriver ? t('edit_driver_title') : t('new_driver_title')}
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleDriverSubmit}>
                    <Modal.Body className="p-4 space-y-3">
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t('full_name_lbl')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required
                                value={driverForm.full_name}
                                onChange={e => setDriverForm({ ...driverForm, full_name: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t('rut_id_lbl')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required
                                value={driverForm.rut}
                                onChange={e => setDriverForm({ ...driverForm, rut: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t('phone_lbl')}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required
                                value={driverForm.phone}
                                onChange={e => setDriverForm({ ...driverForm, phone: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t('email_user_lbl')}</Form.Label>
                            <Form.Control 
                                type="email" 
                                required
                                value={driverForm.email}
                                onChange={e => setDriverForm({ ...driverForm, email: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t('password_lbl')}</Form.Label>
                            <Form.Control 
                                type="password" 
                                placeholder={editingDriver ? t('password_keep_ph') : t('password_min_ph')}
                                value={driverForm.password || ''}
                                onChange={e => setDriverForm({ ...driverForm, password: e.target.value })}
                            />
                        </Form.Group>
                        
                        <Form.Check 
                            type="checkbox" 
                            id="driver-is-active"
                            label={t('active_driver_lbl')}
                            className="fw-semibold mt-2"
                            checked={driverForm.is_active}
                            onChange={e => setDriverForm({ ...driverForm, is_active: e.target.checked })}
                        />
                        <Form.Check 
                            type="checkbox" 
                            id="driver-show-pricing"
                            label={t('show_pricing_lbl')}
                            className="fw-semibold mt-1"
                            checked={driverForm.show_pricing}
                            onChange={e => setDriverForm({ ...driverForm, show_pricing: e.target.checked })}
                        />
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" onClick={() => setShowDriverModal(false)}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit">{t('save_driver_btn')}</Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* ─── MODAL: CRUD VEHÍCULO ───────────────────────────────────────────── */}
            <Modal show={showVehicleModal} onHide={() => setShowVehicleModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">
                        {editingVehicle ? t('edit_vehicle_title') : t('new_vehicle_title')}
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleVehicleSubmit}>
                    <Modal.Body className="p-4 space-y-3">
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t("internal_code_lbl")}</Form.Label>
                                    {providerDetails && providerDetails.mobile_range_start !== null && providerDetails.mobile_range_end !== null && (
                                        <div className="text-muted small mb-1" style={{ fontSize: '11px' }}>
                                            {t('allowed_range')}: <strong>{providerDetails.mobile_range_start} - {providerDetails.mobile_range_end}</strong>
                                        </div>
                                    )}
                                    <Form.Control 
                                        type="text" 
                                        required
                                        placeholder="Ej: 102"
                                        value={vehicleForm.internal_code}
                                        onChange={e => setVehicleForm({ ...vehicleForm, internal_code: e.target.value })}
                                        isInvalid={!vehicleCodeValidation.isValid}
                                    />
                                    {!vehicleCodeValidation.isValid && (
                                        <div className="text-danger small mt-1" style={{ fontSize: '11px' }}>
                                            <div>{vehicleCodeValidation.message}</div>
                                            {vehicleCodeValidation.suggestions.length > 0 && (
                                                <div className="mt-0.5 text-muted">
                                                    {t('suggested_available')}: <strong>{vehicleCodeValidation.suggestions.join(', ')}</strong>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t("plate_lbl")}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        required
                                        placeholder="Ej: AB123CD"
                                        value={vehicleForm.plate}
                                        onChange={e => setVehicleForm({ ...vehicleForm, plate: e.target.value.toUpperCase() })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={8}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t("brand_lbl")}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        required
                                        value={vehicleForm.make}
                                        onChange={e => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t("year_lbl")}</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        required
                                        value={vehicleForm.year}
                                        onChange={e => setVehicleForm({ ...vehicleForm, year: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t("model_lbl")}</Form.Label>
                            <Form.Control 
                                type="text" 
                                required
                                value={vehicleForm.model}
                                onChange={e => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t("color_lbl")}</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        value={vehicleForm.color}
                                        onChange={e => setVehicleForm({ ...vehicleForm, color: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold text-muted small text-uppercase">{t("category_lbl")}</Form.Label>
                                    <Form.Select
                                        required
                                        value={vehicleForm.vehicle_type_id}
                                        onChange={e => setVehicleForm({ ...vehicleForm, vehicle_type_id: e.target.value })}
                                    >
                                        <option value="">- Tipo -</option>
                                        {vehicleTypes.map(vt => (
                                            <option key={vt.id} value={vt.id}>{vt.name} (Cap: {vt.capacity})</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">{t("assigned_driver_lbl")}</Form.Label>
                            <Form.Select
                                value={vehicleForm.current_driver_id}
                                onChange={e => setVehicleForm({ ...vehicleForm, current_driver_id: e.target.value })}
                            >
                                <option value="">- Sin Conductor -</option>
                                {drivers.map(driver => (
                                    <option key={driver.id} value={driver.id}>
                                        {driver.full_name} ({driver.rut})
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Check 
                            type="checkbox" 
                            id="vehicle-is-active"
                            label={t("active_vehicle_lbl")}
                            className="fw-semibold mt-2"
                            checked={vehicleForm.is_active}
                            onChange={e => setVehicleForm({ ...vehicleForm, is_active: e.target.checked })}
                        />
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" onClick={() => setShowVehicleModal(false)}>{t('cancel')}</Button>
                        <Button variant="primary" type="submit">{t("save_vehicle_btn")}</Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* ─── MODAL: CRUD TIPO DE VEHÍCULO ────────────────────────────────────── */}
            <Modal show={showVehicleTypeModal} onHide={() => setShowVehicleTypeModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">
                        {editingVehicleType ? '⚙️ Editar Tipo de Vehículo' : '⚙️ Nuevo Tipo de Vehículo'}
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleVehicleTypeSubmit}>
                    <Modal.Body className="p-4 space-y-3">
                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">Nombre del Tipo de Vehículo *</Form.Label>
                            <Form.Control 
                                type="text" 
                                required
                                placeholder="Ej: Minibús 12 pax"
                                value={vehicleTypeForm.name}
                                onChange={e => setVehicleTypeForm({ ...vehicleTypeForm, name: e.target.value })}
                            />
                            <Form.Text className="text-muted">
                                Escribe un nombre descriptivo para identificar esta capacidad (ej: Van Grande, Minibús Premium).
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Capacidad Máxima (Pasajeros) *</Form.Label>
                            <Form.Control 
                                type="number" 
                                required
                                min="1"
                                max="100"
                                value={vehicleTypeForm.capacity}
                                onChange={e => handleCapacityChange(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Define la cantidad máxima de pasajeros permitida para esta categoría.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Capacidad Máxima (Maletas) *</Form.Label>
                            <Form.Control 
                                type="number" 
                                required
                                min="0"
                                max="100"
                                value={vehicleTypeForm.max_suitcases}
                                onChange={e => setVehicleTypeForm({ ...vehicleTypeForm, max_suitcases: e.target.value })}
                            />
                            <Form.Text className="text-muted">
                                Define la cantidad máxima de maletas estándar permitida para esta categoría.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex gap-4 mt-3 p-3 bg-light rounded border">
                            <Form.Check 
                                type="switch"
                                id="vehicle-type-is-luxury"
                                label={<strong>Categoría LUXURY</strong>}
                                checked={isLuxury}
                                onChange={e => handleLuxuryChange(e.target.checked)}
                            />
                            <Form.Check 
                                type="switch"
                                id="vehicle-type-is-executive"
                                label={<strong>Categoría EXECUTIVE</strong>}
                                checked={isExecutive}
                                onChange={e => handleExecutiveChange(e.target.checked)}
                            />
                        </div>

                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Categoría Homóloga en Booking.com (Automática)</Form.Label>
                            <Form.Control
                                type="text"
                                disabled
                                className="font-monospace fw-bold text-primary bg-light"
                                value={vehicleTypeForm.booking_category}
                            />
                            <Form.Text className="text-muted">
                                Se calcula automáticamente según la cantidad de pasajeros y los switches marcados arriba.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" onClick={() => setShowVehicleTypeModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">Guardar Tipo de Vehículo</Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* ─── MODAL: ACTUALIZACIÓN MASIVA DE TARIFAS ───────────────────────────── */}
            <Modal show={showBulkUpdateModal} onHide={() => setShowBulkUpdateModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold text-dark">{t('tariffs_bulk_btn')} de Tarifas</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleBulkUpdateSubmit}>
                    <Modal.Body className="p-4 space-y-3">
                        <div className="alert alert-info py-2 px-3 small mb-3">
                            Esta herramienta te permite copiar las tarifas de un tipo de vehículo existente hacia otro, aplicando un ajuste porcentual o un monto fijo. Ideal para configurar rápidamente tarifas de una nueva capacidad basada en una existente.
                        </div>

                        <Form.Group>
                            <Form.Label className="fw-bold text-muted small text-uppercase">Vehículo Origen (Base de Tarifas) *</Form.Label>
                            <Form.Select
                                required
                                value={bulkUpdateForm.source_vehicle_type_id}
                                onChange={e => setBulkUpdateForm({ ...bulkUpdateForm, source_vehicle_type_id: e.target.value })}
                            >
                                <option value="">- Seleccione origen -</option>
                                {vehicleTypes.map(vt => (
                                    <option key={vt.id} value={vt.id}>
                                        {vt.name} (Cap: {vt.capacity} pax) {vt.provider_id ? '[Propio]' : '[Sistema]'}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Las tarifas (rutas) configuradas para este vehículo se usarán como plantilla.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Vehículo Destino (A Actualizar/Crear) *</Form.Label>
                            <Form.Select
                                required
                                value={bulkUpdateForm.target_vehicle_type_id}
                                onChange={e => setBulkUpdateForm({ ...bulkUpdateForm, target_vehicle_type_id: e.target.value })}
                            >
                                <option value="">- Seleccione destino -</option>
                                {vehicleTypes.filter(vt => vt.provider_id !== null).map(vt => (
                                    <option key={vt.id} value={vt.id}>
                                        {vt.name} (Cap: {vt.capacity} pax)
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Solo puedes seleccionar tus propios tipos de vehículo autogestionados.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Tipo de Ajuste *</Form.Label>
                            <Form.Select
                                value={bulkUpdateForm.change_type}
                                onChange={e => setBulkUpdateForm({ ...bulkUpdateForm, change_type: e.target.value })}
                            >
                                <option value="percentage">Porcentual (%)</option>
                                <option value="fixed">Monto Fijo (Suma o resta directa)</option>
                            </Form.Select>
                        </Form.Group>

                        {bulkUpdateForm.change_type === 'percentage' ? (
                            <Form.Group className="mt-3">
                                <Form.Label className="fw-bold text-muted small text-uppercase">Ajuste Porcentual (%) *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.1"
                                    required
                                    placeholder="Ej: 5 (para +5%) o -2.5 (para -2.5%)"
                                    value={bulkUpdateForm.percentage_change}
                                    onChange={e => setBulkUpdateForm({ ...bulkUpdateForm, percentage_change: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Ingresa un porcentaje positivo para aumentar el costo, o negativo para disminuirlo. Usa 0 para copiar los precios exactos.
                                </Form.Text>
                            </Form.Group>
                        ) : (
                            <Form.Group className="mt-3">
                                <Form.Label className="fw-bold text-muted small text-uppercase">Ajuste de Monto Fijo (Moneda Local) *</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    required
                                    placeholder="Ej: 5 (para sumar $5) o -10 (para restar $10)"
                                    value={bulkUpdateForm.fixed_change}
                                    onChange={e => setBulkUpdateForm({ ...bulkUpdateForm, fixed_change: e.target.value })}
                                />
                                <Form.Text className="text-muted">
                                    Ingresa el valor numérico exacto a sumar o restar del costo local de las tarifas de origen.
                                </Form.Text>
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="secondary" onClick={() => setShowBulkUpdateModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">Aplicar Actualización Masiva</Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* ─── MODAL: GESTIÓN MASIVA GENIUS (BOOKING.COM) ───────────────────────── */}
            <Modal show={showGeniusBulkModal} onHide={() => setShowGeniusBulkModal(false)} centered>
                <Modal.Header closeButton style={{ backgroundColor: '#003580' }} className="text-white">
                    <Modal.Title className="fw-bold fs-6 text-white d-flex align-items-center gap-2">
                        <span>⭐</span> Configuración Masiva de Descuento Genius
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 space-y-3">
                    <div className="alert alert-primary py-2 px-3 small mb-3 border-0" style={{ backgroundColor: '#e7f0fa', color: '#003580' }}>
                        <strong>Programa Genius de Booking.com:</strong> Ofrece un descuento preferencial (entre 5% y 10%) a los millones de usuarios fidelizados de Booking para aumentar drásticamente el volumen de viajes adjudicados.
                    </div>

                    <div className="bg-light p-3 rounded border mb-3">
                        <small className="text-muted fw-bold d-block mb-1">🤝 Regla de Costo Compartido:</small>
                        <small className="text-muted">
                            El descuento seleccionado se co-financia con la plataforma, reduciendo proporcionalmente el costo a liquidar al proveedor para garantizar la competitividad en las búsquedas.
                        </small>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-dark small text-uppercase">Porcentaje de Descuento Genius *</Form.Label>
                        <Form.Select
                            value={bulkGeniusPercent}
                            onChange={e => setBulkGeniusPercent(parseFloat(e.target.value))}
                            className="fw-bold"
                        >
                            <option value={0}>0% - Desactivar Genius en estas rutas</option>
                            <option value={5}>5% - Descuento Estándar Genius (Recomendado Booking)</option>
                            <option value={6}>6% - Promoción Especial</option>
                            <option value={7}>7% - Promoción Agresiva</option>
                            <option value={8}>8% - Alta Demanda</option>
                            <option value={9}>9% - Alta Competitividad</option>
                            <option value={10}>10% - Máximo Permitido por Booking</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label className="fw-bold text-dark small text-uppercase mb-2">Alcance de Aplicación *</Form.Label>
                        <div className="d-flex flex-column gap-2">
                            {/* Opción 1: Rutas filtradas actualmente */}
                            <div 
                                onClick={() => setBulkGeniusScope('filtered')}
                                className="p-3 rounded border transition-all"
                                style={{ 
                                    cursor: 'pointer', 
                                    borderColor: bulkGeniusScope === 'filtered' ? '#003580' : '#dee2e6',
                                    borderWidth: bulkGeniusScope === 'filtered' ? '2px' : '1px',
                                    backgroundColor: bulkGeniusScope === 'filtered' ? '#f0f4f9' : '#fff'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <input 
                                            type="radio" 
                                            id="bulk-genius-scope-filtered"
                                            name="bulkGeniusScope"
                                            value="filtered"
                                            checked={bulkGeniusScope === 'filtered'}
                                            onChange={() => setBulkGeniusScope('filtered')}
                                            style={{ cursor: 'pointer', width: '18px', height: '18px', accentColor: '#003580' }}
                                        />
                                        <label htmlFor="bulk-genius-scope-filtered" className="m-0 fw-bold text-dark" style={{ cursor: 'pointer' }}>
                                            🎯 Solo rutas filtradas actualmente
                                        </label>
                                    </div>
                                    <Badge 
                                        bg={bulkGeniusScope === 'filtered' ? 'primary' : 'secondary'} 
                                        style={{ backgroundColor: bulkGeniusScope === 'filtered' ? '#003580' : undefined }}
                                    >
                                        {filteredTariffs.length} {filteredTariffs.length === 1 ? 'ruta' : 'rutas'}
                                    </Badge>
                                </div>
                                {(tariffOriginSearch || tariffDestSearch || tariffCapacity || tariffVehicleType) ? (
                                    <div className="small text-muted mt-1 ps-4" style={{ fontSize: '11px' }}>
                                        <span className="fw-bold text-primary">Filtro aplicado:</span> {[
                                            tariffOriginSearch && `Origen "${tariffOriginSearch}"`,
                                            tariffDestSearch && `Destino "${tariffDestSearch}"`,
                                            tariffVehicleType && `Vehículo ${tariffVehicleType}`,
                                            tariffCapacity && `${tariffCapacity} pax`
                                        ].filter(Boolean).join(' • ')}
                                    </div>
                                ) : (
                                    <div className="small text-muted mt-1 ps-4" style={{ fontSize: '11px' }}>
                                        (No hay filtros de búsqueda activos; coincide con todas tus rutas)
                                    </div>
                                )}
                            </div>

                            {/* Opción 2: Todas las rutas */}
                            <div 
                                onClick={() => setBulkGeniusScope('all')}
                                className="p-3 rounded border transition-all"
                                style={{ 
                                    cursor: 'pointer', 
                                    borderColor: bulkGeniusScope === 'all' ? '#003580' : '#dee2e6',
                                    borderWidth: bulkGeniusScope === 'all' ? '2px' : '1px',
                                    backgroundColor: bulkGeniusScope === 'all' ? '#f0f4f9' : '#fff'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <input 
                                            type="radio" 
                                            id="bulk-genius-scope-all"
                                            name="bulkGeniusScope"
                                            value="all"
                                            checked={bulkGeniusScope === 'all'}
                                            onChange={() => setBulkGeniusScope('all')}
                                            style={{ cursor: 'pointer', width: '18px', height: '18px', accentColor: '#003580' }}
                                        />
                                        <label htmlFor="bulk-genius-scope-all" className="m-0 fw-bold text-dark" style={{ cursor: 'pointer' }}>
                                            🌐 Todas mis rutas
                                        </label>
                                    </div>
                                    <Badge 
                                        bg={bulkGeniusScope === 'all' ? 'primary' : 'secondary'} 
                                        style={{ backgroundColor: bulkGeniusScope === 'all' ? '#003580' : undefined }}
                                    >
                                        {tariffs.length} {tariffs.length === 1 ? 'ruta' : 'rutas'}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" size="sm" onClick={() => setShowGeniusBulkModal(false)} disabled={isSavingBulkGenius}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        style={{ backgroundColor: '#003580', borderColor: '#003580' }}
                        className="fw-bold text-white shadow-sm px-3"
                        onClick={handleApplyBulkGenius}
                        disabled={isSavingBulkGenius || (bulkGeniusScope === 'filtered' && filteredTariffs.length === 0)}
                    >
                        {isSavingBulkGenius ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" />
                                Aplicando...
                            </>
                        ) : (
                            `Aplicar ${bulkGeniusPercent}% a ${bulkGeniusScope === 'filtered' ? filteredTariffs.length : tariffs.length} Rutas`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ─── MODAL: RECORDATORIOS DE ITINERARIOS ─────────────────────────────── */}
            <Modal show={showItineraryModal} onHide={() => setShowItineraryModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title className="fw-bold">📋 Recordatorios de Itinerarios (Conductores)</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    <div className="bg-white p-3 rounded shadow-sm mb-3">
                        <Row className="align-items-center">
                            <Col md={6}>
                                <Form.Group className="mb-0">
                                    <Form.Label className="fw-bold text-muted small text-uppercase">Fecha del Itinerario</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={itineraryDate} 
                                        onChange={e => setItineraryDate(e.target.value)} 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex justify-content-end align-items-end mt-3 mt-md-0">
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => loadItineraryTrips(itineraryDate)} 
                                    disabled={itineraryLoading}
                                    size="sm"
                                >
                                    {itineraryLoading ? <Spinner size="sm" animation="border" /> : "🔄 Recargar"}
                                </Button>
                            </Col>
                        </Row>
                    </div>

                    {itineraryLoading ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Buscando viajes del día...</p>
                        </div>
                    ) : groupedItineraries.length === 0 ? (
                        <div className="text-center p-5 bg-white rounded shadow-sm">
                            <span className="display-4 text-muted">📭</span>
                            <h5 className="mt-3 text-muted">No hay viajes con conductor asignado para esta fecha.</h5>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {groupedItineraries.map((group) => {
                                const hasPhone = !!group.driver.phone;
                                return (
                                    <Card key={group.driver.id} className="border-0 shadow-sm overflow-hidden bg-white">
                                        <Card.Header className="bg-white py-3 border-bottom-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div>
                                                <h5 className="fw-bold mb-0 text-dark">
                                                    👨‍✈️ {group.driver.full_name}
                                                </h5>
                                                <small className="text-muted">
                                                    Móvil: <strong className="text-primary">{group.vehicle.internal_code || '---'}</strong> 
                                                    {group.driver.phone && ` | Tel: ${group.driver.phone}`}
                                                </small>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                {getItineraryStatusBadge(group)}
                                                <Button 
                                                    variant="purple" 
                                                    size="sm" 
                                                    onClick={() => handleSendItineraryOfficial(group)}
                                                    disabled={!hasPhone || isSendingItinerary[group.driver.id]}
                                                    className="fw-bold d-flex align-items-center gap-1 text-white"
                                                    style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
                                                >
                                                    {isSendingItinerary[group.driver.id] ? <Spinner size="sm" animation="border" className="me-1" /> : <MessageCircle size={14} />} 
                                                    API WhatsApp
                                                </Button>
                                                <Button 
                                                    variant="outline-success" 
                                                    size="sm" 
                                                    onClick={() => handleSendItinerary(group)}
                                                    disabled={!hasPhone}
                                                    className="fw-bold d-flex align-items-center gap-1"
                                                >
                                                    Enlace Manual
                                                </Button>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="p-0 border-top bg-white">
                                            <Table hover size="sm" className="mb-0 align-middle table-borderless bg-white" style={{ fontSize: '0.85rem' }}>
                                                <thead className="bg-light text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
                                                    <tr>
                                                        <th className="ps-3" style={{ width: '80px' }}>Hora</th>
                                                        <th style={{ width: '150px' }}>Cliente / Pax</th>
                                                        <th>Ruta (Origen / Destino)</th>
                                                        <th className="pe-3" style={{ width: '150px' }}>Acompañantes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.trips.map(t => {
                                                        const isAirportPickup = isAirport(t.pickup_address);
                                                        return (
                                                            <tr key={t.id} className="border-bottom border-light">
                                                                <td className="ps-3 fw-bold text-primary">
                                                                    {format(new Date(t.scheduled_time), 'HH:mm')}
                                                                </td>
                                                                <td>
                                                                    <div className="fw-semibold">{t.client?.name || '---'}</div>
                                                                    <div className="text-dark">
                                                                        👤 {t.passenger_name}
                                                                        <Badge bg="secondary" className="ms-1.5 px-1 py-0.5" style={{ fontSize: '9px' }}>
                                                                            x{Math.max(t.passenger_count || 1, 1 + (t.additional_passengers?.length || 0))}
                                                                        </Badge>
                                                                    </div>
                                                                    <div className="text-muted small">{t.passenger_phone || 'Sin tel.'}</div>
                                                                </td>
                                                                <td>
                                                                    <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                                                        <span className="text-success me-1">🛫</span>
                                                                        {isAirportPickup && <span className="badge bg-info text-white me-1" style={{ fontSize: '8px' }}>AERO</span>}
                                                                        {t.pickup_address}
                                                                    </div>
                                                                    <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                                                        <span className="text-danger me-1">🛬</span>
                                                                        {t.dropoff_address}
                                                                    </div>
                                                                </td>
                                                                <td className="pe-3">
                                                                    {t.additional_passengers && t.additional_passengers.length > 0 ? (
                                                                        <div className="small text-muted">
                                                                            {t.additional_passengers.map(ap => ap.full_name).join(', ')}
                                                                        </div>
                                                                    ) : '-'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={() => setShowItineraryModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>
            <TripAuditModal 
                show={showAuditModal} 
                onHide={() => setShowAuditModal(false)} 
                trip={auditTrip} 
                isLoaded={mapsLoaded} 
            />
        </Container>
    );
};

const ProviderPortalWithLang = (props) => (
    <ErrorBoundary title="Error en el Portal del Proveedor">
        <LanguageProvider>
            <ProviderPortal {...props} />
        </LanguageProvider>
    </ErrorBoundary>
);

export default ProviderPortalWithLang;
